// The company welcome: three optional steps between sign-up and the first chat (C-14).
//
// Namespace `welcome`; owner: the polish onboarding chunk (PL-05). Roles, goals,
// step titles, step 2's connect copy, the tool summaries, the starter and
// connected-tool tasks (their prompts become the person's first message, so
// they follow the interface language too) and the welcome's loading and error
// states. ui/company/onboarding/onboarding-tools.ts maps each tool to its keys.
// Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  logOut: 'Log out',
  progressLabel: 'Getting started',
  stepOf: 'Step {current} of {total}',
  stepFocus: 'Your focus',
  stepTools: 'Your tools',
  stepFirst: 'Your first step',
  focusTitle: 'Tell us how you work.',
  focusTitleNamed: 'Welcome, {name}. How do you work?',
  focusIntro:
    'Choose your role and what you want help with. Both are optional and help us suggest useful tools.',
  roleHeading: 'Your role',
  goalsHeading: 'What would you like help with?',
  roleLeadership: 'Leadership',
  roleProduct: 'Product',
  roleEngineering: 'Engineering & data',
  roleMarketing: 'Marketing',
  roleSales: 'Sales',
  roleOperations: 'Operations',
  roleFinance: 'Finance',
  goalEverydayTitle: 'Stay a step ahead',
  goalEverydayBody: 'Prepare for meetings and catch up on conversations.',
  goalCommerceTitle: 'Understand my business',
  goalCommerceBody: 'Explore products, orders and opportunities.',
  goalFinanceTitle: 'See the numbers clearly',
  goalFinanceBody: 'Make sense of invoices and financial information.',
  goalProjectsTitle: 'Keep work moving',
  goalProjectsBody: 'Connect discussions, issues and project updates.',
  toolsTitle: 'Connect your everyday tools.',
  toolsIntro: 'A little context makes a big difference. Start with one or two.',
  toolsUnavailable:
    'Connections aren’t available right now. You can start with a question or a file.',
  toolsLabel: 'Connect your tools',
  suggestedTools: 'Suggested for you',
  allTools: 'All tools',
  browseAllTools: 'Browse all tools',
  backToSuggestions: 'Back to suggestions',
  searchTools: 'Search tools',
  findTool: 'Find a tool…',
  popupBlocked: 'The browser blocked the pop-up. This page keeps checking while you authorize.',
  openAuthorization: 'Open the authorization page',
  authorizing: 'Finish authorizing in the provider’s window.',
  stopWaiting: 'Stop waiting',
  loadingConnections: 'Loading your connections…',
  connectionsFailed: 'We couldn’t load your connections.',
  useMyAccount: 'Use my account',
  useCompanyAccount: 'Use an account for {company}',
  chooseAudience: 'Choose who can use this connection',
  noToolsMatch: 'No tools match your search. Try another name.',
  noToolsYet: 'There are no tools to suggest yet. You can start with a question or a file.',
  reminderTitle: 'Better with your tools.',
  reminderIntro:
    'One connection is enough to begin. Try a tool you already use, or come back to this later.',
  reminderDoneTitle: 'A good start.',
  reminderDoneIntro: 'You can add more whenever you need them.',
  firstTitle: 'Where shall we start?',
  firstIntro: 'Choose an example to start a conversation, or go straight to your workspace.',
  connectedOne: '1 account connected',
  connectedMany: '{count} accounts connected',
  taskResearchTitle: 'Explore a business opportunity',
  taskResearchPrompt:
    'Help me research an opportunity for our business using public sources. Ask what I want to explore, then help me make a useful, cited brief.',
  taskFileTitle: 'Work through a document or spreadsheet',
  taskFilePrompt:
    'Help me work through a document or spreadsheet. Ask me to attach a file and what I want to learn or get done, then guide me through it.',
  taskChooseTitle: 'Help me choose a first task',
  taskChoosePrompt:
    'Help me get started with Poneva. Use our company context and available capabilities to suggest a few useful things we can do together. Ask one question to help me choose, then guide me through the task.',
  toolGoogleSummary: 'Email, calendar and documents',
  toolSlackSummary: 'Conversations and team knowledge',
  toolShopifySummary: 'Products, orders and inventory',
  toolWooSummary: 'Products, orders and reports',
  toolMerchantSummary: 'Product feeds and statuses',
  toolInvoicesSummary: 'Invoices and financial information',
  toolGithubSummary: 'Projects, issues and pull requests',
  taskMeetingTitle: 'Prepare for my next meeting',
  taskMeetingPrompt:
    'Help me prepare for my next meeting. Check my available calendar, email and documents, ask which meeting if needed, and make a concise brief with sources.',
  taskSlackTitle: 'Catch up on a conversation',
  taskSlackPrompt:
    'Help me catch up on a Slack conversation. Ask which channel or topic to focus on, then summarize decisions, open questions and next steps with links to the messages.',
  taskShopifyTitle: 'Take a fresh look at my products',
  taskShopifyPrompt:
    'Review a small sample of my Shopify products and identify useful improvements to product information. Show the evidence and do not change anything.',
  taskWooTitle: 'Review my product information',
  taskWooPrompt:
    'Review a small sample of my WooCommerce products and identify gaps in product information. Show sources and do not change anything.',
  taskFeedTitle: 'Review my product feed',
  taskFeedPrompt:
    'Help me review my Google Merchant Center product feed. Start with the accessible account and a small sample, and cite what you find.',
  taskFortnoxTitle: 'Make sense of recent invoices',
  taskFortnoxPrompt:
    'Help me understand recent invoices in Fortnox. Ask which period to review and summarize the available information with sources. Do not change anything.',
  taskTripletexTitle: 'Get an overview of recent invoices',
  taskTripletexPrompt:
    'Help me understand recent invoices in Tripletex. Ask which period to review and summarize the available information with sources. Do not change anything.',
  taskGithubTitle: 'Catch up on a project',
  taskGithubPrompt:
    'Help me catch up on a GitHub project. Ask which repository to review and summarize recent issues and pull requests with links.',
  taskTryTitle: 'Try {name}',
  taskTryPrompt:
    'Help me get started with my {name} connection. Discover its available tools, suggest a useful first task, and ask what I want to do. Propose any changes for my review before executing them.',
  back: 'Back',
  continue: 'Continue',
  continueWithout: 'Continue without connecting',
  goToWorkspace: 'Go to workspace',
  opening: 'Opening your workspace…',
  startFailed: 'We could not open your workspace. Please try again.',
  loadingTitle: 'Opening your company…',
  loadingStatus: 'Loading your workspace',
  errorTitle: 'Let’s try that again.',
  tryAgain: 'Try again',
  noCompanyTitle: 'An invitation is needed.',
  noCompanyBody:
    'Your account is not connected to a company. Ask an owner to invite your email address. Your private work is kept.',
  checkAccess: 'Check access status',
};

export type WelcomeCopyKey = keyof typeof en;
export type WelcomeCopy = Readonly<Record<WelcomeCopyKey, string>>;

const bg: WelcomeCopy = {
  logOut: 'Изход',
  progressLabel: 'Първи стъпки',
  stepOf: 'Стъпка {current} от {total}',
  stepFocus: 'Вашият фокус',
  stepTools: 'Вашите инструменти',
  stepFirst: 'Първата ви стъпка',
  focusTitle: 'Разкажете ни как работите.',
  focusTitleNamed: 'Добре дошли, {name}. Как работите?',
  focusIntro:
    'Изберете ролята си и за какво искате помощ. И двете са по избор и ни помагат да предложим полезни инструменти.',
  roleHeading: 'Вашата роля',
  goalsHeading: 'За какво бихте искали помощ?',
  roleLeadership: 'Ръководство',
  roleProduct: 'Продукт',
  roleEngineering: 'Инженерство и данни',
  roleMarketing: 'Маркетинг',
  roleSales: 'Продажби',
  roleOperations: 'Операции',
  roleFinance: 'Финанси',
  goalEverydayTitle: 'Бъдете една стъпка напред',
  goalEverydayBody: 'Подгответе се за срещи и наваксайте с разговорите.',
  goalCommerceTitle: 'Да разбера бизнеса си',
  goalCommerceBody: 'Разгледайте продукти, поръчки и възможности.',
  goalFinanceTitle: 'Ясна картина на числата',
  goalFinanceBody: 'Осмислете фактури и финансова информация.',
  goalProjectsTitle: 'Работата да върви',
  goalProjectsBody: 'Свържете дискусии, задачи и новини по проекти.',
  toolsTitle: 'Свържете ежедневните си инструменти.',
  toolsIntro: 'Малко контекст прави голяма разлика. Започнете с един или два.',
  toolsUnavailable: 'Връзките не са достъпни в момента. Можете да започнете с въпрос или файл.',
  toolsLabel: 'Свържете инструментите си',
  suggestedTools: 'Предложени за вас',
  allTools: 'Всички инструменти',
  browseAllTools: 'Разгледайте всички инструменти',
  backToSuggestions: 'Обратно към предложенията',
  searchTools: 'Търсене на инструменти',
  findTool: 'Намерете инструмент…',
  popupBlocked:
    'Браузърът блокира изскачащия прозорец. Тази страница продължава да проверява, докато давате разрешение.',
  openAuthorization: 'Отворете страницата за разрешение',
  authorizing: 'Завършете разрешаването в прозореца на доставчика.',
  stopWaiting: 'Спрете да чакате',
  loadingConnections: 'Зареждане на връзките ви…',
  connectionsFailed: 'Не успяхме да заредим връзките ви.',
  useMyAccount: 'Използване на моя профил',
  useCompanyAccount: 'Използване на профил за {company}',
  chooseAudience: 'Изберете кой може да използва тази връзка',
  noToolsMatch: 'Няма инструменти, отговарящи на търсенето. Опитайте с друго име.',
  noToolsYet: 'Все още няма инструменти за предлагане. Можете да започнете с въпрос или файл.',
  reminderTitle: 'По-добре с вашите инструменти.',
  reminderIntro:
    'Една връзка е достатъчна за начало. Опитайте с инструмент, който вече използвате, или се върнете към това по-късно.',
  reminderDoneTitle: 'Добро начало.',
  reminderDoneIntro: 'Можете да добавите още, когато ви потрябват.',
  firstTitle: 'Откъде да започнем?',
  firstIntro:
    'Изберете пример, за да започнете разговор, или отидете направо в работното си пространство.',
  connectedOne: '1 свързан профил',
  connectedMany: '{count} свързани профила',
  taskResearchTitle: 'Проучване на бизнес възможност',
  taskResearchPrompt:
    'Помогни ми да проуча възможност за нашия бизнес с помощта на публични източници. Попитай какво искам да проуча, след което ми помогни да изготвя полезно резюме с цитирани източници.',
  taskFileTitle: 'Работа с документ или електронна таблица',
  taskFilePrompt:
    'Помогни ми да разгледам документ или електронна таблица. Помоли ме да прикача файл и попитай какво искам да науча или да свърша, след което ме преведи през него.',
  taskChooseTitle: 'Помогни ми да избера първа задача',
  taskChoosePrompt:
    'Помогни ми да започна с Poneva. Използвай контекста на нашата компания и наличните възможности, за да предложиш няколко полезни неща, които можем да направим заедно. Задай ми един въпрос, за да ми помогнеш да избера, след което ме преведи през задачата.',
  toolGoogleSummary: 'Имейл, календар и документи',
  toolSlackSummary: 'Разговори и знания на екипа',
  toolShopifySummary: 'Продукти, поръчки и наличности',
  toolWooSummary: 'Продукти, поръчки и отчети',
  toolMerchantSummary: 'Продуктови фийдове и статуси',
  toolInvoicesSummary: 'Фактури и финансова информация',
  toolGithubSummary: 'Проекти, задачи и pull заявки',
  taskMeetingTitle: 'Подготовка за следващата ми среща',
  taskMeetingPrompt:
    'Помогни ми да се подготвя за следващата си среща. Провери достъпните ми календар, имейл и документи, попитай коя среща, ако е нужно, и направи кратък брифинг с източници.',
  taskSlackTitle: 'Наваксване с разговор',
  taskSlackPrompt:
    'Помогни ми да наваксам с разговор в Slack. Попитай върху кой канал или тема да се съсредоточим, след това обобщи решенията, отворените въпроси и следващите стъпки с връзки към съобщенията.',
  taskShopifyTitle: 'Нов поглед към продуктите ми',
  taskShopifyPrompt:
    'Прегледай малка извадка от продуктите ми в Shopify и открий полезни подобрения в продуктовата информация. Покажи доказателствата и не променяй нищо.',
  taskWooTitle: 'Преглед на продуктовата ми информация',
  taskWooPrompt:
    'Прегледай малка извадка от продуктите ми в WooCommerce и открий пропуски в продуктовата информация. Покажи източниците и не променяй нищо.',
  taskFeedTitle: 'Преглед на продуктовия ми фийд',
  taskFeedPrompt:
    'Помогни ми да прегледам продуктовия си фийд в Google Merchant Center. Започни с достъпния профил и малка извадка и посочи източници за откритото.',
  taskFortnoxTitle: 'Осмисляне на последните фактури',
  taskFortnoxPrompt:
    'Помогни ми да разбера последните фактури във Fortnox. Попитай кой период да прегледаме и обобщи наличната информация с източници. Не променяй нищо.',
  taskTripletexTitle: 'Преглед на последните фактури',
  taskTripletexPrompt:
    'Помогни ми да разбера последните фактури в Tripletex. Попитай кой период да прегледаме и обобщи наличната информация с източници. Не променяй нищо.',
  taskGithubTitle: 'Наваксване с проект',
  taskGithubPrompt:
    'Помогни ми да наваксам с проект в GitHub. Попитай кое хранилище да прегледаме и обобщи последните задачи и pull заявки с връзки.',
  taskTryTitle: 'Изпробване на {name}',
  taskTryPrompt:
    'Помогни ми да започна с връзката си с {name}. Открий наличните ѝ инструменти, предложи полезна първа задача и попитай какво искам да направя. Предложи ми всички промени за преглед, преди да ги изпълниш.',
  back: 'Назад',
  continue: 'Продължете',
  continueWithout: 'Продължете без свързване',
  goToWorkspace: 'Към работното пространство',
  opening: 'Отваряне на работното пространство…',
  startFailed: 'Не успяхме да отворим работното ви пространство. Опитайте отново.',
  loadingTitle: 'Отваряне на вашата компания…',
  loadingStatus: 'Зареждане на работното пространство',
  errorTitle: 'Нека опитаме отново.',
  tryAgain: 'Опитайте отново',
  noCompanyTitle: 'Необходима е покана.',
  noCompanyBody:
    'Профилът ви не е свързан с компания. Помолете собственик да покани имейл адреса ви. Личната ви работа се запазва.',
  checkAccess: 'Проверка на достъпа',
};

const hr: WelcomeCopy = {
  logOut: 'Odjava',
  progressLabel: 'Prvi koraci',
  stepOf: 'Korak {current} od {total}',
  stepFocus: 'Vaš fokus',
  stepTools: 'Vaši alati',
  stepFirst: 'Vaš prvi korak',
  focusTitle: 'Recite nam kako radite.',
  focusTitleNamed: 'Dobro došli, {name}. Kako radite?',
  focusIntro:
    'Odaberite svoju ulogu i ono u čemu želite pomoć. Oboje je neobavezno i pomaže nam predložiti korisne alate.',
  roleHeading: 'Vaša uloga',
  goalsHeading: 'U čemu vam možemo pomoći?',
  roleLeadership: 'Vodstvo',
  roleProduct: 'Proizvod',
  roleEngineering: 'Inženjering i podaci',
  roleMarketing: 'Marketing',
  roleSales: 'Prodaja',
  roleOperations: 'Operacije',
  roleFinance: 'Financije',
  goalEverydayTitle: 'Budite korak ispred',
  goalEverydayBody: 'Pripremite se za sastanke i sustignite razgovore.',
  goalCommerceTitle: 'Razumjeti svoje poslovanje',
  goalCommerceBody: 'Istražite proizvode, narudžbe i prilike.',
  goalFinanceTitle: 'Jasno vidjeti brojke',
  goalFinanceBody: 'Snađite se u računima i financijskim podacima.',
  goalProjectsTitle: 'Neka posao teče',
  goalProjectsBody: 'Povežite rasprave, zadatke i novosti o projektima.',
  toolsTitle: 'Povežite svoje svakodnevne alate.',
  toolsIntro: 'Malo konteksta čini veliku razliku. Počnite s jednim ili dva.',
  toolsUnavailable: 'Veze trenutačno nisu dostupne. Možete početi s pitanjem ili datotekom.',
  toolsLabel: 'Povežite svoje alate',
  suggestedTools: 'Predloženo za vas',
  allTools: 'Svi alati',
  browseAllTools: 'Pregledajte sve alate',
  backToSuggestions: 'Natrag na prijedloge',
  searchTools: 'Pretraži alate',
  findTool: 'Pronađite alat…',
  popupBlocked:
    'Preglednik je blokirao skočni prozor. Ova stranica nastavlja provjeravati dok dajete odobrenje.',
  openAuthorization: 'Otvorite stranicu za odobrenje',
  authorizing: 'Dovršite odobrenje u prozoru pružatelja usluge.',
  stopWaiting: 'Prestani čekati',
  loadingConnections: 'Učitavanje vaših veza…',
  connectionsFailed: 'Nismo uspjeli učitati vaše veze.',
  useMyAccount: 'Koristi moj račun',
  useCompanyAccount: 'Koristi račun za {company}',
  chooseAudience: 'Odaberite tko može koristiti ovu vezu',
  noToolsMatch: 'Nijedan alat ne odgovara pretrazi. Pokušajte s drugim nazivom.',
  noToolsYet: 'Još nema alata za prijedlog. Možete početi s pitanjem ili datotekom.',
  reminderTitle: 'Bolje uz vaše alate.',
  reminderIntro:
    'Za početak je dovoljna jedna veza. Isprobajte alat koji već koristite ili se vratite ovome kasnije.',
  reminderDoneTitle: 'Dobar početak.',
  reminderDoneIntro: 'Više ih možete dodati kad god zatrebate.',
  firstTitle: 'Odakle da počnemo?',
  firstIntro: 'Odaberite primjer za početak razgovora ili idite ravno u svoj radni prostor.',
  connectedOne: '1 povezan račun',
  connectedMany: 'Povezanih računa: {count}',
  taskResearchTitle: 'Istražite poslovnu priliku',
  taskResearchPrompt:
    'Pomozi mi istražiti priliku za naše poslovanje pomoću javnih izvora. Pitaj me što želim istražiti, a zatim mi pomozi izraditi koristan sažetak s navedenim izvorima.',
  taskFileTitle: 'Prođite kroz dokument ili proračunsku tablicu',
  taskFilePrompt:
    'Pomozi mi proći kroz dokument ili proračunsku tablicu. Zamoli me da priložim datoteku i pitaj što želim saznati ili obaviti, a zatim me vodi kroz to.',
  taskChooseTitle: 'Pomozi mi odabrati prvi zadatak',
  taskChoosePrompt:
    'Pomozi mi da počnem s Ponevom. Iskoristi kontekst naše tvrtke i dostupne mogućnosti kako bi predložio nekoliko korisnih stvari koje možemo napraviti zajedno. Postavi mi jedno pitanje koje će mi pomoći da odaberem, a zatim me vodi kroz zadatak.',
  toolGoogleSummary: 'E-pošta, kalendar i dokumenti',
  toolSlackSummary: 'Razgovori i znanje tima',
  toolShopifySummary: 'Proizvodi, narudžbe i zalihe',
  toolWooSummary: 'Proizvodi, narudžbe i izvješća',
  toolMerchantSummary: 'Feedovi proizvoda i statusi',
  toolInvoicesSummary: 'Računi i financijski podaci',
  toolGithubSummary: 'Projekti, zadaci i pull requestovi',
  taskMeetingTitle: 'Pripremi me za sljedeći sastanak',
  taskMeetingPrompt:
    'Pomozi mi da se pripremim za sljedeći sastanak. Provjeri moj dostupni kalendar, e-poštu i dokumente, pitaj koji sastanak ako je potrebno i napravi sažet pregled s izvorima.',
  taskSlackTitle: 'Nadoknadi propušteno u razgovoru',
  taskSlackPrompt:
    'Pomozi mi da nadoknadim propušteno u razgovoru na Slacku. Pitaj na koji kanal ili temu da se usredotočimo, a zatim sažmi odluke, otvorena pitanja i sljedeće korake s poveznicama na poruke.',
  taskShopifyTitle: 'Svjež pogled na moje proizvode',
  taskShopifyPrompt:
    'Pregledaj mali uzorak mojih proizvoda na Shopifyju i pronađi korisna poboljšanja podataka o proizvodima. Pokaži dokaze i ništa ne mijenjaj.',
  taskWooTitle: 'Pregledaj podatke o mojim proizvodima',
  taskWooPrompt:
    'Pregledaj mali uzorak mojih proizvoda u WooCommerceu i pronađi nedostatke u podacima o proizvodima. Pokaži izvore i ništa ne mijenjaj.',
  taskFeedTitle: 'Pregledaj moj feed proizvoda',
  taskFeedPrompt:
    'Pomozi mi pregledati feed proizvoda u Google Merchant Centeru. Počni s dostupnim računom i malim uzorkom te navedi izvore za ono što pronađeš.',
  taskFortnoxTitle: 'Objasni mi nedavne račune',
  taskFortnoxPrompt:
    'Pomozi mi razumjeti nedavne račune u Fortnoxu. Pitaj koje razdoblje da pregledamo i sažmi dostupne podatke s izvorima. Ništa ne mijenjaj.',
  taskTripletexTitle: 'Daj mi pregled nedavnih računa',
  taskTripletexPrompt:
    'Pomozi mi razumjeti nedavne račune u Tripletexu. Pitaj koje razdoblje da pregledamo i sažmi dostupne podatke s izvorima. Ništa ne mijenjaj.',
  taskGithubTitle: 'Nadoknadi propušteno na projektu',
  taskGithubPrompt:
    'Pomozi mi da nadoknadim propušteno na projektu na GitHubu. Pitaj koji repozitorij da pregledamo i sažmi nedavne zadatke i pull requestove s poveznicama.',
  taskTryTitle: 'Isprobaj {name}',
  taskTryPrompt:
    'Pomozi mi da počnem s vezom za {name}. Otkrij njezine dostupne alate, predloži koristan prvi zadatak i pitaj što želim napraviti. Sve promjene predloži mi na pregled prije nego što ih izvršiš.',
  back: 'Natrag',
  continue: 'Nastavi',
  continueWithout: 'Nastavi bez povezivanja',
  goToWorkspace: 'Idi u radni prostor',
  opening: 'Otvaranje radnog prostora…',
  startFailed: 'Nismo mogli otvoriti vaš radni prostor. Pokušajte ponovno.',
  loadingTitle: 'Otvaranje vaše tvrtke…',
  loadingStatus: 'Učitavanje radnog prostora',
  errorTitle: 'Pokušajmo ponovno.',
  tryAgain: 'Pokušaj ponovno',
  noCompanyTitle: 'Potrebna je pozivnica.',
  noCompanyBody:
    'Vaš račun nije povezan s tvrtkom. Zamolite vlasnika da pozove vašu adresu e-pošte. Vaš privatni rad ostaje sačuvan.',
  checkAccess: 'Provjeri status pristupa',
};

const cs: WelcomeCopy = {
  logOut: 'Odhlásit se',
  progressLabel: 'Začínáme',
  stepOf: 'Krok {current} z {total}',
  stepFocus: 'Vaše zaměření',
  stepTools: 'Vaše nástroje',
  stepFirst: 'Váš první krok',
  focusTitle: 'Řekněte nám, jak pracujete.',
  focusTitleNamed: 'Vítejte, {name}. Jak pracujete?',
  focusIntro:
    'Vyberte svou roli a s čím chcete pomoct. Obojí je nepovinné a pomáhá nám navrhnout užitečné nástroje.',
  roleHeading: 'Vaše role',
  goalsHeading: 'S čím byste chtěli pomoct?',
  roleLeadership: 'Vedení',
  roleProduct: 'Produkt',
  roleEngineering: 'Vývoj a data',
  roleMarketing: 'Marketing',
  roleSales: 'Obchod',
  roleOperations: 'Provoz',
  roleFinance: 'Finance',
  goalEverydayTitle: 'Být o krok napřed',
  goalEverydayBody: 'Připravte se na schůzky a dožeňte konverzace.',
  goalCommerceTitle: 'Porozumět svému byznysu',
  goalCommerceBody: 'Prozkoumejte produkty, objednávky a příležitosti.',
  goalFinanceTitle: 'Vidět čísla jasně',
  goalFinanceBody: 'Vyznejte se ve fakturách a finančních údajích.',
  goalProjectsTitle: 'Posouvat práci dál',
  goalProjectsBody: 'Propojte diskuze, úkoly a novinky z projektů.',
  toolsTitle: 'Připojte své každodenní nástroje.',
  toolsIntro: 'Trocha kontextu udělá velký rozdíl. Začněte jedním nebo dvěma.',
  toolsUnavailable: 'Připojení teď nejsou k dispozici. Můžete začít otázkou nebo souborem.',
  toolsLabel: 'Připojte své nástroje',
  suggestedTools: 'Doporučeno pro vás',
  allTools: 'Všechny nástroje',
  browseAllTools: 'Procházet všechny nástroje',
  backToSuggestions: 'Zpět na doporučení',
  searchTools: 'Hledat nástroje',
  findTool: 'Najít nástroj…',
  popupBlocked:
    'Prohlížeč zablokoval vyskakovací okno. Tato stránka mezitím dál kontroluje, zda jste přístup povolili.',
  openAuthorization: 'Otevřít stránku pro povolení přístupu',
  authorizing: 'Dokončete povolení přístupu v okně poskytovatele.',
  stopWaiting: 'Přestat čekat',
  loadingConnections: 'Načítání vašich připojení…',
  connectionsFailed: 'Nepodařilo se načíst vaše připojení.',
  useMyAccount: 'Použít můj účet',
  useCompanyAccount: 'Použít účet pro {company}',
  chooseAudience: 'Vyberte, kdo může toto připojení používat',
  noToolsMatch: 'Hledání neodpovídá žádný nástroj. Zkuste jiný název.',
  noToolsYet: 'Zatím není co doporučit. Můžete začít otázkou nebo souborem.',
  reminderTitle: 'Lepší s vašimi nástroji.',
  reminderIntro:
    'Na začátek stačí jedno připojení. Vyzkoušejte nástroj, který už používáte, nebo se k tomu vraťte později.',
  reminderDoneTitle: 'Dobrý začátek.',
  reminderDoneIntro: 'Další můžete přidat, kdykoli je budete potřebovat.',
  firstTitle: 'Čím začneme?',
  firstIntro: 'Vyberte příklad a zahajte konverzaci, nebo přejděte rovnou do pracovního prostoru.',
  connectedOne: 'Připojen 1 účet',
  connectedMany: 'Připojené účty: {count}',
  taskResearchTitle: 'Prozkoumat obchodní příležitost',
  taskResearchPrompt:
    'Pomoz mi prozkoumat příležitost pro naši firmu z veřejných zdrojů. Zeptej se, co chci zkoumat, a pak mi pomoz připravit užitečné shrnutí se zdroji.',
  taskFileTitle: 'Projít dokument nebo tabulku',
  taskFilePrompt:
    'Pomoz mi projít dokument nebo tabulku. Požádej mě o přiložení souboru, zeptej se, co chci zjistit nebo udělat, a pak mě tím proveď.',
  taskChooseTitle: 'Pomozte mi vybrat první úkol',
  taskChoosePrompt:
    'Pomoz mi začít s Poneva. Využij kontext naší firmy a dostupné možnosti a navrhni pár užitečných věcí, které můžeme udělat spolu. Polož jednu otázku, která mi pomůže vybrat, a pak mě provázej úkolem.',
  toolGoogleSummary: 'E-mail, kalendář a dokumenty',
  toolSlackSummary: 'Konverzace a znalosti týmu',
  toolShopifySummary: 'Produkty, objednávky a zásoby',
  toolWooSummary: 'Produkty, objednávky a přehledy',
  toolMerchantSummary: 'Produktové feedy a stavy',
  toolInvoicesSummary: 'Faktury a finanční informace',
  toolGithubSummary: 'Projekty, issues a pull requesty',
  taskMeetingTitle: 'Připravit se na další schůzku',
  taskMeetingPrompt:
    'Pomoz mi připravit se na mou další schůzku. Projdi můj dostupný kalendář, e-maily a dokumenty, v případě potřeby se zeptej, o kterou schůzku jde, a připrav stručný podklad se zdroji.',
  taskSlackTitle: 'Dohnat konverzaci',
  taskSlackPrompt:
    'Pomoz mi dohnat konverzaci ve Slacku. Zeptej se, na jaký kanál nebo téma se zaměřit, a pak shrň rozhodnutí, otevřené otázky a další kroky s odkazy na zprávy.',
  taskShopifyTitle: 'Podívat se na mé produkty znovu',
  taskShopifyPrompt:
    'Projdi malý vzorek mých produktů v Shopify a najdi užitečná vylepšení produktových informací. Ukaž podklady a nic neměň.',
  taskWooTitle: 'Zkontrolovat informace o produktech',
  taskWooPrompt:
    'Projdi malý vzorek mých produktů ve WooCommerce a najdi mezery v produktových informacích. Uveď zdroje a nic neměň.',
  taskFeedTitle: 'Zkontrolovat produktový feed',
  taskFeedPrompt:
    'Pomoz mi zkontrolovat můj produktový feed v Google Merchant Center. Začni s dostupným účtem a malým vzorkem a u zjištění uveď zdroje.',
  taskFortnoxTitle: 'Vyznat se v nedávných fakturách',
  taskFortnoxPrompt:
    'Pomoz mi pochopit nedávné faktury ve Fortnoxu. Zeptej se, jaké období projít, a shrň dostupné informace se zdroji. Nic neměň.',
  taskTripletexTitle: 'Získat přehled o nedávných fakturách',
  taskTripletexPrompt:
    'Pomoz mi pochopit nedávné faktury v Tripletexu. Zeptej se, jaké období projít, a shrň dostupné informace se zdroji. Nic neměň.',
  taskGithubTitle: 'Dohnat dění v projektu',
  taskGithubPrompt:
    'Pomoz mi dohnat dění v projektu na GitHubu. Zeptej se, který repozitář projít, a shrň nedávné issues a pull requesty s odkazy.',
  taskTryTitle: 'Vyzkoušet {name}',
  taskTryPrompt:
    'Pomoz mi začít s mým připojením {name}. Zjisti, jaké nástroje nabízí, navrhni užitečný první úkol a zeptej se, co chci udělat. Všechny změny mi před provedením předlož ke kontrole.',
  back: 'Zpět',
  continue: 'Pokračovat',
  continueWithout: 'Pokračovat bez připojení',
  goToWorkspace: 'Přejít do pracovního prostoru',
  opening: 'Otevírání pracovního prostoru…',
  startFailed: 'Pracovní prostor se nepodařilo otevřít. Zkuste to znovu.',
  loadingTitle: 'Otevírání vaší firmy…',
  loadingStatus: 'Načítání pracovního prostoru',
  errorTitle: 'Zkusme to znovu.',
  tryAgain: 'Zkusit znovu',
  noCompanyTitle: 'Potřebujete pozvánku.',
  noCompanyBody:
    'Váš účet není propojen s žádnou firmou. Požádejte vlastníka, aby pozval vaši e-mailovou adresu. Vaše soukromá práce zůstane zachována.',
  checkAccess: 'Zkontrolovat přístup',
};

const da: WelcomeCopy = {
  logOut: 'Log ud',
  progressLabel: 'Kom i gang',
  stepOf: 'Trin {current} af {total}',
  stepFocus: 'Dit fokus',
  stepTools: 'Dine værktøjer',
  stepFirst: 'Dit første skridt',
  focusTitle: 'Fortæl os, hvordan du arbejder.',
  focusTitleNamed: 'Velkommen, {name}. Hvordan arbejder du?',
  focusIntro:
    'Vælg din rolle, og hvad du vil have hjælp til. Begge dele er valgfrie og hjælper os med at foreslå nyttige værktøjer.',
  roleHeading: 'Din rolle',
  goalsHeading: 'Hvad vil du have hjælp til?',
  roleLeadership: 'Ledelse',
  roleProduct: 'Produkt',
  roleEngineering: 'Teknik og data',
  roleMarketing: 'Markedsføring',
  roleSales: 'Salg',
  roleOperations: 'Drift',
  roleFinance: 'Økonomi',
  goalEverydayTitle: 'Vær et skridt foran',
  goalEverydayBody: 'Forbered møder, og kom ajour med samtaler.',
  goalCommerceTitle: 'Forstå min forretning',
  goalCommerceBody: 'Udforsk produkter, ordrer og muligheder.',
  goalFinanceTitle: 'Se tallene tydeligt',
  goalFinanceBody: 'Få overblik over fakturaer og økonomiske oplysninger.',
  goalProjectsTitle: 'Hold arbejdet i gang',
  goalProjectsBody: 'Forbind diskussioner, sager og projektopdateringer.',
  toolsTitle: 'Forbind dine hverdagsværktøjer.',
  toolsIntro: 'Lidt kontekst gør en stor forskel. Start med et eller to.',
  toolsUnavailable:
    'Forbindelser er ikke tilgængelige lige nu. Du kan starte med et spørgsmål eller en fil.',
  toolsLabel: 'Forbind dine værktøjer',
  suggestedTools: 'Foreslået til dig',
  allTools: 'Alle værktøjer',
  browseAllTools: 'Se alle værktøjer',
  backToSuggestions: 'Tilbage til forslagene',
  searchTools: 'Søg efter værktøjer',
  findTool: 'Find et værktøj…',
  popupBlocked:
    'Browseren blokerede pop op-vinduet. Siden bliver ved med at tjekke, mens du giver adgang.',
  openAuthorization: 'Åbn godkendelsessiden',
  authorizing: 'Afslut godkendelsen i udbyderens vindue.',
  stopWaiting: 'Stop med at vente',
  loadingConnections: 'Indlæser dine forbindelser…',
  connectionsFailed: 'Vi kunne ikke indlæse dine forbindelser.',
  useMyAccount: 'Brug min konto',
  useCompanyAccount: 'Brug en konto for {company}',
  chooseAudience: 'Vælg, hvem der kan bruge denne forbindelse',
  noToolsMatch: 'Ingen værktøjer matcher din søgning. Prøv et andet navn.',
  noToolsYet:
    'Der er ingen værktøjer at foreslå endnu. Du kan starte med et spørgsmål eller en fil.',
  reminderTitle: 'Bedre med dine værktøjer.',
  reminderIntro:
    'Én forbindelse er nok til at begynde. Prøv et værktøj, du allerede bruger, eller vend tilbage senere.',
  reminderDoneTitle: 'En god start.',
  reminderDoneIntro: 'Du kan tilføje flere, når du har brug for dem.',
  firstTitle: 'Hvor skal vi starte?',
  firstIntro: 'Vælg et eksempel for at starte en samtale, eller gå direkte til dit arbejdsområde.',
  connectedOne: '1 konto forbundet',
  connectedMany: '{count} konti forbundet',
  taskResearchTitle: 'Undersøg en forretningsmulighed',
  taskResearchPrompt:
    'Hjælp mig med at undersøge en mulighed for vores forretning ud fra offentlige kilder. Spørg, hvad jeg vil undersøge, og hjælp mig derefter med at lave et nyttigt overblik med kildehenvisninger.',
  taskFileTitle: 'Gennemgå et dokument eller regneark',
  taskFilePrompt:
    'Hjælp mig med at gennemgå et dokument eller regneark. Bed mig vedhæfte en fil, og spørg, hvad jeg vil vide eller have gjort, og guid mig derefter igennem det.',
  taskChooseTitle: 'Hjælp mig med at vælge en første opgave',
  taskChoosePrompt:
    'Hjælp mig med at komme i gang med Poneva. Brug vores virksomheds kontekst og de tilgængelige funktioner til at foreslå et par nyttige ting, vi kan gøre sammen. Stil ét spørgsmål, der hjælper mig med at vælge, og guid mig derefter gennem opgaven.',
  toolGoogleSummary: 'Mail, kalender og dokumenter',
  toolSlackSummary: 'Samtaler og teamets viden',
  toolShopifySummary: 'Produkter, ordrer og lager',
  toolWooSummary: 'Produkter, ordrer og rapporter',
  toolMerchantSummary: 'Produktfeeds og status',
  toolInvoicesSummary: 'Fakturaer og økonomiske oplysninger',
  toolGithubSummary: 'Projekter, issues og pull requests',
  taskMeetingTitle: 'Forbered mit næste møde',
  taskMeetingPrompt:
    'Hjælp mig med at forberede mit næste møde. Tjek min tilgængelige kalender, mail og mine dokumenter, spørg hvilket møde det gælder, hvis det er nødvendigt, og lav et kort oplæg med kilder.',
  taskSlackTitle: 'Følg op på en samtale',
  taskSlackPrompt:
    'Hjælp mig med at følge op på en samtale i Slack. Spørg, hvilken kanal eller hvilket emne vi skal fokusere på, og opsummer derefter beslutninger, åbne spørgsmål og næste skridt med links til beskederne.',
  taskShopifyTitle: 'Se mine produkter med friske øjne',
  taskShopifyPrompt:
    'Gennemgå et lille udvalg af mine Shopify-produkter, og find nyttige forbedringer af produktinformationen. Vis dokumentationen, og ændr ikke noget.',
  taskWooTitle: 'Gennemgå min produktinformation',
  taskWooPrompt:
    'Gennemgå et lille udvalg af mine WooCommerce-produkter, og find huller i produktinformationen. Vis kilderne, og ændr ikke noget.',
  taskFeedTitle: 'Gennemgå mit produktfeed',
  taskFeedPrompt:
    'Hjælp mig med at gennemgå mit produktfeed i Google Merchant Center. Start med den tilgængelige konto og et lille udvalg, og angiv kilder til det, du finder.',
  taskFortnoxTitle: 'Få styr på de seneste fakturaer',
  taskFortnoxPrompt:
    'Hjælp mig med at forstå de seneste fakturaer i Fortnox. Spørg, hvilken periode vi skal gennemgå, og opsummer de tilgængelige oplysninger med kilder. Ændr ikke noget.',
  taskTripletexTitle: 'Få overblik over de seneste fakturaer',
  taskTripletexPrompt:
    'Hjælp mig med at forstå de seneste fakturaer i Tripletex. Spørg, hvilken periode vi skal gennemgå, og opsummer de tilgængelige oplysninger med kilder. Ændr ikke noget.',
  taskGithubTitle: 'Følg op på et projekt',
  taskGithubPrompt:
    'Hjælp mig med at følge op på et GitHub-projekt. Spørg, hvilket repository vi skal gennemgå, og opsummer de seneste issues og pull requests med links.',
  taskTryTitle: 'Prøv {name}',
  taskTryPrompt:
    'Hjælp mig med at komme i gang med min {name}-forbindelse. Find dens tilgængelige værktøjer, foreslå en nyttig første opgave, og spørg, hvad jeg vil gøre. Forelæg alle ændringer for mig til gennemsyn, før du udfører dem.',
  back: 'Tilbage',
  continue: 'Fortsæt',
  continueWithout: 'Fortsæt uden at forbinde',
  goToWorkspace: 'Gå til arbejdsområdet',
  opening: 'Åbner dit arbejdsområde…',
  startFailed: 'Vi kunne ikke åbne dit arbejdsområde. Prøv igen.',
  loadingTitle: 'Åbner din virksomhed…',
  loadingStatus: 'Indlæser dit arbejdsområde',
  errorTitle: 'Lad os prøve igen.',
  tryAgain: 'Prøv igen',
  noCompanyTitle: 'Du skal have en invitation.',
  noCompanyBody:
    'Din konto er ikke knyttet til en virksomhed. Bed en ejer om at invitere din e-mailadresse. Dit private arbejde bevares.',
  checkAccess: 'Tjek adgang',
};

const nl: WelcomeCopy = {
  logOut: 'Uitloggen',
  progressLabel: 'Aan de slag',
  stepOf: 'Stap {current} van {total}',
  stepFocus: 'Je focus',
  stepTools: 'Je tools',
  stepFirst: 'Je eerste stap',
  focusTitle: 'Vertel ons hoe je werkt.',
  focusTitleNamed: 'Welkom, {name}. Hoe werk jij?',
  focusIntro:
    'Kies je rol en waarmee je hulp wilt. Beide zijn optioneel en helpen ons nuttige tools voor te stellen.',
  roleHeading: 'Je rol',
  goalsHeading: 'Waarmee wil je hulp?',
  roleLeadership: 'Directie',
  roleProduct: 'Product',
  roleEngineering: 'Engineering en data',
  roleMarketing: 'Marketing',
  roleSales: 'Verkoop',
  roleOperations: 'Operatie',
  roleFinance: 'Financiën',
  goalEverydayTitle: 'Een stap vooruit blijven',
  goalEverydayBody: 'Vergaderingen voorbereiden en gesprekken bijlezen.',
  goalCommerceTitle: 'Mijn bedrijf begrijpen',
  goalCommerceBody: 'Producten, bestellingen en kansen verkennen.',
  goalFinanceTitle: 'De cijfers helder zien',
  goalFinanceBody: 'Facturen en financiële informatie begrijpen.',
  goalProjectsTitle: 'Het werk gaande houden',
  goalProjectsBody: 'Discussies, issues en projectupdates verbinden.',
  toolsTitle: 'Koppel je dagelijkse tools.',
  toolsIntro: 'Een beetje context maakt een groot verschil. Begin met één of twee.',
  toolsUnavailable:
    'Koppelingen zijn nu niet beschikbaar. Je kunt beginnen met een vraag of een bestand.',
  toolsLabel: 'Koppel je tools',
  suggestedTools: 'Voorgesteld voor jou',
  allTools: 'Alle tools',
  browseAllTools: 'Alle tools bekijken',
  backToSuggestions: 'Terug naar suggesties',
  searchTools: 'Tools zoeken',
  findTool: 'Zoek een tool…',
  popupBlocked:
    'De browser heeft de pop-up geblokkeerd. Deze pagina blijft controleren terwijl je toegang geeft.',
  openAuthorization: 'Open de autorisatiepagina',
  authorizing: 'Rond de autorisatie af in het venster van de aanbieder.',
  stopWaiting: 'Stoppen met wachten',
  loadingConnections: 'Je koppelingen laden…',
  connectionsFailed: 'We konden je koppelingen niet laden.',
  useMyAccount: 'Mijn account gebruiken',
  useCompanyAccount: 'Een account voor {company} gebruiken',
  chooseAudience: 'Kies wie deze koppeling mag gebruiken',
  noToolsMatch: 'Geen tools gevonden voor je zoekopdracht. Probeer een andere naam.',
  noToolsYet:
    'Er zijn nog geen tools om voor te stellen. Je kunt beginnen met een vraag of een bestand.',
  reminderTitle: 'Beter met je tools.',
  reminderIntro:
    'Eén koppeling is genoeg om te beginnen. Probeer een tool die je al gebruikt, of kom hier later op terug.',
  reminderDoneTitle: 'Een goed begin.',
  reminderDoneIntro: 'Je kunt er meer toevoegen wanneer je ze nodig hebt.',
  firstTitle: 'Waar beginnen we?',
  firstIntro: 'Kies een voorbeeld om een gesprek te starten, of ga direct naar je werkruimte.',
  connectedOne: '1 account gekoppeld',
  connectedMany: '{count} accounts gekoppeld',
  taskResearchTitle: 'Een zakelijke kans verkennen',
  taskResearchPrompt:
    'Help me een kans voor ons bedrijf te onderzoeken met openbare bronnen. Vraag wat ik wil verkennen en help me daarna een nuttig overzicht met bronvermeldingen te maken.',
  taskFileTitle: 'Een document of spreadsheet doornemen',
  taskFilePrompt:
    'Help me een document of spreadsheet door te nemen. Vraag me een bestand toe te voegen en wat ik wil weten of gedaan wil krijgen, en begeleid me daarna.',
  taskChooseTitle: 'Help me een eerste taak kiezen',
  taskChoosePrompt:
    'Help me op weg met Poneva. Gebruik de context van ons bedrijf en de beschikbare mogelijkheden om een paar nuttige dingen voor te stellen die we samen kunnen doen. Stel één vraag om me te helpen kiezen en begeleid me daarna door de taak.',
  toolGoogleSummary: 'E-mail, agenda en documenten',
  toolSlackSummary: 'Gesprekken en teamkennis',
  toolShopifySummary: 'Producten, bestellingen en voorraad',
  toolWooSummary: 'Producten, bestellingen en rapporten',
  toolMerchantSummary: 'Productfeeds en statussen',
  toolInvoicesSummary: 'Facturen en financiële informatie',
  toolGithubSummary: 'Projecten, issues en pull requests',
  taskMeetingTitle: 'Mijn volgende meeting voorbereiden',
  taskMeetingPrompt:
    'Help me mijn volgende meeting voor te bereiden. Bekijk mijn beschikbare agenda, e-mail en documenten, vraag zo nodig om welke meeting het gaat en maak een beknopte briefing met bronnen.',
  taskSlackTitle: 'Bijlezen in een gesprek',
  taskSlackPrompt:
    'Help me bij te lezen in een Slack-gesprek. Vraag op welk kanaal of onderwerp we ons richten en vat daarna besluiten, open vragen en volgende stappen samen met links naar de berichten.',
  taskShopifyTitle: 'Met een frisse blik naar mijn producten kijken',
  taskShopifyPrompt:
    'Bekijk een kleine steekproef van mijn Shopify-producten en vind nuttige verbeteringen voor de productinformatie. Laat het bewijs zien en wijzig niets.',
  taskWooTitle: 'Mijn productinformatie nalopen',
  taskWooPrompt:
    'Bekijk een kleine steekproef van mijn WooCommerce-producten en vind hiaten in de productinformatie. Toon bronnen en wijzig niets.',
  taskFeedTitle: 'Mijn productfeed nalopen',
  taskFeedPrompt:
    'Help me mijn productfeed in Google Merchant Center na te lopen. Begin met het beschikbare account en een kleine steekproef, en vermeld bronnen bij wat je vindt.',
  taskFortnoxTitle: 'Recente facturen begrijpen',
  taskFortnoxPrompt:
    'Help me recente facturen in Fortnox te begrijpen. Vraag welke periode we bekijken en vat de beschikbare informatie samen met bronnen. Wijzig niets.',
  taskTripletexTitle: 'Overzicht krijgen van recente facturen',
  taskTripletexPrompt:
    'Help me recente facturen in Tripletex te begrijpen. Vraag welke periode we bekijken en vat de beschikbare informatie samen met bronnen. Wijzig niets.',
  taskGithubTitle: 'Bijlezen in een project',
  taskGithubPrompt:
    'Help me bij te lezen in een GitHub-project. Vraag welke repository we bekijken en vat recente issues en pull requests samen met links.',
  taskTryTitle: '{name} proberen',
  taskTryPrompt:
    'Help me op weg met mijn {name}-koppeling. Ontdek de beschikbare tools, stel een nuttige eerste taak voor en vraag wat ik wil doen. Leg wijzigingen eerst aan mij voor voordat je ze uitvoert.',
  back: 'Terug',
  continue: 'Doorgaan',
  continueWithout: 'Doorgaan zonder koppeling',
  goToWorkspace: 'Naar de werkruimte',
  opening: 'Je werkruimte wordt geopend…',
  startFailed: 'We konden je werkruimte niet openen. Probeer het opnieuw.',
  loadingTitle: 'Je bedrijf wordt geopend…',
  loadingStatus: 'Je werkruimte wordt geladen',
  errorTitle: 'Laten we het opnieuw proberen.',
  tryAgain: 'Opnieuw proberen',
  noCompanyTitle: 'Je hebt een uitnodiging nodig.',
  noCompanyBody:
    'Je account is niet gekoppeld aan een bedrijf. Vraag een eigenaar om je e-mailadres uit te nodigen. Je privéwerk blijft bewaard.',
  checkAccess: 'Toegang controleren',
};

const et: WelcomeCopy = {
  logOut: 'Logi välja',
  progressLabel: 'Alustamine',
  stepOf: 'Samm {current}/{total}',
  stepFocus: 'Sinu fookus',
  stepTools: 'Sinu tööriistad',
  stepFirst: 'Sinu esimene samm',
  focusTitle: 'Räägi meile, kuidas sa töötad.',
  focusTitleNamed: 'Tere tulemast, {name}. Kuidas sa töötad?',
  focusIntro:
    'Vali oma roll ja see, milles soovid abi. Mõlemad on valikulised ja aitavad meil kasulikke tööriistu soovitada.',
  roleHeading: 'Sinu roll',
  goalsHeading: 'Milles sa abi soovid?',
  roleLeadership: 'Juhtkond',
  roleProduct: 'Toode',
  roleEngineering: 'Tehnoloogia ja andmed',
  roleMarketing: 'Turundus',
  roleSales: 'Müük',
  roleOperations: 'Tegevused',
  roleFinance: 'Rahandus',
  goalEverydayTitle: 'Ole sammu võrra ees',
  goalEverydayBody: 'Valmistu koosolekuteks ja jõua vestlustega järele.',
  goalCommerceTitle: 'Mõista oma äri',
  goalCommerceBody: 'Uuri tooteid, tellimusi ja võimalusi.',
  goalFinanceTitle: 'Näe numbreid selgelt',
  goalFinanceBody: 'Mõista arveid ja finantsteavet.',
  goalProjectsTitle: 'Hoia töö liikumas',
  goalProjectsBody: 'Seo arutelud, piletid ja projekti uuendused.',
  toolsTitle: 'Ühenda oma igapäevased tööriistad.',
  toolsIntro: 'Veidi konteksti teeb suure vahe. Alusta ühe või kahega.',
  toolsUnavailable: 'Ühendused pole praegu saadaval. Võid alustada küsimuse või failiga.',
  toolsLabel: 'Ühenda oma tööriistad',
  suggestedTools: 'Sulle soovitatud',
  allTools: 'Kõik tööriistad',
  browseAllTools: 'Sirvi kõiki tööriistu',
  backToSuggestions: 'Tagasi soovituste juurde',
  searchTools: 'Otsi tööriistu',
  findTool: 'Leia tööriist…',
  popupBlocked:
    'Brauser blokeeris hüpikakna. See leht kontrollib edasi, kuni sa juurdepääsu annad.',
  openAuthorization: 'Ava autoriseerimisleht',
  authorizing: 'Lõpeta autoriseerimine teenusepakkuja aknas.',
  stopWaiting: 'Lõpeta ootamine',
  loadingConnections: 'Laadin sinu ühendusi…',
  connectionsFailed: 'Sinu ühendusi ei õnnestunud laadida.',
  useMyAccount: 'Kasuta minu kontot',
  useCompanyAccount: 'Kasuta ettevõtte {company} kontot',
  chooseAudience: 'Vali, kes saavad seda ühendust kasutada',
  noToolsMatch: 'Otsingule ei vasta ükski tööriist. Proovi mõnda teist nime.',
  noToolsYet: 'Veel pole tööriistu, mida soovitada. Võid alustada küsimuse või failiga.',
  reminderTitle: 'Parem oma tööriistadega.',
  reminderIntro:
    'Alustamiseks piisab ühest ühendusest. Proovi tööriista, mida juba kasutad, või tule selle juurde hiljem tagasi.',
  reminderDoneTitle: 'Hea algus.',
  reminderDoneIntro: 'Saad neid juurde lisada, kui vaja.',
  firstTitle: 'Millest alustame?',
  firstIntro: 'Vali näide, et vestlust alustada, või mine otse oma tööruumi.',
  connectedOne: '1 konto ühendatud',
  connectedMany: '{count} kontot ühendatud',
  taskResearchTitle: 'Uuri ärivõimalust',
  taskResearchPrompt:
    'Aita mul avalike allikate põhjal uurida meie ettevõtte jaoks üht võimalust. Küsi, mida tahan uurida, ja aita mul seejärel koostada kasulik allikaviidetega ülevaade.',
  taskFileTitle: 'Töötle dokumenti või arvutustabelit',
  taskFilePrompt:
    'Aita mul läbi töötada dokument või arvutustabel. Palu mul fail lisada ja küsi, mida tahan teada saada või ära teha, ning juhenda mind seejärel.',
  taskChooseTitle: 'Aita mul esimene ülesanne valida',
  taskChoosePrompt:
    'Aita mul Ponevaga alustada. Kasuta meie ettevõtte konteksti ja saadaolevaid võimalusi, et soovitada paari kasulikku asja, mida saame koos teha. Esita üks küsimus, mis aitab mul valida, ja juhenda mind seejärel ülesande läbi.',
  toolGoogleSummary: 'E-post, kalender ja dokumendid',
  toolSlackSummary: 'Vestlused ja meeskonna teadmised',
  toolShopifySummary: 'Tooted, tellimused ja laoseis',
  toolWooSummary: 'Tooted, tellimused ja aruanded',
  toolMerchantSummary: 'Tootevood ja olekud',
  toolInvoicesSummary: 'Arved ja finantsteave',
  toolGithubSummary: 'Projektid, ülesanded ja muudatustaotlused',
  taskMeetingTitle: 'Aita mul järgmiseks kohtumiseks valmistuda',
  taskMeetingPrompt:
    'Aita mul järgmiseks kohtumiseks valmistuda. Vaata läbi minu kättesaadav kalender, e-post ja dokumendid, küsi vajadusel, millisest kohtumisest jutt käib, ja koosta allikatega lühike ülevaade.',
  taskSlackTitle: 'Vii mind vestlusega kurssi',
  taskSlackPrompt:
    'Vii mind Slacki vestlusega kurssi. Küsi, millisele kanalile või teemale keskenduda, ja tee siis kokkuvõte otsustest, lahtistest küsimustest ja järgmistest sammudest koos linkidega sõnumitele.',
  taskShopifyTitle: 'Vaata mu tooteid värske pilguga',
  taskShopifyPrompt:
    'Vaata läbi väike valim minu Shopify toodetest ja leia kasulikke parandusi tooteteabes. Näita tõendeid ja ära muuda midagi.',
  taskWooTitle: 'Vaata mu tooteteave üle',
  taskWooPrompt:
    'Vaata läbi väike valim minu WooCommerce’i toodetest ja leia tooteteabe lüngad. Näita allikaid ja ära muuda midagi.',
  taskFeedTitle: 'Vaata mu tootevoog üle',
  taskFeedPrompt:
    'Aita mul üle vaadata minu Google Merchant Centeri tootevoog. Alusta ligipääsetava kontoga ja väikese valimiga ning viita leitule allikatega.',
  taskFortnoxTitle: 'Aita mul hiljutistest arvetest aru saada',
  taskFortnoxPrompt:
    'Aita mul mõista hiljutisi arveid Fortnoxis. Küsi, millist perioodi vaadata, ja tee kättesaadavast teabest allikatega kokkuvõte. Ära muuda midagi.',
  taskTripletexTitle: 'Anna ülevaade hiljutistest arvetest',
  taskTripletexPrompt:
    'Aita mul mõista hiljutisi arveid Tripletexis. Küsi, millist perioodi vaadata, ja tee kättesaadavast teabest allikatega kokkuvõte. Ära muuda midagi.',
  taskGithubTitle: 'Vii mind projektiga kurssi',
  taskGithubPrompt:
    'Vii mind GitHubi projektiga kurssi. Küsi, millist repositooriumi vaadata, ja tee hiljutistest ülesannetest ja muudatustaotlustest linkidega kokkuvõte.',
  taskTryTitle: 'Proovi ühendust {name}',
  taskTryPrompt:
    'Aita mul alustada ühendusega {name}. Uuri välja selle saadaolevad tööriistad, soovita kasulik esimene ülesanne ja küsi, mida ma teha tahan. Enne muudatuste tegemist esita need mulle ülevaatamiseks.',
  back: 'Tagasi',
  continue: 'Jätka',
  continueWithout: 'Jätka ühendamata',
  goToWorkspace: 'Mine tööruumi',
  opening: 'Sinu tööruumi avamine…',
  startFailed: 'Sinu tööruumi ei õnnestunud avada. Proovi uuesti.',
  loadingTitle: 'Sinu ettevõtte avamine…',
  loadingStatus: 'Sinu tööruumi laadimine',
  errorTitle: 'Proovime uuesti.',
  tryAgain: 'Proovi uuesti',
  noCompanyTitle: 'Vajad kutset.',
  noCompanyBody:
    'Sinu konto pole ühegi ettevõttega seotud. Palu omanikul oma e-posti aadress kutsuda. Sinu privaatne töö säilib.',
  checkAccess: 'Kontrolli juurdepääsu',
};

const fi: WelcomeCopy = {
  logOut: 'Kirjaudu ulos',
  progressLabel: 'Aloitus',
  stepOf: 'Vaihe {current}/{total}',
  stepFocus: 'Painopisteesi',
  stepTools: 'Työkalusi',
  stepFirst: 'Ensimmäinen askeleesi',
  focusTitle: 'Kerro, miten työskentelet.',
  focusTitleNamed: 'Tervetuloa, {name}. Miten työskentelet?',
  focusIntro:
    'Valitse roolisi ja se, missä haluat apua. Molemmat ovat vapaaehtoisia ja auttavat meitä ehdottamaan hyödyllisiä työkaluja.',
  roleHeading: 'Roolisi',
  goalsHeading: 'Missä haluaisit apua?',
  roleLeadership: 'Johto',
  roleProduct: 'Tuote',
  roleEngineering: 'Tekniikka ja data',
  roleMarketing: 'Markkinointi',
  roleSales: 'Myynti',
  roleOperations: 'Operatiivinen toiminta',
  roleFinance: 'Talous',
  goalEverydayTitle: 'Pysy askeleen edellä',
  goalEverydayBody: 'Valmistaudu kokouksiin ja pysy keskustelujen tasalla.',
  goalCommerceTitle: 'Ymmärrä liiketoimintaani',
  goalCommerceBody: 'Tutki tuotteita, tilauksia ja mahdollisuuksia.',
  goalFinanceTitle: 'Näe luvut selkeästi',
  goalFinanceBody: 'Ymmärrä laskuja ja taloustietoja.',
  goalProjectsTitle: 'Pidä työ liikkeessä',
  goalProjectsBody: 'Yhdistä keskustelut, tiketit ja projektipäivitykset.',
  toolsTitle: 'Yhdistä arjen työkalusi.',
  toolsIntro: 'Pieni konteksti tekee suuren eron. Aloita yhdellä tai kahdella.',
  toolsUnavailable:
    'Yhteydet eivät ole juuri nyt käytettävissä. Voit aloittaa kysymyksellä tai tiedostolla.',
  toolsLabel: 'Yhdistä työkalusi',
  suggestedTools: 'Ehdotukset sinulle',
  allTools: 'Kaikki työkalut',
  browseAllTools: 'Selaa kaikkia työkaluja',
  backToSuggestions: 'Takaisin ehdotuksiin',
  searchTools: 'Hae työkaluja',
  findTool: 'Etsi työkalu…',
  popupBlocked:
    'Selain esti ponnahdusikkunan. Tämä sivu jatkaa tarkistamista, kun myönnät käyttöoikeuden.',
  openAuthorization: 'Avaa valtuutussivu',
  authorizing: 'Viimeistele valtuutus palveluntarjoajan ikkunassa.',
  stopWaiting: 'Lopeta odottaminen',
  loadingConnections: 'Ladataan yhteyksiäsi…',
  connectionsFailed: 'Yhteyksiäsi ei voitu ladata.',
  useMyAccount: 'Käytä omaa tiliäni',
  useCompanyAccount: 'Käytä tiliä yritykselle {company}',
  chooseAudience: 'Valitse, ketkä voivat käyttää tätä yhteyttä',
  noToolsMatch: 'Hakuasi vastaavia työkaluja ei löytynyt. Kokeile toista nimeä.',
  noToolsYet: 'Ehdotettavia työkaluja ei vielä ole. Voit aloittaa kysymyksellä tai tiedostolla.',
  reminderTitle: 'Parempi työkaluillasi.',
  reminderIntro:
    'Yksi yhteys riittää alkuun. Kokeile työkalua, jota jo käytät, tai palaa tähän myöhemmin.',
  reminderDoneTitle: 'Hyvä alku.',
  reminderDoneIntro: 'Voit lisätä niitä lisää aina tarvittaessa.',
  firstTitle: 'Mistä aloitetaan?',
  firstIntro: 'Valitse esimerkki aloittaaksesi keskustelun tai siirry suoraan työtilaasi.',
  connectedOne: '1 tili yhdistetty',
  connectedMany: '{count} tiliä yhdistetty',
  taskResearchTitle: 'Tutki liiketoimintamahdollisuutta',
  taskResearchPrompt:
    'Auta minua tutkimaan liiketoimintamme mahdollisuutta julkisten lähteiden avulla. Kysy, mitä haluan selvittää, ja auta sitten laatimaan hyödyllinen yhteenveto lähteineen.',
  taskFileTitle: 'Käy läpi asiakirja tai laskentataulukko',
  taskFilePrompt:
    'Auta minua käymään läpi asiakirja tai laskentataulukko. Pyydä minua liittämään tiedosto ja kysy, mitä haluan selvittää tai saada tehdyksi, ja opasta minut sitten sen läpi.',
  taskChooseTitle: 'Auta minua valitsemaan ensimmäinen tehtävä',
  taskChoosePrompt:
    'Auta minua pääsemään alkuun Ponevan kanssa. Käytä yrityksemme kontekstia ja käytettävissä olevia ominaisuuksia ehdottaaksesi muutamia hyödyllisiä asioita, joita voimme tehdä yhdessä. Esitä yksi kysymys, joka auttaa minua valitsemaan, ja opasta minut sitten tehtävän läpi.',
  toolGoogleSummary: 'Sähköposti, kalenteri ja dokumentit',
  toolSlackSummary: 'Keskustelut ja tiimin tieto',
  toolShopifySummary: 'Tuotteet, tilaukset ja varasto',
  toolWooSummary: 'Tuotteet, tilaukset ja raportit',
  toolMerchantSummary: 'Tuotesyötteet ja tilat',
  toolInvoicesSummary: 'Laskut ja taloustiedot',
  toolGithubSummary: 'Projektit, tiketit ja pull requestit',
  taskMeetingTitle: 'Valmistaudu seuraavaan tapaamiseen',
  taskMeetingPrompt:
    'Auta minua valmistautumaan seuraavaan tapaamiseeni. Tarkista käytettävissä oleva kalenterini, sähköpostini ja dokumenttini, kysy tarvittaessa, mistä tapaamisesta on kyse, ja laadi tiivis muistio lähteineen.',
  taskSlackTitle: 'Pääse kärryille keskustelusta',
  taskSlackPrompt:
    'Auta minua pääsemään kärryille Slack-keskustelusta. Kysy, mihin kanavaan tai aiheeseen keskitytään, ja tee sitten yhteenveto päätöksistä, avoimista kysymyksistä ja seuraavista vaiheista linkkeineen viesteihin.',
  taskShopifyTitle: 'Katso tuotteitani uusin silmin',
  taskShopifyPrompt:
    'Käy läpi pieni otos Shopify-tuotteistani ja etsi hyödyllisiä parannuksia tuotetietoihin. Näytä perusteet äläkä muuta mitään.',
  taskWooTitle: 'Tarkista tuotetietoni',
  taskWooPrompt:
    'Käy läpi pieni otos WooCommerce-tuotteistani ja etsi puutteita tuotetiedoista. Näytä lähteet äläkä muuta mitään.',
  taskFeedTitle: 'Tarkista tuotesyötteeni',
  taskFeedPrompt:
    'Auta minua tarkistamaan Google Merchant Center -tuotesyötteeni. Aloita käytettävissä olevasta tilistä ja pienestä otoksesta, ja mainitse lähteet havainnoillesi.',
  taskFortnoxTitle: 'Selvitä viimeaikaiset laskut',
  taskFortnoxPrompt:
    'Auta minua ymmärtämään viimeaikaiset laskut Fortnoxissa. Kysy, mikä jakso käydään läpi, ja tiivistä saatavilla olevat tiedot lähteineen. Älä muuta mitään.',
  taskTripletexTitle: 'Luo yleiskuva viimeaikaisista laskuista',
  taskTripletexPrompt:
    'Auta minua ymmärtämään viimeaikaiset laskut Tripletexissä. Kysy, mikä jakso käydään läpi, ja tiivistä saatavilla olevat tiedot lähteineen. Älä muuta mitään.',
  taskGithubTitle: 'Pääse kärryille projektista',
  taskGithubPrompt:
    'Auta minua pääsemään kärryille GitHub-projektista. Kysy, mikä repositorio käydään läpi, ja tee yhteenveto viimeaikaisista tiketeistä ja pull requesteista linkkeineen.',
  taskTryTitle: 'Kokeile yhteyttä {name}',
  taskTryPrompt:
    'Auta minua pääsemään alkuun yhteyden {name} kanssa. Selvitä sen käytettävissä olevat työkalut, ehdota hyödyllistä ensimmäistä tehtävää ja kysy, mitä haluan tehdä. Esitä kaikki muutokset minulle tarkistettavaksi ennen kuin teet ne.',
  back: 'Takaisin',
  continue: 'Jatka',
  continueWithout: 'Jatka yhdistämättä',
  goToWorkspace: 'Siirry työtilaan',
  opening: 'Avataan työtilaasi…',
  startFailed: 'Työtilaasi ei voitu avata. Yritä uudelleen.',
  loadingTitle: 'Avataan yritystäsi…',
  loadingStatus: 'Ladataan työtilaasi',
  errorTitle: 'Yritetään uudelleen.',
  tryAgain: 'Yritä uudelleen',
  noCompanyTitle: 'Tarvitset kutsun.',
  noCompanyBody:
    'Tiliäsi ei ole liitetty yritykseen. Pyydä omistajaa kutsumaan sähköpostiosoitteesi. Yksityinen työsi säilyy.',
  checkAccess: 'Tarkista käyttöoikeus',
};

const fr: WelcomeCopy = {
  logOut: 'Se déconnecter',
  progressLabel: 'Premiers pas',
  stepOf: 'Étape {current} sur {total}',
  stepFocus: 'Vos priorités',
  stepTools: 'Vos outils',
  stepFirst: 'Votre première étape',
  focusTitle: 'Dites-nous comment vous travaillez.',
  focusTitleNamed: 'Bienvenue, {name}. Comment travaillez-vous ?',
  focusIntro:
    'Choisissez votre rôle et ce pour quoi vous souhaitez de l’aide. Les deux sont facultatifs et nous aident à suggérer des outils utiles.',
  roleHeading: 'Votre rôle',
  goalsHeading: 'Pour quoi souhaitez-vous de l’aide ?',
  roleLeadership: 'Direction',
  roleProduct: 'Produit',
  roleEngineering: 'Ingénierie et données',
  roleMarketing: 'Marketing',
  roleSales: 'Ventes',
  roleOperations: 'Opérations',
  roleFinance: 'Finance',
  goalEverydayTitle: 'Garder une longueur d’avance',
  goalEverydayBody: 'Préparer vos réunions et rattraper les conversations.',
  goalCommerceTitle: 'Comprendre mon activité',
  goalCommerceBody: 'Explorer produits, commandes et opportunités.',
  goalFinanceTitle: 'Voir clair dans les chiffres',
  goalFinanceBody: 'Comprendre les factures et les informations financières.',
  goalProjectsTitle: 'Faire avancer le travail',
  goalProjectsBody: 'Relier discussions, tickets et mises à jour de projet.',
  toolsTitle: 'Connectez vos outils du quotidien.',
  toolsIntro: 'Un peu de contexte fait une grande différence. Commencez par un ou deux.',
  toolsUnavailable:
    'Les connexions ne sont pas disponibles pour le moment. Vous pouvez commencer par une question ou un fichier.',
  toolsLabel: 'Connectez vos outils',
  suggestedTools: 'Suggestions pour vous',
  allTools: 'Tous les outils',
  browseAllTools: 'Parcourir tous les outils',
  backToSuggestions: 'Retour aux suggestions',
  searchTools: 'Rechercher des outils',
  findTool: 'Trouver un outil…',
  popupBlocked:
    'Le navigateur a bloqué la fenêtre pop-up. Cette page continue de vérifier pendant que vous donnez votre autorisation.',
  openAuthorization: 'Ouvrir la page d’autorisation',
  authorizing: 'Terminez l’autorisation dans la fenêtre du fournisseur.',
  stopWaiting: 'Arrêter d’attendre',
  loadingConnections: 'Chargement de vos connexions…',
  connectionsFailed: 'Impossible de charger vos connexions.',
  useMyAccount: 'Utiliser mon compte',
  useCompanyAccount: 'Utiliser un compte pour {company}',
  chooseAudience: 'Choisir qui peut utiliser cette connexion',
  noToolsMatch: 'Aucun outil ne correspond à votre recherche. Essayez un autre nom.',
  noToolsYet:
    'Aucun outil à suggérer pour le moment. Vous pouvez commencer par une question ou un fichier.',
  reminderTitle: 'Mieux avec vos outils.',
  reminderIntro:
    'Une seule connexion suffit pour commencer. Essayez un outil que vous utilisez déjà, ou revenez-y plus tard.',
  reminderDoneTitle: 'Un bon début.',
  reminderDoneIntro: 'Vous pourrez en ajouter d’autres quand vous en aurez besoin.',
  firstTitle: 'Par où commencer ?',
  firstIntro:
    'Choisissez un exemple pour lancer une conversation, ou accédez directement à votre espace de travail.',
  connectedOne: '1 compte connecté',
  connectedMany: '{count} comptes connectés',
  taskResearchTitle: 'Explorer une opportunité commerciale',
  taskResearchPrompt:
    'Aide-moi à étudier une opportunité pour notre entreprise à partir de sources publiques. Demande-moi ce que je veux explorer, puis aide-moi à rédiger une synthèse utile et sourcée.',
  taskFileTitle: 'Analyser un document ou un tableur',
  taskFilePrompt:
    'Aide-moi à analyser un document ou un tableur. Demande-moi de joindre un fichier et ce que je veux apprendre ou accomplir, puis guide-moi.',
  taskChooseTitle: 'Aidez-moi à choisir une première tâche',
  taskChoosePrompt:
    'Aide-moi à démarrer avec Poneva. Utilise le contexte de notre entreprise et les capacités disponibles pour proposer quelques choses utiles à faire ensemble. Pose une question pour m’aider à choisir, puis guide-moi dans la tâche.',
  toolGoogleSummary: 'E-mail, agenda et documents',
  toolSlackSummary: 'Conversations et connaissances de l’équipe',
  toolShopifySummary: 'Produits, commandes et stock',
  toolWooSummary: 'Produits, commandes et rapports',
  toolMerchantSummary: 'Flux de produits et statuts',
  toolInvoicesSummary: 'Factures et informations financières',
  toolGithubSummary: 'Projets, tickets et pull requests',
  taskMeetingTitle: 'Préparer ma prochaine réunion',
  taskMeetingPrompt:
    'Aide-moi à préparer ma prochaine réunion. Consulte mon agenda, mes e-mails et mes documents disponibles, demande-moi de quelle réunion il s’agit si nécessaire, et rédige une note concise avec les sources.',
  taskSlackTitle: 'Rattraper une conversation',
  taskSlackPrompt:
    'Aide-moi à rattraper une conversation Slack. Demande-moi sur quel canal ou quel sujet nous concentrer, puis résume les décisions, les questions ouvertes et les prochaines étapes avec des liens vers les messages.',
  taskShopifyTitle: 'Porter un regard neuf sur mes produits',
  taskShopifyPrompt:
    'Examine un petit échantillon de mes produits Shopify et repère des améliorations utiles des informations produit. Montre les éléments sur lesquels tu t’appuies et ne modifie rien.',
  taskWooTitle: 'Vérifier mes informations produit',
  taskWooPrompt:
    'Examine un petit échantillon de mes produits WooCommerce et repère les lacunes dans les informations produit. Indique les sources et ne modifie rien.',
  taskFeedTitle: 'Vérifier mon flux de produits',
  taskFeedPrompt:
    'Aide-moi à vérifier mon flux de produits Google Merchant Center. Commence par le compte accessible et un petit échantillon, et cite tes sources pour ce que tu trouves.',
  taskFortnoxTitle: 'Y voir clair dans les factures récentes',
  taskFortnoxPrompt:
    'Aide-moi à comprendre les factures récentes dans Fortnox. Demande-moi quelle période examiner et résume les informations disponibles avec les sources. Ne modifie rien.',
  taskTripletexTitle: 'Avoir une vue d’ensemble des factures récentes',
  taskTripletexPrompt:
    'Aide-moi à comprendre les factures récentes dans Tripletex. Demande-moi quelle période examiner et résume les informations disponibles avec les sources. Ne modifie rien.',
  taskGithubTitle: 'Faire le point sur un projet',
  taskGithubPrompt:
    'Aide-moi à faire le point sur un projet GitHub. Demande-moi quel dépôt examiner et résume les tickets et pull requests récents avec des liens.',
  taskTryTitle: 'Essayer {name}',
  taskTryPrompt:
    'Aide-moi à démarrer avec ma connexion {name}. Découvre les outils disponibles, propose une première tâche utile et demande-moi ce que je veux faire. Soumets-moi toute modification pour validation avant de l’exécuter.',
  back: 'Retour',
  continue: 'Continuer',
  continueWithout: 'Continuer sans connexion',
  goToWorkspace: 'Aller à l’espace de travail',
  opening: 'Ouverture de votre espace de travail…',
  startFailed: 'Impossible d’ouvrir votre espace de travail. Veuillez réessayer.',
  loadingTitle: 'Ouverture de votre entreprise…',
  loadingStatus: 'Chargement de votre espace de travail',
  errorTitle: 'Réessayons.',
  tryAgain: 'Réessayer',
  noCompanyTitle: 'Une invitation est nécessaire.',
  noCompanyBody:
    'Votre compte n’est lié à aucune entreprise. Demandez à un propriétaire d’inviter votre adresse e-mail. Votre travail privé est conservé.',
  checkAccess: 'Vérifier l’accès',
};

const de: WelcomeCopy = {
  logOut: 'Abmelden',
  progressLabel: 'Erste Schritte',
  stepOf: 'Schritt {current} von {total}',
  stepFocus: 'Ihr Fokus',
  stepTools: 'Ihre Tools',
  stepFirst: 'Ihr erster Schritt',
  focusTitle: 'Erzählen Sie uns, wie Sie arbeiten.',
  focusTitleNamed: 'Willkommen, {name}. Wie arbeiten Sie?',
  focusIntro:
    'Wählen Sie Ihre Rolle und wobei Sie Hilfe möchten. Beides ist optional und hilft uns, passende Tools vorzuschlagen.',
  roleHeading: 'Ihre Rolle',
  goalsHeading: 'Wobei möchten Sie Hilfe?',
  roleLeadership: 'Geschäftsleitung',
  roleProduct: 'Produkt',
  roleEngineering: 'Technik und Daten',
  roleMarketing: 'Marketing',
  roleSales: 'Vertrieb',
  roleOperations: 'Betrieb',
  roleFinance: 'Finanzen',
  goalEverydayTitle: 'Einen Schritt voraus sein',
  goalEverydayBody: 'Meetings vorbereiten und Gespräche nachholen.',
  goalCommerceTitle: 'Mein Geschäft verstehen',
  goalCommerceBody: 'Produkte, Bestellungen und Chancen erkunden.',
  goalFinanceTitle: 'Die Zahlen klar sehen',
  goalFinanceBody: 'Rechnungen und Finanzdaten verstehen.',
  goalProjectsTitle: 'Die Arbeit am Laufen halten',
  goalProjectsBody: 'Diskussionen, Tickets und Projektupdates verbinden.',
  toolsTitle: 'Verbinden Sie Ihre Alltagstools.',
  toolsIntro: 'Ein wenig Kontext macht einen großen Unterschied. Beginnen Sie mit einem oder zwei.',
  toolsUnavailable:
    'Verbindungen sind gerade nicht verfügbar. Sie können mit einer Frage oder einer Datei beginnen.',
  toolsLabel: 'Verbinden Sie Ihre Tools',
  suggestedTools: 'Vorschläge für Sie',
  allTools: 'Alle Tools',
  browseAllTools: 'Alle Tools durchsuchen',
  backToSuggestions: 'Zurück zu den Vorschlägen',
  searchTools: 'Tools suchen',
  findTool: 'Tool finden…',
  popupBlocked:
    'Der Browser hat das Pop-up blockiert. Diese Seite prüft weiter, während Sie den Zugriff erlauben.',
  openAuthorization: 'Autorisierungsseite öffnen',
  authorizing: 'Schließen Sie die Autorisierung im Fenster des Anbieters ab.',
  stopWaiting: 'Nicht mehr warten',
  loadingConnections: 'Ihre Verbindungen werden geladen…',
  connectionsFailed: 'Ihre Verbindungen konnten nicht geladen werden.',
  useMyAccount: 'Mein Konto verwenden',
  useCompanyAccount: 'Ein Konto für {company} verwenden',
  chooseAudience: 'Festlegen, wer diese Verbindung nutzen darf',
  noToolsMatch: 'Keine Tools passen zu Ihrer Suche. Versuchen Sie einen anderen Namen.',
  noToolsYet:
    'Es gibt noch keine Tools zum Vorschlagen. Sie können mit einer Frage oder einer Datei beginnen.',
  reminderTitle: 'Besser mit Ihren Tools.',
  reminderIntro:
    'Eine Verbindung genügt für den Anfang. Probieren Sie ein Tool aus, das Sie bereits nutzen, oder kommen Sie später darauf zurück.',
  reminderDoneTitle: 'Ein guter Anfang.',
  reminderDoneIntro: 'Sie können jederzeit weitere hinzufügen.',
  firstTitle: 'Womit fangen wir an?',
  firstIntro:
    'Wählen Sie ein Beispiel, um ein Gespräch zu beginnen, oder gehen Sie direkt zu Ihrem Arbeitsbereich.',
  connectedOne: '1 Konto verbunden',
  connectedMany: '{count} Konten verbunden',
  taskResearchTitle: 'Eine Geschäftschance erkunden',
  taskResearchPrompt:
    'Hilf mir, eine Chance für unser Geschäft anhand öffentlicher Quellen zu recherchieren. Frag, was ich erkunden möchte, und hilf mir dann, eine nützliche Zusammenfassung mit Quellen zu erstellen.',
  taskFileTitle: 'Ein Dokument oder eine Tabelle durcharbeiten',
  taskFilePrompt:
    'Hilf mir, ein Dokument oder eine Tabelle durchzuarbeiten. Bitte mich, eine Datei anzuhängen, frag, was ich herausfinden oder erledigen möchte, und führe mich dann hindurch.',
  taskChooseTitle: 'Hilf mir, eine erste Aufgabe zu wählen',
  taskChoosePrompt:
    'Hilf mir beim Einstieg in Poneva. Nutze den Kontext unseres Unternehmens und die verfügbaren Funktionen, um ein paar nützliche Dinge vorzuschlagen, die wir gemeinsam tun können. Stell eine Frage, die mir bei der Wahl hilft, und führe mich dann durch die Aufgabe.',
  toolGoogleSummary: 'E-Mail, Kalender und Dokumente',
  toolSlackSummary: 'Unterhaltungen und Teamwissen',
  toolShopifySummary: 'Produkte, Bestellungen und Bestand',
  toolWooSummary: 'Produkte, Bestellungen und Berichte',
  toolMerchantSummary: 'Produktfeeds und Status',
  toolInvoicesSummary: 'Rechnungen und Finanzinformationen',
  toolGithubSummary: 'Projekte, Issues und Pull Requests',
  taskMeetingTitle: 'Mein nächstes Meeting vorbereiten',
  taskMeetingPrompt:
    'Unterstützen Sie mich bei der Vorbereitung auf mein nächstes Meeting. Prüfen Sie meinen verfügbaren Kalender, meine E-Mails und Dokumente, fragen Sie bei Bedarf, um welches Meeting es sich handelt, und erstellen Sie eine kurze Zusammenfassung mit Quellen.',
  taskSlackTitle: 'Eine Unterhaltung nachlesen',
  taskSlackPrompt:
    'Hilf mir, bei einer Slack-Unterhaltung auf den neuesten Stand zu kommen. Frag, auf welchen Kanal oder welches Thema wir uns konzentrieren, und fasse dann Entscheidungen, offene Fragen und nächste Schritte mit Links zu den Nachrichten zusammen.',
  taskShopifyTitle: 'Meine Produkte mit frischem Blick ansehen',
  taskShopifyPrompt:
    'Prüfe eine kleine Auswahl meiner Shopify-Produkte und finde sinnvolle Verbesserungen der Produktinformationen. Zeig die Belege und ändere nichts.',
  taskWooTitle: 'Meine Produktinformationen prüfen',
  taskWooPrompt:
    'Prüfe eine kleine Auswahl meiner WooCommerce-Produkte und finde Lücken in den Produktinformationen. Nenne Quellen und ändere nichts.',
  taskFeedTitle: 'Meinen Produktfeed prüfen',
  taskFeedPrompt:
    'Unterstützen Sie mich bei der Überprüfung meines Produktfeeds im Google Merchant Center. Beginnen Sie mit dem zugänglichen Konto und einer kleinen Stichprobe und nennen Sie die Quellen Ihrer Ergebnisse.',
  taskFortnoxTitle: 'Aktuelle Rechnungen verstehen',
  taskFortnoxPrompt:
    'Hilf mir, aktuelle Rechnungen in Fortnox zu verstehen. Frag, welcher Zeitraum geprüft werden soll, und fasse die verfügbaren Informationen mit Quellen zusammen. Ändere nichts.',
  taskTripletexTitle: 'Überblick über aktuelle Rechnungen',
  taskTripletexPrompt:
    'Hilf mir, aktuelle Rechnungen in Tripletex zu verstehen. Frag, welcher Zeitraum geprüft werden soll, und fasse die verfügbaren Informationen mit Quellen zusammen. Ändere nichts.',
  taskGithubTitle: 'Auf den Stand eines Projekts kommen',
  taskGithubPrompt:
    'Hilf mir, bei einem GitHub-Projekt auf den neuesten Stand zu kommen. Frag, welches Repository geprüft werden soll, und fasse aktuelle Issues und Pull Requests mit Links zusammen.',
  taskTryTitle: '{name} ausprobieren',
  taskTryPrompt:
    'Unterstützen Sie mich beim Einstieg in meine {name}-Verbindung. Ermitteln Sie die verfügbaren Tools, schlagen Sie eine nützliche erste Aufgabe vor und fragen Sie, was ich tun möchte. Schlagen Sie alle Änderungen zur Prüfung vor, bevor Sie sie ausführen.',
  back: 'Zurück',
  continue: 'Weiter',
  continueWithout: 'Ohne Verbindung fortfahren',
  goToWorkspace: 'Zum Arbeitsbereich',
  opening: 'Ihr Arbeitsbereich wird geöffnet…',
  startFailed: 'Ihr Arbeitsbereich konnte nicht geöffnet werden. Bitte versuchen Sie es erneut.',
  loadingTitle: 'Ihr Unternehmen wird geöffnet…',
  loadingStatus: 'Ihr Arbeitsbereich wird geladen',
  errorTitle: 'Versuchen wir es noch einmal.',
  tryAgain: 'Erneut versuchen',
  noCompanyTitle: 'Eine Einladung ist erforderlich.',
  noCompanyBody:
    'Ihr Konto ist mit keinem Unternehmen verbunden. Bitten Sie eine Inhaberin oder einen Inhaber, Ihre E-Mail-Adresse einzuladen. Ihre private Arbeit bleibt erhalten.',
  checkAccess: 'Zugriff prüfen',
};

const el: WelcomeCopy = {
  logOut: 'Αποσύνδεση',
  progressLabel: 'Πρώτα βήματα',
  stepOf: 'Βήμα {current} από {total}',
  stepFocus: 'Η εστίασή σας',
  stepTools: 'Τα εργαλεία σας',
  stepFirst: 'Το πρώτο σας βήμα',
  focusTitle: 'Πείτε μας πώς εργάζεστε.',
  focusTitleNamed: 'Καλώς ήρθατε, {name}. Πώς εργάζεστε;',
  focusIntro:
    'Επιλέξτε τον ρόλο σας και σε τι θέλετε βοήθεια. Και τα δύο είναι προαιρετικά και μας βοηθούν να προτείνουμε χρήσιμα εργαλεία.',
  roleHeading: 'Ο ρόλος σας',
  goalsHeading: 'Σε τι θα θέλατε βοήθεια;',
  roleLeadership: 'Διοίκηση',
  roleProduct: 'Προϊόν',
  roleEngineering: 'Μηχανική και δεδομένα',
  roleMarketing: 'Μάρκετινγκ',
  roleSales: 'Πωλήσεις',
  roleOperations: 'Λειτουργίες',
  roleFinance: 'Οικονομικά',
  goalEverydayTitle: 'Ένα βήμα μπροστά',
  goalEverydayBody: 'Προετοιμαστείτε για συναντήσεις και ενημερωθείτε για συζητήσεις.',
  goalCommerceTitle: 'Κατανόηση της επιχείρησής μου',
  goalCommerceBody: 'Εξερευνήστε προϊόντα, παραγγελίες και ευκαιρίες.',
  goalFinanceTitle: 'Καθαρή εικόνα των αριθμών',
  goalFinanceBody: 'Βγάλτε νόημα από τιμολόγια και οικονομικά στοιχεία.',
  goalProjectsTitle: 'Η δουλειά να προχωρά',
  goalProjectsBody: 'Συνδέστε συζητήσεις, ζητήματα και ενημερώσεις έργων.',
  toolsTitle: 'Συνδέστε τα καθημερινά σας εργαλεία.',
  toolsIntro: 'Λίγο πλαίσιο κάνει μεγάλη διαφορά. Ξεκινήστε με ένα ή δύο.',
  toolsUnavailable:
    'Οι συνδέσεις δεν είναι διαθέσιμες αυτή τη στιγμή. Μπορείτε να ξεκινήσετε με μια ερώτηση ή ένα αρχείο.',
  toolsLabel: 'Συνδέστε τα εργαλεία σας',
  suggestedTools: 'Προτάσεις για εσάς',
  allTools: 'Όλα τα εργαλεία',
  browseAllTools: 'Περιήγηση σε όλα τα εργαλεία',
  backToSuggestions: 'Πίσω στις προτάσεις',
  searchTools: 'Αναζήτηση εργαλείων',
  findTool: 'Βρείτε ένα εργαλείο…',
  popupBlocked:
    'Το πρόγραμμα περιήγησης μπλόκαρε το αναδυόμενο παράθυρο. Αυτή η σελίδα συνεχίζει να ελέγχει όσο δίνετε την εξουσιοδότηση.',
  openAuthorization: 'Ανοίξτε τη σελίδα εξουσιοδότησης',
  authorizing: 'Ολοκληρώστε την εξουσιοδότηση στο παράθυρο του παρόχου.',
  stopWaiting: 'Διακοπή αναμονής',
  loadingConnections: 'Φόρτωση των συνδέσεών σας…',
  connectionsFailed: 'Δεν ήταν δυνατή η φόρτωση των συνδέσεών σας.',
  useMyAccount: 'Χρήση του λογαριασμού μου',
  useCompanyAccount: 'Χρήση λογαριασμού για {company}',
  chooseAudience: 'Επιλέξτε ποιοι μπορούν να χρησιμοποιούν αυτή τη σύνδεση',
  noToolsMatch: 'Κανένα εργαλείο δεν ταιριάζει με την αναζήτησή σας. Δοκιμάστε άλλο όνομα.',
  noToolsYet:
    'Δεν υπάρχουν ακόμη εργαλεία για πρόταση. Μπορείτε να ξεκινήσετε με μια ερώτηση ή ένα αρχείο.',
  reminderTitle: 'Καλύτερα με τα εργαλεία σας.',
  reminderIntro:
    'Μία σύνδεση αρκεί για αρχή. Δοκιμάστε ένα εργαλείο που ήδη χρησιμοποιείτε ή επιστρέψτε σε αυτό αργότερα.',
  reminderDoneTitle: 'Καλή αρχή.',
  reminderDoneIntro: 'Μπορείτε να προσθέσετε κι άλλα όποτε τα χρειαστείτε.',
  firstTitle: 'Από πού να ξεκινήσουμε;',
  firstIntro:
    'Επιλέξτε ένα παράδειγμα για να ξεκινήσετε μια συζήτηση ή πηγαίνετε κατευθείαν στον χώρο εργασίας σας.',
  connectedOne: '1 συνδεδεμένος λογαριασμός',
  connectedMany: '{count} συνδεδεμένοι λογαριασμοί',
  taskResearchTitle: 'Εξερεύνηση μιας επιχειρηματικής ευκαιρίας',
  taskResearchPrompt:
    'Βοήθησέ με να ερευνήσω μια ευκαιρία για την επιχείρησή μας χρησιμοποιώντας δημόσιες πηγές. Ρώτα με τι θέλω να εξερευνήσω και μετά βοήθησέ με να φτιάξω μια χρήσιμη σύνοψη με παραπομπές.',
  taskFileTitle: 'Δουλειά σε έγγραφο ή υπολογιστικό φύλλο',
  taskFilePrompt:
    'Βοήθησέ με να δουλέψω πάνω σε ένα έγγραφο ή υπολογιστικό φύλλο. Ζήτησέ μου να επισυνάψω ένα αρχείο και ρώτα τι θέλω να μάθω ή να κάνω, και μετά καθοδήγησέ με.',
  taskChooseTitle: 'Βοήθησέ με να επιλέξω μια πρώτη εργασία',
  taskChoosePrompt:
    'Βοήθησέ με να ξεκινήσω με το Poneva. Χρησιμοποίησε το πλαίσιο της εταιρείας μας και τις διαθέσιμες δυνατότητες για να προτείνεις μερικά χρήσιμα πράγματα που μπορούμε να κάνουμε μαζί. Κάνε μου μία ερώτηση για να με βοηθήσεις να επιλέξω και μετά καθοδήγησέ με στην εργασία.',
  toolGoogleSummary: 'Email, ημερολόγιο και έγγραφα',
  toolSlackSummary: 'Συζητήσεις και γνώση της ομάδας',
  toolShopifySummary: 'Προϊόντα, παραγγελίες και απόθεμα',
  toolWooSummary: 'Προϊόντα, παραγγελίες και αναφορές',
  toolMerchantSummary: 'Ροές προϊόντων και καταστάσεις',
  toolInvoicesSummary: 'Τιμολόγια και οικονομικά στοιχεία',
  toolGithubSummary: 'Έργα, ζητήματα και pull requests',
  taskMeetingTitle: 'Προετοιμασία για την επόμενη συνάντησή μου',
  taskMeetingPrompt:
    'Βοήθησέ με να προετοιμαστώ για την επόμενη συνάντησή μου. Έλεγξε το διαθέσιμο ημερολόγιο, τα email και τα έγγραφά μου, ρώτησε ποια συνάντηση αν χρειάζεται και φτιάξε ένα σύντομο ενημερωτικό σημείωμα με πηγές.',
  taskSlackTitle: 'Ενημέρωση για μια συζήτηση',
  taskSlackPrompt:
    'Βοήθησέ με να ενημερωθώ για μια συζήτηση στο Slack. Ρώτησε σε ποιο κανάλι ή θέμα να εστιάσουμε και μετά σύνοψε τις αποφάσεις, τα ανοιχτά ερωτήματα και τα επόμενα βήματα με συνδέσμους προς τα μηνύματα.',
  taskShopifyTitle: 'Μια νέα ματιά στα προϊόντα μου',
  taskShopifyPrompt:
    'Εξέτασε ένα μικρό δείγμα των προϊόντων μου στο Shopify και εντόπισε χρήσιμες βελτιώσεις στις πληροφορίες προϊόντων. Δείξε τα στοιχεία και μην αλλάξεις τίποτα.',
  taskWooTitle: 'Έλεγχος των πληροφοριών προϊόντων μου',
  taskWooPrompt:
    'Εξέτασε ένα μικρό δείγμα των προϊόντων μου στο WooCommerce και εντόπισε κενά στις πληροφορίες προϊόντων. Δείξε τις πηγές και μην αλλάξεις τίποτα.',
  taskFeedTitle: 'Έλεγχος της ροής προϊόντων μου',
  taskFeedPrompt:
    'Βοήθησέ με να ελέγξω τη ροή προϊόντων μου στο Google Merchant Center. Ξεκίνα με τον προσβάσιμο λογαριασμό και ένα μικρό δείγμα και ανέφερε πηγές για όσα βρίσκεις.',
  taskFortnoxTitle: 'Κατανόηση των πρόσφατων τιμολογίων',
  taskFortnoxPrompt:
    'Βοήθησέ με να κατανοήσω τα πρόσφατα τιμολόγια στο Fortnox. Ρώτησε ποια περίοδο να εξετάσουμε και σύνοψε τις διαθέσιμες πληροφορίες με πηγές. Μην αλλάξεις τίποτα.',
  taskTripletexTitle: 'Επισκόπηση των πρόσφατων τιμολογίων',
  taskTripletexPrompt:
    'Βοήθησέ με να κατανοήσω τα πρόσφατα τιμολόγια στο Tripletex. Ρώτησε ποια περίοδο να εξετάσουμε και σύνοψε τις διαθέσιμες πληροφορίες με πηγές. Μην αλλάξεις τίποτα.',
  taskGithubTitle: 'Ενημέρωση για ένα έργο',
  taskGithubPrompt:
    'Βοήθησέ με να ενημερωθώ για ένα έργο στο GitHub. Ρώτησε ποιο αποθετήριο να εξετάσουμε και σύνοψε τα πρόσφατα ζητήματα και pull requests με συνδέσμους.',
  taskTryTitle: 'Δοκιμή του {name}',
  taskTryPrompt:
    'Βοήθησέ με να ξεκινήσω με τη σύνδεσή μου στο {name}. Ανακάλυψε τα διαθέσιμα εργαλεία της, πρότεινε μια χρήσιμη πρώτη εργασία και ρώτησε τι θέλω να κάνω. Πρότεινέ μου τυχόν αλλαγές για έλεγχο πριν τις εκτελέσεις.',
  back: 'Πίσω',
  continue: 'Συνέχεια',
  continueWithout: 'Συνέχεια χωρίς σύνδεση',
  goToWorkspace: 'Μετάβαση στον χώρο εργασίας',
  opening: 'Άνοιγμα του χώρου εργασίας σας…',
  startFailed: 'Δεν μπορέσαμε να ανοίξουμε τον χώρο εργασίας σας. Δοκιμάστε ξανά.',
  loadingTitle: 'Άνοιγμα της εταιρείας σας…',
  loadingStatus: 'Φόρτωση του χώρου εργασίας σας',
  errorTitle: 'Ας το δοκιμάσουμε ξανά.',
  tryAgain: 'Δοκιμάστε ξανά',
  noCompanyTitle: 'Χρειάζεται πρόσκληση.',
  noCompanyBody:
    'Ο λογαριασμός σας δεν είναι συνδεδεμένος με εταιρεία. Ζητήστε από έναν κάτοχο να προσκαλέσει τη διεύθυνση email σας. Η ιδιωτική σας εργασία διατηρείται.',
  checkAccess: 'Έλεγχος πρόσβασης',
};

const hu: WelcomeCopy = {
  logOut: 'Kijelentkezés',
  progressLabel: 'Első lépések',
  stepOf: '{current}/{total}. lépés',
  stepFocus: 'Az Ön fókusza',
  stepTools: 'Az Ön eszközei',
  stepFirst: 'Az első lépés',
  focusTitle: 'Mondja el, hogyan dolgozik.',
  focusTitleNamed: 'Üdvözöljük, {name}! Hogyan dolgozik?',
  focusIntro:
    'Válassza ki a szerepkörét, és hogy miben kér segítséget. Mindkettő opcionális, és segít hasznos eszközöket javasolni.',
  roleHeading: 'Az Ön szerepköre',
  goalsHeading: 'Miben segíthetünk?',
  roleLeadership: 'Vezetés',
  roleProduct: 'Termék',
  roleEngineering: 'Fejlesztés és adatok',
  roleMarketing: 'Marketing',
  roleSales: 'Értékesítés',
  roleOperations: 'Működés',
  roleFinance: 'Pénzügy',
  goalEverydayTitle: 'Egy lépéssel előrébb',
  goalEverydayBody: 'Készüljön fel a megbeszélésekre, és zárkózzon fel a beszélgetésekhez.',
  goalCommerceTitle: 'Értsem az üzletemet',
  goalCommerceBody: 'Termékek, rendelések és lehetőségek feltárása.',
  goalFinanceTitle: 'Tisztán lássam a számokat',
  goalFinanceBody: 'Igazodjon el a számlákban és a pénzügyi adatokban.',
  goalProjectsTitle: 'Haladjon a munka',
  goalProjectsBody: 'Kösse össze a megbeszéléseket, feladatokat és projektfrissítéseket.',
  toolsTitle: 'Kapcsolja össze mindennapi eszközeit.',
  toolsIntro: 'Egy kis kontextus nagy különbséget jelent. Kezdjen egy-kettővel.',
  toolsUnavailable:
    'A kapcsolatok jelenleg nem érhetők el. Kezdhet egy kérdéssel vagy egy fájllal.',
  toolsLabel: 'Kapcsolja össze eszközeit',
  suggestedTools: 'Javaslatok Önnek',
  allTools: 'Minden eszköz',
  browseAllTools: 'Összes eszköz böngészése',
  backToSuggestions: 'Vissza a javaslatokhoz',
  searchTools: 'Eszközök keresése',
  findTool: 'Eszköz keresése…',
  popupBlocked:
    'A böngésző letiltotta a felugró ablakot. Ez az oldal tovább ellenőriz, amíg Ön engedélyezi a hozzáférést.',
  openAuthorization: 'Engedélyezési oldal megnyitása',
  authorizing: 'Fejezze be az engedélyezést a szolgáltató ablakában.',
  stopWaiting: 'Várakozás leállítása',
  loadingConnections: 'Kapcsolatok betöltése…',
  connectionsFailed: 'Nem sikerült betölteni a kapcsolatait.',
  useMyAccount: 'Saját fiókom használata',
  useCompanyAccount: 'Fiók használata ehhez: {company}',
  chooseAudience: 'Válassza ki, ki használhatja ezt a kapcsolatot',
  noToolsMatch: 'Egy eszköz sem felel meg a keresésnek. Próbáljon másik nevet.',
  noToolsYet: 'Még nincs javasolható eszköz. Kezdhet egy kérdéssel vagy egy fájllal.',
  reminderTitle: 'Jobb az eszközeivel.',
  reminderIntro:
    'Kezdésnek egy kapcsolat is elég. Próbáljon ki egy már használt eszközt, vagy térjen vissza erre később.',
  reminderDoneTitle: 'Jó kezdet.',
  reminderDoneIntro: 'Bármikor hozzáadhat továbbiakat, amikor szüksége van rájuk.',
  firstTitle: 'Hol kezdjük?',
  firstIntro:
    'Válasszon egy példát egy beszélgetés indításához, vagy lépjen egyenesen a munkaterületére.',
  connectedOne: '1 fiók csatlakoztatva',
  connectedMany: '{count} fiók csatlakoztatva',
  taskResearchTitle: 'Üzleti lehetőség feltárása',
  taskResearchPrompt:
    'Segíts nyilvános források alapján megvizsgálni egy lehetőséget a vállalkozásunk számára. Kérdezd meg, mit szeretnék feltárni, majd segíts egy hasznos, forrásokkal ellátott összefoglalót készíteni.',
  taskFileTitle: 'Dokumentum vagy táblázat feldolgozása',
  taskFilePrompt:
    'Segíts feldolgozni egy dokumentumot vagy táblázatot. Kérd meg, hogy csatoljak egy fájlt, és kérdezd meg, mit szeretnék megtudni vagy elvégezni, majd vezess végig rajta.',
  taskChooseTitle: 'Segíts kiválasztani az első feladatot',
  taskChoosePrompt:
    'Segíts elkezdeni a Poneva használatát. Használd a cégünk kontextusát és az elérhető képességeket, hogy javasolj néhány hasznos dolgot, amit együtt elvégezhetünk. Tegyél fel egy kérdést, ami segít választani, majd vezess végig a feladaton.',
  toolGoogleSummary: 'E-mail, naptár és dokumentumok',
  toolSlackSummary: 'Beszélgetések és csapattudás',
  toolShopifySummary: 'Termékek, rendelések és készlet',
  toolWooSummary: 'Termékek, rendelések és jelentések',
  toolMerchantSummary: 'Termékfeedek és állapotok',
  toolInvoicesSummary: 'Számlák és pénzügyi információk',
  toolGithubSummary: 'Projektek, feladatok és pull requestek',
  taskMeetingTitle: 'Felkészülés a következő megbeszélésemre',
  taskMeetingPrompt:
    'Segíts felkészülni a következő megbeszélésemre. Nézd át az elérhető naptáramat, e-mailjeimet és dokumentumaimat, szükség esetén kérdezd meg, melyik megbeszélésről van szó, és készíts tömör összefoglalót forrásokkal.',
  taskSlackTitle: 'Képbe kerülés egy beszélgetésben',
  taskSlackPrompt:
    'Segíts képbe kerülni egy Slack-beszélgetésben. Kérdezd meg, melyik csatornára vagy témára koncentráljunk, majd foglald össze a döntéseket, a nyitott kérdéseket és a következő lépéseket az üzenetekre mutató linkekkel.',
  taskShopifyTitle: 'Friss szemmel a termékeimről',
  taskShopifyPrompt:
    'Nézz át egy kis mintát a Shopify-termékeimből, és keress hasznos javításokat a termékinformációkban. Mutasd meg a bizonyítékokat, és ne változtass semmin.',
  taskWooTitle: 'Termékinformációim áttekintése',
  taskWooPrompt:
    'Nézz át egy kis mintát a WooCommerce-termékeimből, és keresd meg a hiányosságokat a termékinformációkban. Mutasd meg a forrásokat, és ne változtass semmin.',
  taskFeedTitle: 'Termékfeedem áttekintése',
  taskFeedPrompt:
    'Segíts áttekinteni a Google Merchant Center-termékfeedemet. Kezdd az elérhető fiókkal és egy kis mintával, és add meg a forrásokat ahhoz, amit találsz.',
  taskFortnoxTitle: 'A legutóbbi számlák megértése',
  taskFortnoxPrompt:
    'Segíts megérteni a legutóbbi számlákat a Fortnoxban. Kérdezd meg, melyik időszakot nézzük át, és foglald össze az elérhető információkat forrásokkal. Ne változtass semmin.',
  taskTripletexTitle: 'Áttekintés a legutóbbi számlákról',
  taskTripletexPrompt:
    'Segíts megérteni a legutóbbi számlákat a Tripletexben. Kérdezd meg, melyik időszakot nézzük át, és foglald össze az elérhető információkat forrásokkal. Ne változtass semmin.',
  taskGithubTitle: 'Képbe kerülés egy projektben',
  taskGithubPrompt:
    'Segíts képbe kerülni egy GitHub-projektben. Kérdezd meg, melyik repositoryt nézzük át, és foglald össze a legutóbbi feladatokat és pull requesteket linkekkel.',
  taskTryTitle: '{name} kipróbálása',
  taskTryPrompt:
    'Segíts elindulni ezzel a kapcsolattal: {name}. Derítsd fel az elérhető eszközeit, javasolj egy hasznos első feladatot, és kérdezd meg, mit szeretnék csinálni. Minden módosítást mutass meg nekem jóváhagyásra, mielőtt végrehajtod.',
  back: 'Vissza',
  continue: 'Tovább',
  continueWithout: 'Tovább kapcsolódás nélkül',
  goToWorkspace: 'Ugrás a munkaterületre',
  opening: 'Munkaterület megnyitása…',
  startFailed: 'Nem sikerült megnyitni a munkaterületét. Próbálja újra.',
  loadingTitle: 'A cég megnyitása…',
  loadingStatus: 'Munkaterület betöltése',
  errorTitle: 'Próbáljuk újra.',
  tryAgain: 'Próbálja újra',
  noCompanyTitle: 'Meghívásra van szükség.',
  noCompanyBody:
    'Fiókja nincs céghez kapcsolva. Kérje meg egy tulajdonost, hogy hívja meg az e-mail-címét. A privát munkája megmarad.',
  checkAccess: 'Hozzáférés ellenőrzése',
};

const ga: WelcomeCopy = {
  logOut: 'Logáil amach',
  progressLabel: 'Ag tosú',
  stepOf: 'Céim {current} as {total}',
  stepFocus: 'Do fhócas',
  stepTools: 'Do uirlisí',
  stepFirst: 'Do chéad chéim',
  focusTitle: 'Inis dúinn conas a oibríonn tú.',
  focusTitleNamed: 'Fáilte, {name}. Conas a oibríonn tú?',
  focusIntro:
    'Roghnaigh do ról agus cad leis a dteastaíonn cabhair uait. Tá an dá rud roghnach agus cabhraíonn siad linn uirlisí úsáideacha a mholadh.',
  roleHeading: 'Do ról',
  goalsHeading: 'Cad leis ar mhaith leat cabhair?',
  roleLeadership: 'Ceannaireacht',
  roleProduct: 'Táirge',
  roleEngineering: 'Innealtóireacht agus sonraí',
  roleMarketing: 'Margaíocht',
  roleSales: 'Díolacháin',
  roleOperations: 'Oibríochtaí',
  roleFinance: 'Airgeadas',
  goalEverydayTitle: 'Fan céim chun tosaigh',
  goalEverydayBody: 'Ullmhaigh do chruinnithe agus tar suas le comhráite.',
  goalCommerceTitle: 'Tuiscint ar mo ghnó',
  goalCommerceBody: 'Déan iniúchadh ar tháirgí, orduithe agus deiseanna.',
  goalFinanceTitle: 'Na figiúirí a fheiceáil go soiléir',
  goalFinanceBody: 'Bain ciall as sonraisc agus faisnéis airgeadais.',
  goalProjectsTitle: 'Coinnigh an obair ag gluaiseacht',
  goalProjectsBody: 'Nasc plé, saincheisteanna agus nuashonruithe tionscadail.',
  toolsTitle: 'Ceangail d’uirlisí laethúla.',
  toolsIntro: 'Déanann beagán comhthéacs difríocht mhór. Tosaigh le ceann nó dhó.',
  toolsUnavailable: 'Níl naisc ar fáil faoi láthair. Is féidir leat tosú le ceist nó comhad.',
  toolsLabel: 'Ceangail d’uirlisí',
  suggestedTools: 'Molta duit',
  allTools: 'Na huirlisí go léir',
  browseAllTools: 'Brabhsáil na huirlisí go léir',
  backToSuggestions: 'Ar ais chuig na moltaí',
  searchTools: 'Cuardaigh uirlisí',
  findTool: 'Aimsigh uirlis…',
  popupBlocked:
    'Chuir an brabhsálaí bac ar an bhfuinneog aníos. Leanann an leathanach seo ag seiceáil fad a thugann tú údarú.',
  openAuthorization: 'Oscail an leathanach údaraithe',
  authorizing: 'Críochnaigh an t-údarú i bhfuinneog an tsoláthraí.',
  stopWaiting: 'Stop ag fanacht',
  loadingConnections: 'Do naisc á lódáil…',
  connectionsFailed: 'Níorbh fhéidir do naisc a lódáil.',
  useMyAccount: 'Úsáid mo chuntas',
  useCompanyAccount: 'Úsáid cuntas do {company}',
  chooseAudience: 'Roghnaigh cé a fhéadfaidh an nasc seo a úsáid',
  noToolsMatch: 'Níl aon uirlis a mheaitseálann do chuardach. Bain triail as ainm eile.',
  noToolsYet: 'Níl aon uirlisí le moladh fós. Is féidir leat tosú le ceist nó comhad.',
  reminderTitle: 'Níos fearr le d’uirlisí.',
  reminderIntro:
    'Is leor nasc amháin chun tosú. Bain triail as uirlis a úsáideann tú cheana, nó fill ar seo níos déanaí.',
  reminderDoneTitle: 'Tús maith.',
  reminderDoneIntro: 'Is féidir leat tuilleadh a chur leis aon uair a theastaíonn siad uait.',
  firstTitle: 'Cá dtosóimid?',
  firstIntro: 'Roghnaigh sampla chun comhrá a thosú, nó téigh díreach chuig do spás oibre.',
  connectedOne: '1 chuntas ceangailte',
  connectedMany: 'Cuntais cheangailte: {count}',
  taskResearchTitle: 'Déan iniúchadh ar dheis ghnó',
  taskResearchPrompt:
    'Cabhraigh liom deis dár ngnó a thaighde ag úsáid foinsí poiblí. Fiafraigh díom cad ba mhaith liom a iniúchadh, ansin cabhraigh liom achoimre úsáideach le foinsí a ullmhú.',
  taskFileTitle: 'Oibrigh trí dhoiciméad nó scarbhileog',
  taskFilePrompt:
    'Cabhraigh liom oibriú trí dhoiciméad nó scarbhileog. Iarr orm comhad a cheangal agus fiafraigh cad ba mhaith liom a fhoghlaim nó a dhéanamh, ansin treoraigh mé tríd.',
  taskChooseTitle: 'Cabhraigh liom an chéad tasc a roghnú',
  taskChoosePrompt:
    'Cabhraigh liom tosú le Poneva. Úsáid comhthéacs ár gcomhlachta agus na cumais atá ar fáil chun cúpla rud úsáideach a mholadh ar féidir linn a dhéanamh le chéile. Cuir ceist amháin orm chun cabhrú liom roghnú, ansin treoraigh mé tríd an tasc.',
  toolGoogleSummary: 'Ríomhphost, féilire agus doiciméid',
  toolSlackSummary: 'Comhráite agus eolas na foirne',
  toolShopifySummary: 'Táirgí, orduithe agus stoc',
  toolWooSummary: 'Táirgí, orduithe agus tuarascálacha',
  toolMerchantSummary: 'Fothaí táirgí agus stádais',
  toolInvoicesSummary: 'Sonraisc agus faisnéis airgeadais',
  toolGithubSummary: 'Tionscadail, saincheisteanna agus iarratais tarraingthe',
  taskMeetingTitle: 'Ullmhaigh do mo chéad chruinniú eile',
  taskMeetingPrompt:
    'Cabhraigh liom ullmhú do mo chéad chruinniú eile. Seiceáil an féilire, an ríomhphost agus na doiciméid atá ar fáil dom, fiafraigh cén cruinniú más gá, agus déan achoimre ghonta le foinsí.',
  taskSlackTitle: 'Tar suas chun dáta ar chomhrá',
  taskSlackPrompt:
    'Cabhraigh liom teacht suas chun dáta ar chomhrá Slack. Fiafraigh cén cainéal nó ábhar ar cheart díriú air, ansin déan achoimre ar chinntí, ar cheisteanna oscailte agus ar na chéad chéimeanna eile le naisc chuig na teachtaireachtaí.',
  taskShopifyTitle: 'Féach as an nua ar mo tháirgí',
  taskShopifyPrompt:
    'Athbhreithnigh sampla beag de mo tháirgí Shopify agus aimsigh feabhsuithe úsáideacha ar fhaisnéis na dtáirgí. Taispeáin an fhianaise agus ná hathraigh aon rud.',
  taskWooTitle: 'Athbhreithnigh faisnéis mo tháirgí',
  taskWooPrompt:
    'Athbhreithnigh sampla beag de mo tháirgí WooCommerce agus aimsigh bearnaí i bhfaisnéis na dtáirgí. Taispeáin foinsí agus ná hathraigh aon rud.',
  taskFeedTitle: 'Athbhreithnigh m’fhotha táirgí',
  taskFeedPrompt:
    'Cabhraigh liom m’fhotha táirgí Google Merchant Center a athbhreithniú. Tosaigh leis an gcuntas inrochtana agus sampla beag, agus luaigh foinsí don mhéid a aimsíonn tú.',
  taskFortnoxTitle: 'Tuig na sonraisc is déanaí',
  taskFortnoxPrompt:
    'Cabhraigh liom na sonraisc is déanaí in Fortnox a thuiscint. Fiafraigh cén tréimhse le hathbhreithniú agus déan achoimre ar an bhfaisnéis atá ar fáil le foinsí. Ná hathraigh aon rud.',
  taskTripletexTitle: 'Faigh forléargas ar na sonraisc is déanaí',
  taskTripletexPrompt:
    'Cabhraigh liom na sonraisc is déanaí in Tripletex a thuiscint. Fiafraigh cén tréimhse le hathbhreithniú agus déan achoimre ar an bhfaisnéis atá ar fáil le foinsí. Ná hathraigh aon rud.',
  taskGithubTitle: 'Tar suas chun dáta ar thionscadal',
  taskGithubPrompt:
    'Cabhraigh liom teacht suas chun dáta ar thionscadal GitHub. Fiafraigh cén stór le hathbhreithniú agus déan achoimre ar shaincheisteanna agus ar iarratais tarraingthe le déanaí le naisc.',
  taskTryTitle: 'Bain triail as {name}',
  taskTryPrompt:
    'Cabhraigh liom tosú le mo nasc {name}. Faigh amach na huirlisí atá ar fáil ann, mol céad tasc úsáideach, agus fiafraigh cad ba mhaith liom a dhéanamh. Mol aon athruithe dom le hathbhreithniú sula gcuirfidh tú i gcrích iad.',
  back: 'Siar',
  continue: 'Lean ar aghaidh',
  continueWithout: 'Lean ar aghaidh gan nascadh',
  goToWorkspace: 'Téigh chuig an spás oibre',
  opening: 'Do spás oibre á oscailt…',
  startFailed: 'Níorbh fhéidir linn do spás oibre a oscailt. Bain triail eile as.',
  loadingTitle: 'Do chomhlacht á oscailt…',
  loadingStatus: 'Do spás oibre á lódáil',
  errorTitle: 'Bainimis triail eile as.',
  tryAgain: 'Bain triail eile as',
  noCompanyTitle: 'Tá cuireadh ag teastáil.',
  noCompanyBody:
    'Níl do chuntas ceangailte le comhlacht. Iarr ar úinéir cuireadh a thabhairt do do sheoladh ríomhphoist. Coinnítear d’obair phríobháideach.',
  checkAccess: 'Seiceáil stádas rochtana',
};

const it: WelcomeCopy = {
  logOut: 'Esci',
  progressLabel: 'Primi passi',
  stepOf: 'Passaggio {current} di {total}',
  stepFocus: 'Le tue priorità',
  stepTools: 'I tuoi strumenti',
  stepFirst: 'Il tuo primo passo',
  focusTitle: 'Raccontaci come lavori.',
  focusTitleNamed: 'Benvenuto, {name}. Come lavori?',
  focusIntro:
    'Scegli il tuo ruolo e in cosa vuoi aiuto. Entrambi sono facoltativi e ci aiutano a suggerire strumenti utili.',
  roleHeading: 'Il tuo ruolo',
  goalsHeading: 'In cosa vorresti aiuto?',
  roleLeadership: 'Direzione',
  roleProduct: 'Prodotto',
  roleEngineering: 'Ingegneria e dati',
  roleMarketing: 'Marketing',
  roleSales: 'Vendite',
  roleOperations: 'Operazioni',
  roleFinance: 'Finanza',
  goalEverydayTitle: 'Essere un passo avanti',
  goalEverydayBody: 'Prepara le riunioni e aggiornati sulle conversazioni.',
  goalCommerceTitle: 'Capire la mia attività',
  goalCommerceBody: 'Esplora prodotti, ordini e opportunità.',
  goalFinanceTitle: 'Vedere chiaro nei numeri',
  goalFinanceBody: 'Dai un senso a fatture e dati finanziari.',
  goalProjectsTitle: 'Far procedere il lavoro',
  goalProjectsBody: 'Collega discussioni, ticket e aggiornamenti di progetto.',
  toolsTitle: 'Collega i tuoi strumenti di ogni giorno.',
  toolsIntro: 'Un po’ di contesto fa una grande differenza. Inizia con uno o due.',
  toolsUnavailable:
    'Le connessioni non sono disponibili al momento. Puoi iniziare con una domanda o un file.',
  toolsLabel: 'Collega i tuoi strumenti',
  suggestedTools: 'Suggeriti per te',
  allTools: 'Tutti gli strumenti',
  browseAllTools: 'Sfoglia tutti gli strumenti',
  backToSuggestions: 'Torna ai suggerimenti',
  searchTools: 'Cerca strumenti',
  findTool: 'Trova uno strumento…',
  popupBlocked:
    'Il browser ha bloccato il pop-up. Questa pagina continua a controllare mentre concedi l’autorizzazione.',
  openAuthorization: 'Apri la pagina di autorizzazione',
  authorizing: 'Completa l’autorizzazione nella finestra del fornitore.',
  stopWaiting: 'Smetti di attendere',
  loadingConnections: 'Caricamento delle connessioni…',
  connectionsFailed: 'Non è stato possibile caricare le connessioni.',
  useMyAccount: 'Usa il mio account',
  useCompanyAccount: 'Usa un account per {company}',
  chooseAudience: 'Scegli chi può usare questa connessione',
  noToolsMatch: 'Nessuno strumento corrisponde alla ricerca. Prova un altro nome.',
  noToolsYet: 'Non ci sono ancora strumenti da suggerire. Puoi iniziare con una domanda o un file.',
  reminderTitle: 'Meglio con i tuoi strumenti.',
  reminderIntro:
    'Basta una connessione per iniziare. Prova uno strumento che usi già, oppure torna qui più tardi.',
  reminderDoneTitle: 'Un buon inizio.',
  reminderDoneIntro: 'Puoi aggiungerne altri quando ti servono.',
  firstTitle: 'Da dove iniziamo?',
  firstIntro:
    'Scegli un esempio per avviare una conversazione, oppure vai direttamente al tuo spazio di lavoro.',
  connectedOne: '1 account collegato',
  connectedMany: '{count} account collegati',
  taskResearchTitle: 'Esplorare un’opportunità di business',
  taskResearchPrompt:
    'Aiutami a studiare un’opportunità per la nostra attività usando fonti pubbliche. Chiedimi cosa voglio esplorare, poi aiutami a preparare una sintesi utile e con le fonti.',
  taskFileTitle: 'Analizzare un documento o un foglio di calcolo',
  taskFilePrompt:
    'Aiutami ad analizzare un documento o un foglio di calcolo. Chiedimi di allegare un file e cosa voglio sapere o ottenere, poi guidami passo passo.',
  taskChooseTitle: 'Aiutami a scegliere un primo compito',
  taskChoosePrompt:
    'Aiutami a iniziare con Poneva. Usa il contesto della nostra azienda e le funzionalità disponibili per suggerire alcune cose utili da fare insieme. Fammi una domanda per aiutarmi a scegliere, poi guidami nel compito.',
  toolGoogleSummary: 'Email, calendario e documenti',
  toolSlackSummary: 'Conversazioni e conoscenze del team',
  toolShopifySummary: 'Prodotti, ordini e inventario',
  toolWooSummary: 'Prodotti, ordini e report',
  toolMerchantSummary: 'Feed dei prodotti e stati',
  toolInvoicesSummary: 'Fatture e informazioni finanziarie',
  toolGithubSummary: 'Progetti, issue e pull request',
  taskMeetingTitle: 'Preparare la mia prossima riunione',
  taskMeetingPrompt:
    'Aiutami a preparare la mia prossima riunione. Controlla il calendario, le email e i documenti disponibili, chiedimi di quale riunione si tratta se serve e prepara un breve riepilogo con le fonti.',
  taskSlackTitle: 'Mettermi al passo con una conversazione',
  taskSlackPrompt:
    'Aiutami a mettermi al passo con una conversazione su Slack. Chiedimi su quale canale o argomento concentrarci, poi riassumi decisioni, domande aperte e prossimi passi con i link ai messaggi.',
  taskShopifyTitle: 'Guardare i miei prodotti con occhi nuovi',
  taskShopifyPrompt:
    'Esamina un piccolo campione dei miei prodotti Shopify e individua miglioramenti utili alle informazioni di prodotto. Mostra le prove e non modificare nulla.',
  taskWooTitle: 'Rivedere le informazioni dei miei prodotti',
  taskWooPrompt:
    'Esamina un piccolo campione dei miei prodotti WooCommerce e individua le lacune nelle informazioni di prodotto. Indica le fonti e non modificare nulla.',
  taskFeedTitle: 'Rivedere il mio feed prodotti',
  taskFeedPrompt:
    'Aiutami a rivedere il mio feed prodotti di Google Merchant Center. Parti dall’account accessibile e da un piccolo campione, e cita le fonti di ciò che trovi.',
  taskFortnoxTitle: 'Capire le fatture recenti',
  taskFortnoxPrompt:
    'Aiutami a capire le fatture recenti in Fortnox. Chiedimi quale periodo esaminare e riassumi le informazioni disponibili con le fonti. Non modificare nulla.',
  taskTripletexTitle: 'Avere una panoramica delle fatture recenti',
  taskTripletexPrompt:
    'Aiutami a capire le fatture recenti in Tripletex. Chiedimi quale periodo esaminare e riassumi le informazioni disponibili con le fonti. Non modificare nulla.',
  taskGithubTitle: 'Mettermi al passo con un progetto',
  taskGithubPrompt:
    'Aiutami a mettermi al passo con un progetto GitHub. Chiedimi quale repository esaminare e riassumi issue e pull request recenti con i link.',
  taskTryTitle: 'Provare {name}',
  taskTryPrompt:
    'Aiutami a iniziare con la mia connessione {name}. Scopri gli strumenti disponibili, suggerisci un primo compito utile e chiedimi cosa voglio fare. Proponimi qualsiasi modifica da rivedere prima di eseguirla.',
  back: 'Indietro',
  continue: 'Continua',
  continueWithout: 'Continua senza collegare',
  goToWorkspace: 'Vai allo spazio di lavoro',
  opening: 'Apertura dello spazio di lavoro…',
  startFailed: 'Non è stato possibile aprire il tuo spazio di lavoro. Riprova.',
  loadingTitle: 'Apertura della tua azienda…',
  loadingStatus: 'Caricamento dello spazio di lavoro',
  errorTitle: 'Riproviamo.',
  tryAgain: 'Riprova',
  noCompanyTitle: 'Serve un invito.',
  noCompanyBody:
    'Il tuo account non è collegato a nessuna azienda. Chiedi a un proprietario di invitare il tuo indirizzo email. Il tuo lavoro privato viene conservato.',
  checkAccess: 'Verifica l’accesso',
};

const lv: WelcomeCopy = {
  logOut: 'Izrakstīties',
  progressLabel: 'Darba sākšana',
  stepOf: '{current}. solis no {total}',
  stepFocus: 'Jūsu fokuss',
  stepTools: 'Jūsu rīki',
  stepFirst: 'Jūsu pirmais solis',
  focusTitle: 'Pastāstiet, kā jūs strādājat.',
  focusTitleNamed: 'Laipni lūdzam, {name}! Kā jūs strādājat?',
  focusIntro:
    'Izvēlieties savu lomu un to, kur vēlaties palīdzību. Abas izvēles nav obligātas un palīdz mums ieteikt noderīgus rīkus.',
  roleHeading: 'Jūsu loma',
  goalsHeading: 'Kur jums noderētu palīdzība?',
  roleLeadership: 'Vadība',
  roleProduct: 'Produkts',
  roleEngineering: 'Inženierija un dati',
  roleMarketing: 'Mārketings',
  roleSales: 'Pārdošana',
  roleOperations: 'Darbības',
  roleFinance: 'Finanses',
  goalEverydayTitle: 'Būt soli priekšā',
  goalEverydayBody: 'Sagatavojieties sanāksmēm un iepazīstieties ar sarunām.',
  goalCommerceTitle: 'Izprast savu biznesu',
  goalCommerceBody: 'Izpētiet produktus, pasūtījumus un iespējas.',
  goalFinanceTitle: 'Skaidri redzēt skaitļus',
  goalFinanceBody: 'Izprotiet rēķinus un finanšu informāciju.',
  goalProjectsTitle: 'Uzturēt darbu kustībā',
  goalProjectsBody: 'Sasaistiet diskusijas, pieteikumus un projektu jaunumus.',
  toolsTitle: 'Pievienojiet ikdienas rīkus.',
  toolsIntro: 'Nedaudz konteksta rada lielu atšķirību. Sāciet ar vienu vai diviem.',
  toolsUnavailable: 'Savienojumi pašlaik nav pieejami. Varat sākt ar jautājumu vai failu.',
  toolsLabel: 'Pievienojiet savus rīkus',
  suggestedTools: 'Ieteikts jums',
  allTools: 'Visi rīki',
  browseAllTools: 'Pārlūkot visus rīkus',
  backToSuggestions: 'Atpakaļ uz ieteikumiem',
  searchTools: 'Meklēt rīkus',
  findTool: 'Atrodiet rīku…',
  popupBlocked:
    'Pārlūks bloķēja uznirstošo logu. Šī lapa turpina pārbaudīt, kamēr jūs piešķirat piekļuvi.',
  openAuthorization: 'Atvērt autorizācijas lapu',
  authorizing: 'Pabeidziet autorizāciju pakalpojuma sniedzēja logā.',
  stopWaiting: 'Beigt gaidīt',
  loadingConnections: 'Ielādē jūsu savienojumus…',
  connectionsFailed: 'Neizdevās ielādēt jūsu savienojumus.',
  useMyAccount: 'Izmantot manu kontu',
  useCompanyAccount: 'Izmantot kontu uzņēmumam {company}',
  chooseAudience: 'Izvēlieties, kas var izmantot šo savienojumu',
  noToolsMatch: 'Neviens rīks neatbilst meklēšanai. Mēģiniet citu nosaukumu.',
  noToolsYet: 'Vēl nav rīku, ko ieteikt. Varat sākt ar jautājumu vai failu.',
  reminderTitle: 'Labāk ar jūsu rīkiem.',
  reminderIntro:
    'Lai sāktu, pietiek ar vienu savienojumu. Izmēģiniet rīku, ko jau izmantojat, vai atgriezieties pie šī vēlāk.',
  reminderDoneTitle: 'Labs sākums.',
  reminderDoneIntro: 'Vairāk varēsiet pievienot, kad būs vajadzīgs.',
  firstTitle: 'Ar ko sāksim?',
  firstIntro: 'Izvēlieties piemēru, lai sāktu sarunu, vai dodieties tieši uz savu darbvietu.',
  connectedOne: 'Pievienots 1 konts',
  connectedMany: 'Pievienoti konti: {count}',
  taskResearchTitle: 'Izpētīt biznesa iespēju',
  taskResearchPrompt:
    'Palīdzi man izpētīt iespēju mūsu biznesam, izmantojot publiskus avotus. Pajautā, ko vēlos izpētīt, un pēc tam palīdzi sagatavot noderīgu kopsavilkumu ar atsaucēm.',
  taskFileTitle: 'Izskatīt dokumentu vai izklājlapu',
  taskFilePrompt:
    'Palīdzi man izskatīt dokumentu vai izklājlapu. Palūdz man pievienot failu un pajautā, ko vēlos uzzināt vai paveikt, un pēc tam vadi mani cauri.',
  taskChooseTitle: 'Palīdziet izvēlēties pirmo uzdevumu',
  taskChoosePrompt:
    'Palīdzi man sākt darbu ar Poneva. Izmanto mūsu uzņēmuma kontekstu un pieejamās iespējas, lai ieteiktu dažas noderīgas lietas, ko varam darīt kopā. Uzdod vienu jautājumu, kas palīdz man izvēlēties, un pēc tam vadi mani cauri uzdevumam.',
  toolGoogleSummary: 'E-pasts, kalendārs un dokumenti',
  toolSlackSummary: 'Sarunas un komandas zināšanas',
  toolShopifySummary: 'Produkti, pasūtījumi un krājumi',
  toolWooSummary: 'Produkti, pasūtījumi un atskaites',
  toolMerchantSummary: 'Produktu plūsmas un statusi',
  toolInvoicesSummary: 'Rēķini un finanšu informācija',
  toolGithubSummary: 'Projekti, problēmas un pull pieprasījumi',
  taskMeetingTitle: 'Sagatavoties nākamajai sanāksmei',
  taskMeetingPrompt:
    'Palīdzi man sagatavoties nākamajai sanāksmei. Pārbaudi manu pieejamo kalendāru, e-pastu un dokumentus, ja vajag, pajautā, par kuru sanāksmi ir runa, un sagatavo īsu kopsavilkumu ar avotiem.',
  taskSlackTitle: 'Iepazīties ar sarunas gaitu',
  taskSlackPrompt:
    'Palīdzi man iepazīties ar Slack sarunas gaitu. Pajautā, uz kuru kanālu vai tēmu koncentrēties, un tad apkopo lēmumus, atklātos jautājumus un nākamos soļus ar saitēm uz ziņojumiem.',
  taskShopifyTitle: 'Paskatīties uz saviem produktiem no jauna',
  taskShopifyPrompt:
    'Pārskati nelielu manu Shopify produktu izlasi un atrodi noderīgus produktu informācijas uzlabojumus. Parādi pierādījumus un neko nemaini.',
  taskWooTitle: 'Pārskatīt produktu informāciju',
  taskWooPrompt:
    'Pārskati nelielu manu WooCommerce produktu izlasi un atrodi trūkumus produktu informācijā. Norādi avotus un neko nemaini.',
  taskFeedTitle: 'Pārskatīt produktu plūsmu',
  taskFeedPrompt:
    'Palīdzi man pārskatīt manu Google Merchant Center produktu plūsmu. Sāc ar pieejamo kontu un nelielu izlasi un norādi avotus atradumiem.',
  taskFortnoxTitle: 'Saprast nesenos rēķinus',
  taskFortnoxPrompt:
    'Palīdzi man saprast nesenos rēķinus sistēmā Fortnox. Pajautā, kuru periodu pārskatīt, un apkopo pieejamo informāciju ar avotiem. Neko nemaini.',
  taskTripletexTitle: 'Gūt pārskatu par neseniem rēķiniem',
  taskTripletexPrompt:
    'Palīdzi man saprast nesenos rēķinus sistēmā Tripletex. Pajautā, kuru periodu pārskatīt, un apkopo pieejamo informāciju ar avotiem. Neko nemaini.',
  taskGithubTitle: 'Iepazīties ar projekta gaitu',
  taskGithubPrompt:
    'Palīdzi man iepazīties ar GitHub projekta gaitu. Pajautā, kuru repozitoriju pārskatīt, un apkopo nesenās problēmas un pull pieprasījumus ar saitēm.',
  taskTryTitle: 'Izmēģināt {name}',
  taskTryPrompt:
    'Palīdzi man sākt darbu ar savienojumu {name}. Noskaidro tā pieejamos rīkus, iesaki noderīgu pirmo uzdevumu un pajautā, ko es vēlos darīt. Pirms izmaiņu veikšanas iesniedz tās man pārskatīšanai.',
  back: 'Atpakaļ',
  continue: 'Turpināt',
  continueWithout: 'Turpināt bez savienošanas',
  goToWorkspace: 'Uz darbvietu',
  opening: 'Atver jūsu darbvietu…',
  startFailed: 'Neizdevās atvērt jūsu darbvietu. Mēģiniet vēlreiz.',
  loadingTitle: 'Atver jūsu uzņēmumu…',
  loadingStatus: 'Ielādē jūsu darbvietu',
  errorTitle: 'Mēģināsim vēlreiz.',
  tryAgain: 'Mēģināt vēlreiz',
  noCompanyTitle: 'Nepieciešams uzaicinājums.',
  noCompanyBody:
    'Jūsu konts nav saistīts ar uzņēmumu. Palūdziet īpašniekam uzaicināt jūsu e-pasta adresi. Jūsu privātais darbs tiek saglabāts.',
  checkAccess: 'Pārbaudīt piekļuvi',
};

const lt: WelcomeCopy = {
  logOut: 'Atsijungti',
  progressLabel: 'Pradžia',
  stepOf: '{current} žingsnis iš {total}',
  stepFocus: 'Jūsų dėmesys',
  stepTools: 'Jūsų įrankiai',
  stepFirst: 'Jūsų pirmasis žingsnis',
  focusTitle: 'Papasakokite, kaip dirbate.',
  focusTitleNamed: 'Sveiki, {name}. Kaip dirbate?',
  focusIntro:
    'Pasirinkite savo vaidmenį ir tai, kur norite pagalbos. Abu pasirinkimai neprivalomi ir padeda mums pasiūlyti naudingų įrankių.',
  roleHeading: 'Jūsų vaidmuo',
  goalsHeading: 'Kur jums praverstų pagalba?',
  roleLeadership: 'Vadovybė',
  roleProduct: 'Produktas',
  roleEngineering: 'Inžinerija ir duomenys',
  roleMarketing: 'Rinkodara',
  roleSales: 'Pardavimai',
  roleOperations: 'Operacijos',
  roleFinance: 'Finansai',
  goalEverydayTitle: 'Būti žingsniu priekyje',
  goalEverydayBody: 'Pasiruoškite susitikimams ir susipažinkite su pokalbiais.',
  goalCommerceTitle: 'Suprasti savo verslą',
  goalCommerceBody: 'Tyrinėkite produktus, užsakymus ir galimybes.',
  goalFinanceTitle: 'Aiškiai matyti skaičius',
  goalFinanceBody: 'Supraskite sąskaitas ir finansinę informaciją.',
  goalProjectsTitle: 'Kad darbas judėtų',
  goalProjectsBody: 'Susiekite diskusijas, užduotis ir projektų naujienas.',
  toolsTitle: 'Prijunkite kasdienius įrankius.',
  toolsIntro: 'Šiek tiek konteksto labai padeda. Pradėkite nuo vieno ar dviejų.',
  toolsUnavailable: 'Ryšiai šiuo metu nepasiekiami. Galite pradėti nuo klausimo ar failo.',
  toolsLabel: 'Prijunkite savo įrankius',
  suggestedTools: 'Siūloma jums',
  allTools: 'Visi įrankiai',
  browseAllTools: 'Naršyti visus įrankius',
  backToSuggestions: 'Grįžti prie pasiūlymų',
  searchTools: 'Ieškoti įrankių',
  findTool: 'Raskite įrankį…',
  popupBlocked:
    'Naršyklė užblokavo iššokantįjį langą. Šis puslapis toliau tikrina, kol suteikiate prieigą.',
  openAuthorization: 'Atidaryti autorizavimo puslapį',
  authorizing: 'Užbaikite autorizavimą paslaugų teikėjo lange.',
  stopWaiting: 'Nebelaukti',
  loadingConnections: 'Įkeliami jūsų ryšiai…',
  connectionsFailed: 'Nepavyko įkelti jūsų ryšių.',
  useMyAccount: 'Naudoti mano paskyrą',
  useCompanyAccount: 'Naudoti paskyrą įmonei {company}',
  chooseAudience: 'Pasirinkite, kas gali naudoti šį ryšį',
  noToolsMatch: 'Paieškos neatitinka joks įrankis. Pabandykite kitą pavadinimą.',
  noToolsYet: 'Kol kas nėra ką pasiūlyti. Galite pradėti nuo klausimo ar failo.',
  reminderTitle: 'Geriau su jūsų įrankiais.',
  reminderIntro:
    'Pradžiai užtenka vieno ryšio. Išbandykite įrankį, kurį jau naudojate, arba grįžkite prie to vėliau.',
  reminderDoneTitle: 'Gera pradžia.',
  reminderDoneIntro: 'Daugiau galėsite pridėti, kai prireiks.',
  firstTitle: 'Nuo ko pradėsime?',
  firstIntro: 'Pasirinkite pavyzdį pokalbiui pradėti arba eikite tiesiai į savo darbo erdvę.',
  connectedOne: 'Prijungta 1 paskyra',
  connectedMany: 'Prijungta paskyrų: {count}',
  taskResearchTitle: 'Ištirti verslo galimybę',
  taskResearchPrompt:
    'Padėk man ištirti galimybę mūsų verslui naudojant viešus šaltinius. Paklausk, ką noriu ištirti, ir tada padėk parengti naudingą santrauką su šaltiniais.',
  taskFileTitle: 'Peržiūrėti dokumentą ar skaičiuoklę',
  taskFilePrompt:
    'Padėk man peržiūrėti dokumentą ar skaičiuoklę. Paprašyk pridėti failą ir paklausk, ką noriu sužinoti ar atlikti, tada mane vesk.',
  taskChooseTitle: 'Padėkite pasirinkti pirmą užduotį',
  taskChoosePrompt:
    'Padėk man pradėti naudotis Poneva. Pasinaudok mūsų įmonės kontekstu ir turimomis galimybėmis ir pasiūlyk kelis naudingus dalykus, kuriuos galime nuveikti kartu. Užduok vieną klausimą, kuris padėtų man pasirinkti, ir tada vesk mane per užduotį.',
  toolGoogleSummary: 'El. paštas, kalendorius ir dokumentai',
  toolSlackSummary: 'Pokalbiai ir komandos žinios',
  toolShopifySummary: 'Produktai, užsakymai ir atsargos',
  toolWooSummary: 'Produktai, užsakymai ir ataskaitos',
  toolMerchantSummary: 'Produktų srautai ir būsenos',
  toolInvoicesSummary: 'Sąskaitos faktūros ir finansinė informacija',
  toolGithubSummary: 'Projektai, problemos ir pull užklausos',
  taskMeetingTitle: 'Pasiruošti kitam susitikimui',
  taskMeetingPrompt:
    'Padėk man pasiruošti kitam susitikimui. Peržiūrėk man prieinamą kalendorių, el. paštą ir dokumentus, jei reikia, paklausk, apie kurį susitikimą kalbama, ir parenk glaustą apžvalgą su šaltiniais.',
  taskSlackTitle: 'Susigaudyti pokalbyje',
  taskSlackPrompt:
    'Padėk man susigaudyti Slack pokalbyje. Paklausk, į kurį kanalą ar temą susitelkti, tada apibendrink sprendimus, atvirus klausimus ir tolesnius žingsnius su nuorodomis į žinutes.',
  taskShopifyTitle: 'Šviežiai pažvelgti į savo produktus',
  taskShopifyPrompt:
    'Peržiūrėk nedidelę mano Shopify produktų imtį ir rask naudingų produktų informacijos patobulinimų. Parodyk įrodymus ir nieko nekeisk.',
  taskWooTitle: 'Peržiūrėti produktų informaciją',
  taskWooPrompt:
    'Peržiūrėk nedidelę mano WooCommerce produktų imtį ir rask produktų informacijos spragų. Nurodyk šaltinius ir nieko nekeisk.',
  taskFeedTitle: 'Peržiūrėti produktų srautą',
  taskFeedPrompt:
    'Padėk man peržiūrėti mano Google Merchant Center produktų srautą. Pradėk nuo prieinamos paskyros ir nedidelės imties ir nurodyk šaltinius tam, ką randi.',
  taskFortnoxTitle: 'Suprasti naujausias sąskaitas',
  taskFortnoxPrompt:
    'Padėk man suprasti naujausias sąskaitas faktūras sistemoje Fortnox. Paklausk, kurį laikotarpį peržiūrėti, ir apibendrink turimą informaciją su šaltiniais. Nieko nekeisk.',
  taskTripletexTitle: 'Apžvelgti naujausias sąskaitas',
  taskTripletexPrompt:
    'Padėk man suprasti naujausias sąskaitas faktūras sistemoje Tripletex. Paklausk, kurį laikotarpį peržiūrėti, ir apibendrink turimą informaciją su šaltiniais. Nieko nekeisk.',
  taskGithubTitle: 'Susigaudyti projekte',
  taskGithubPrompt:
    'Padėk man susigaudyti GitHub projekte. Paklausk, kurią saugyklą peržiūrėti, ir apibendrink naujausias problemas ir pull užklausas su nuorodomis.',
  taskTryTitle: 'Išbandyti {name}',
  taskTryPrompt:
    'Padėk man pradėti naudotis ryšiu {name}. Išsiaiškink jo prieinamus įrankius, pasiūlyk naudingą pirmą užduotį ir paklausk, ką noriu daryti. Prieš atlikdamas bet kokius pakeitimus, pateik juos man peržiūrėti.',
  back: 'Atgal',
  continue: 'Tęsti',
  continueWithout: 'Tęsti neprijungus',
  goToWorkspace: 'Eiti į darbo erdvę',
  opening: 'Atidaroma jūsų darbo erdvė…',
  startFailed: 'Nepavyko atidaryti jūsų darbo erdvės. Bandykite dar kartą.',
  loadingTitle: 'Atidaroma jūsų įmonė…',
  loadingStatus: 'Įkeliama jūsų darbo erdvė',
  errorTitle: 'Pabandykime dar kartą.',
  tryAgain: 'Bandyti dar kartą',
  noCompanyTitle: 'Reikia kvietimo.',
  noCompanyBody:
    'Jūsų paskyra nesusieta su jokia įmone. Paprašykite savininko pakviesti jūsų el. pašto adresą. Jūsų privatus darbas išsaugomas.',
  checkAccess: 'Tikrinti prieigą',
};

const mt: WelcomeCopy = {
  logOut: 'Oħroġ',
  progressLabel: 'Biex tibda',
  stepOf: 'Pass {current} minn {total}',
  stepFocus: 'Il-fokus tiegħek',
  stepTools: 'L-għodod tiegħek',
  stepFirst: 'L-ewwel pass tiegħek',
  focusTitle: 'Għidilna kif taħdem.',
  focusTitleNamed: 'Merħba, {name}. Kif taħdem?',
  focusIntro:
    'Agħżel ir-rwol tiegħek u f’hiex trid l-għajnuna. It-tnejn huma fakultattivi u jgħinuna nissuġġerixxu għodod utli.',
  roleHeading: 'Ir-rwol tiegħek',
  goalsHeading: 'F’hiex tixtieq l-għajnuna?',
  roleLeadership: 'Tmexxija',
  roleProduct: 'Prodott',
  roleEngineering: 'Inġinerija u data',
  roleMarketing: 'Marketing',
  roleSales: 'Bejgħ',
  roleOperations: 'Operazzjonijiet',
  roleFinance: 'Finanzi',
  goalEverydayTitle: 'Ibqa’ pass quddiem',
  goalEverydayBody: 'Ipprepara għal-laqgħat u lħaq mal-konversazzjonijiet.',
  goalCommerceTitle: 'Nifhem in-negozju tiegħi',
  goalCommerceBody: 'Esplora prodotti, ordnijiet u opportunitajiet.',
  goalFinanceTitle: 'Nara n-numri b’mod ċar',
  goalFinanceBody: 'Agħmel sens mill-fatturi u l-informazzjoni finanzjarja.',
  goalProjectsTitle: 'Żomm ix-xogħol għaddej',
  goalProjectsBody: 'Għaqqad diskussjonijiet, kwistjonijiet u aġġornamenti tal-proġetti.',
  toolsTitle: 'Qabbad l-għodod ta’ kuljum tiegħek.',
  toolsIntro: 'Ftit kuntest jagħmel differenza kbira. Ibda b’waħda jew tnejn.',
  toolsUnavailable:
    'Il-konnessjonijiet mhumiex disponibbli bħalissa. Tista’ tibda b’mistoqsija jew b’fajl.',
  toolsLabel: 'Qabbad l-għodod tiegħek',
  suggestedTools: 'Issuġġeriti għalik',
  allTools: 'L-għodod kollha',
  browseAllTools: 'Ara l-għodod kollha',
  backToSuggestions: 'Lura għas-suġġerimenti',
  searchTools: 'Fittex l-għodod',
  findTool: 'Sib għodda…',
  popupBlocked:
    'Il-browser imblokka t-tieqa pop-up. Din il-paġna tibqa’ tiċċekkja waqt li tagħti l-awtorizzazzjoni.',
  openAuthorization: 'Iftaħ il-paġna tal-awtorizzazzjoni',
  authorizing: 'Lesti l-awtorizzazzjoni fit-tieqa tal-fornitur.',
  stopWaiting: 'Tibqax tistenna',
  loadingConnections: 'Qed jitgħabbew il-konnessjonijiet tiegħek…',
  connectionsFailed: 'Ma rnexxilniex intellgħu l-konnessjonijiet tiegħek.',
  useMyAccount: 'Uża l-kont tiegħi',
  useCompanyAccount: 'Uża kont għal {company}',
  chooseAudience: 'Agħżel min jista’ juża din il-konnessjoni',
  noToolsMatch: 'L-ebda għodda ma taqbel mat-tfittxija tiegħek. Ipprova isem ieħor.',
  noToolsYet: 'Għad m’hemmx għodod x’nissuġġerixxu. Tista’ tibda b’mistoqsija jew b’fajl.',
  reminderTitle: 'Aħjar bl-għodod tiegħek.',
  reminderIntro:
    'Konnessjoni waħda hija biżżejjed biex tibda. Ipprova għodda li diġà tuża, jew erġa’ lura għal dan aktar tard.',
  reminderDoneTitle: 'Bidu tajjeb.',
  reminderDoneIntro: 'Tista’ żżid aktar meta jkollok bżonnhom.',
  firstTitle: 'Minn fejn nibdew?',
  firstIntro:
    'Agħżel eżempju biex tibda konversazzjoni, jew mur dritt fl-ispazju tax-xogħol tiegħek.',
  connectedOne: 'Kont 1 imqabbad',
  connectedMany: 'Kontijiet imqabbda: {count}',
  taskResearchTitle: 'Esplora opportunità ta’ negozju',
  taskResearchPrompt:
    'Għinni nirriċerka opportunità għan-negozju tagħna billi nuża sorsi pubbliċi. Staqsini x’irrid nesplora, imbagħad għinni nagħmel sommarju utli b’referenzi.',
  taskFileTitle: 'Aħdem fuq dokument jew spreadsheet',
  taskFilePrompt:
    'Għinni naħdem fuq dokument jew spreadsheet. Itlobni nehmeż fajl u staqsini x’irrid nitgħallem jew nagħmel, imbagħad iggwidani fih.',
  taskChooseTitle: 'Għinni nagħżel l-ewwel kompitu',
  taskChoosePrompt:
    'Għinni nibda b’Poneva. Uża l-kuntest tal-kumpanija tagħna u l-kapaċitajiet disponibbli biex tissuġġerixxi ftit affarijiet utli li nistgħu nagħmlu flimkien. Agħmilli mistoqsija waħda biex tgħinni nagħżel, imbagħad iggwidani fil-kompitu.',
  toolGoogleSummary: 'Email, kalendarju u dokumenti',
  toolSlackSummary: 'Konversazzjonijiet u għarfien tat-tim',
  toolShopifySummary: 'Prodotti, ordnijiet u stokk',
  toolWooSummary: 'Prodotti, ordnijiet u rapporti',
  toolMerchantSummary: 'Feeds tal-prodotti u status',
  toolInvoicesSummary: 'Fatturi u informazzjoni finanzjarja',
  toolGithubSummary: 'Proġetti, kwistjonijiet u pull requests',
  taskMeetingTitle: 'Ipprepara għal-laqgħa li jmiss tiegħi',
  taskMeetingPrompt:
    'Għinni nipprepara għal-laqgħa li jmiss tiegħi. Iċċekkja l-kalendarju, l-email u d-dokumenti disponibbli tiegħi, staqsi liema laqgħa jekk ikun meħtieġ, u agħmel sommarju qasir b’sorsi.',
  taskSlackTitle: 'Laħħaq ma’ konversazzjoni',
  taskSlackPrompt:
    'Għinni nlaħħaq ma’ konversazzjoni fuq Slack. Staqsi fuq liema kanal jew suġġett niffokaw, imbagħad agħti sommarju tad-deċiżjonijiet, il-mistoqsijiet miftuħa u l-passi li jmiss b’links għall-messaġġi.',
  taskShopifyTitle: 'Agħti ħarsa ġdida lejn il-prodotti tiegħi',
  taskShopifyPrompt:
    'Irrevedi kampjun żgħir tal-prodotti tiegħi fuq Shopify u identifika titjib utli fl-informazzjoni tal-prodotti. Uri l-evidenza u tagħmilx bidliet.',
  taskWooTitle: 'Irrevedi l-informazzjoni tal-prodotti tiegħi',
  taskWooPrompt:
    'Irrevedi kampjun żgħir tal-prodotti tiegħi fuq WooCommerce u identifika n-nuqqasijiet fl-informazzjoni tal-prodotti. Uri s-sorsi u tagħmilx bidliet.',
  taskFeedTitle: 'Irrevedi l-feed tal-prodotti tiegħi',
  taskFeedPrompt:
    'Għinni nirrevedi l-feed tal-prodotti tiegħi f’Google Merchant Center. Ibda bil-kont aċċessibbli u b’kampjun żgħir, u semmi s-sorsi ta’ dak li ssib.',
  taskFortnoxTitle: 'Ifhem il-fatturi reċenti',
  taskFortnoxPrompt:
    'Għinni nifhem il-fatturi reċenti f’Fortnox. Staqsi liema perjodu nirrevedu u agħti sommarju tal-informazzjoni disponibbli b’sorsi. Tagħmilx bidliet.',
  taskTripletexTitle: 'Ħarsa ġenerali lejn il-fatturi reċenti',
  taskTripletexPrompt:
    'Għinni nifhem il-fatturi reċenti f’Tripletex. Staqsi liema perjodu nirrevedu u agħti sommarju tal-informazzjoni disponibbli b’sorsi. Tagħmilx bidliet.',
  taskGithubTitle: 'Laħħaq ma’ proġett',
  taskGithubPrompt:
    'Għinni nlaħħaq ma’ proġett fuq GitHub. Staqsi liema repożitorju nirrevedu u agħti sommarju tal-kwistjonijiet u l-pull requests reċenti b’links.',
  taskTryTitle: 'Ipprova {name}',
  taskTryPrompt:
    'Għinni nibda bil-konnessjoni tiegħi ma’ {name}. Skopri l-għodod disponibbli tagħha, issuġġerixxi l-ewwel kompitu utli, u staqsini x’irrid nagħmel. Ipproponi kull bidla biex nirrevediha qabel ma teżegwiha.',
  back: 'Lura',
  continue: 'Kompli',
  continueWithout: 'Kompli mingħajr ma tqabbad',
  goToWorkspace: 'Mur fl-ispazju tax-xogħol',
  opening: 'Qed jinfetaħ l-ispazju tax-xogħol tiegħek…',
  startFailed: 'Ma stajniex niftħu l-ispazju tax-xogħol tiegħek. Erġa’ pprova.',
  loadingTitle: 'Qed tinfetaħ il-kumpanija tiegħek…',
  loadingStatus: 'Qed jitgħabba l-ispazju tax-xogħol tiegħek',
  errorTitle: 'Ejja nerġgħu nippruvaw.',
  tryAgain: 'Erġa’ pprova',
  noCompanyTitle: 'Hemm bżonn ta’ invit.',
  noCompanyBody:
    'Il-kont tiegħek mhuwiex marbut ma’ kumpanija. Itlob lil sid jistieden l-indirizz tal-email tiegħek. Ix-xogħol privat tiegħek jinżamm.',
  checkAccess: 'Iċċekkja l-istatus tal-aċċess',
};

const pl: WelcomeCopy = {
  logOut: 'Wyloguj się',
  progressLabel: 'Pierwsze kroki',
  stepOf: 'Krok {current} z {total}',
  stepFocus: 'Twoje priorytety',
  stepTools: 'Twoje narzędzia',
  stepFirst: 'Twój pierwszy krok',
  focusTitle: 'Powiedz nam, jak pracujesz.',
  focusTitleNamed: 'Witaj, {name}. Jak pracujesz?',
  focusIntro:
    'Wybierz swoją rolę i to, w czym potrzebujesz pomocy. Oba wybory są opcjonalne i pomagają nam proponować przydatne narzędzia.',
  roleHeading: 'Twoja rola',
  goalsHeading: 'W czym możemy pomóc?',
  roleLeadership: 'Zarząd',
  roleProduct: 'Produkt',
  roleEngineering: 'Inżynieria i dane',
  roleMarketing: 'Marketing',
  roleSales: 'Sprzedaż',
  roleOperations: 'Operacje',
  roleFinance: 'Finanse',
  goalEverydayTitle: 'Bądź krok przed innymi',
  goalEverydayBody: 'Przygotuj się do spotkań i nadrób zaległe rozmowy.',
  goalCommerceTitle: 'Zrozum mój biznes',
  goalCommerceBody: 'Poznaj produkty, zamówienia i możliwości.',
  goalFinanceTitle: 'Zobacz liczby wyraźnie',
  goalFinanceBody: 'Zrozum faktury i informacje finansowe.',
  goalProjectsTitle: 'Popychaj pracę naprzód',
  goalProjectsBody: 'Połącz dyskusje, zgłoszenia i aktualizacje projektów.',
  toolsTitle: 'Połącz swoje codzienne narzędzia.',
  toolsIntro: 'Odrobina kontekstu robi dużą różnicę. Zacznij od jednego lub dwóch.',
  toolsUnavailable: 'Połączenia są teraz niedostępne. Możesz zacząć od pytania lub pliku.',
  toolsLabel: 'Połącz swoje narzędzia',
  suggestedTools: 'Propozycje dla Ciebie',
  allTools: 'Wszystkie narzędzia',
  browseAllTools: 'Przeglądaj wszystkie narzędzia',
  backToSuggestions: 'Wróć do propozycji',
  searchTools: 'Szukaj narzędzi',
  findTool: 'Znajdź narzędzie…',
  popupBlocked:
    'Przeglądarka zablokowała wyskakujące okno. Ta strona sprawdza dalej, gdy udzielasz dostępu.',
  openAuthorization: 'Otwórz stronę autoryzacji',
  authorizing: 'Dokończ autoryzację w oknie dostawcy.',
  stopWaiting: 'Przestań czekać',
  loadingConnections: 'Wczytywanie połączeń…',
  connectionsFailed: 'Nie udało się wczytać połączeń.',
  useMyAccount: 'Użyj mojego konta',
  useCompanyAccount: 'Użyj konta dla {company}',
  chooseAudience: 'Wybierz, kto może korzystać z tego połączenia',
  noToolsMatch: 'Żadne narzędzie nie pasuje do wyszukiwania. Spróbuj innej nazwy.',
  noToolsYet: 'Nie ma jeszcze narzędzi do zaproponowania. Możesz zacząć od pytania lub pliku.',
  reminderTitle: 'Lepiej z Twoimi narzędziami.',
  reminderIntro:
    'Na początek wystarczy jedno połączenie. Wypróbuj narzędzie, którego już używasz, albo wróć do tego później.',
  reminderDoneTitle: 'Dobry początek.',
  reminderDoneIntro: 'Kolejne możesz dodać, kiedy będą potrzebne.',
  firstTitle: 'Od czego zaczniemy?',
  firstIntro: 'Wybierz przykład, aby rozpocząć rozmowę, albo przejdź od razu do obszaru roboczego.',
  connectedOne: 'Połączono 1 konto',
  connectedMany: 'Połączone konta: {count}',
  taskResearchTitle: 'Zbadaj okazję biznesową',
  taskResearchPrompt:
    'Pomóż mi zbadać okazję dla naszej firmy na podstawie publicznych źródeł. Zapytaj, co chcę zbadać, a potem pomóż mi przygotować przydatne podsumowanie ze źródłami.',
  taskFileTitle: 'Przejrzyj dokument lub arkusz kalkulacyjny',
  taskFilePrompt:
    'Pomóż mi przejrzeć dokument lub arkusz kalkulacyjny. Poproś mnie o załączenie pliku i zapytaj, czego chcę się dowiedzieć lub co zrobić, a potem mnie poprowadź.',
  taskChooseTitle: 'Pomóż mi wybrać pierwsze zadanie',
  taskChoosePrompt:
    'Pomóż mi zacząć pracę z Poneva. Wykorzystaj kontekst naszej firmy i dostępne możliwości, aby zaproponować kilka przydatnych rzeczy, które możemy zrobić razem. Zadaj jedno pytanie, które pomoże mi wybrać, a potem poprowadź mnie przez zadanie.',
  toolGoogleSummary: 'E-mail, kalendarz i dokumenty',
  toolSlackSummary: 'Rozmowy i wiedza zespołu',
  toolShopifySummary: 'Produkty, zamówienia i stany magazynowe',
  toolWooSummary: 'Produkty, zamówienia i raporty',
  toolMerchantSummary: 'Pliki produktowe i statusy',
  toolInvoicesSummary: 'Faktury i informacje finansowe',
  toolGithubSummary: 'Projekty, zgłoszenia i pull requesty',
  taskMeetingTitle: 'Przygotuj mnie do następnego spotkania',
  taskMeetingPrompt:
    'Pomóż mi przygotować się do następnego spotkania. Sprawdź mój dostępny kalendarz, e-maile i dokumenty, w razie potrzeby zapytaj, o które spotkanie chodzi, i przygotuj zwięzłe podsumowanie ze źródłami.',
  taskSlackTitle: 'Pomóż mi nadrobić rozmowę',
  taskSlackPrompt:
    'Pomóż mi nadrobić rozmowę na Slacku. Zapytaj, na którym kanale lub temacie się skupić, a potem podsumuj decyzje, otwarte pytania i kolejne kroki z linkami do wiadomości.',
  taskShopifyTitle: 'Spójrz świeżym okiem na moje produkty',
  taskShopifyPrompt:
    'Przejrzyj małą próbkę moich produktów w Shopify i znajdź przydatne ulepszenia informacji o produktach. Pokaż dowody i niczego nie zmieniaj.',
  taskWooTitle: 'Przejrzyj informacje o moich produktach',
  taskWooPrompt:
    'Przejrzyj małą próbkę moich produktów w WooCommerce i znajdź luki w informacjach o produktach. Podaj źródła i niczego nie zmieniaj.',
  taskFeedTitle: 'Przejrzyj mój plik produktowy',
  taskFeedPrompt:
    'Pomóż mi przejrzeć plik produktowy w Google Merchant Center. Zacznij od dostępnego konta i małej próbki, a przy ustaleniach podaj źródła.',
  taskFortnoxTitle: 'Wyjaśnij mi ostatnie faktury',
  taskFortnoxPrompt:
    'Pomóż mi zrozumieć ostatnie faktury w Fortnox. Zapytaj, jaki okres przejrzeć, i podsumuj dostępne informacje ze źródłami. Niczego nie zmieniaj.',
  taskTripletexTitle: 'Daj mi przegląd ostatnich faktur',
  taskTripletexPrompt:
    'Pomóż mi zrozumieć ostatnie faktury w Tripletex. Zapytaj, jaki okres przejrzeć, i podsumuj dostępne informacje ze źródłami. Niczego nie zmieniaj.',
  taskGithubTitle: 'Pomóż mi nadrobić projekt',
  taskGithubPrompt:
    'Pomóż mi nadrobić zaległości w projekcie na GitHubie. Zapytaj, które repozytorium przejrzeć, i podsumuj ostatnie zgłoszenia i pull requesty z linkami.',
  taskTryTitle: 'Wypróbuj {name}',
  taskTryPrompt:
    'Pomóż mi zacząć korzystać z połączenia {name}. Sprawdź dostępne w nim narzędzia, zaproponuj przydatne pierwsze zadanie i zapytaj, co chcę zrobić. Każdą zmianę przedstaw mi do przeglądu, zanim ją wykonasz.',
  back: 'Wstecz',
  continue: 'Dalej',
  continueWithout: 'Kontynuuj bez łączenia',
  goToWorkspace: 'Przejdź do obszaru roboczego',
  opening: 'Otwieranie obszaru roboczego…',
  startFailed: 'Nie udało się otworzyć obszaru roboczego. Spróbuj ponownie.',
  loadingTitle: 'Otwieranie Twojej firmy…',
  loadingStatus: 'Ładowanie obszaru roboczego',
  errorTitle: 'Spróbujmy jeszcze raz.',
  tryAgain: 'Spróbuj ponownie',
  noCompanyTitle: 'Potrzebne jest zaproszenie.',
  noCompanyBody:
    'Twoje konto nie jest powiązane z żadną firmą. Poproś właściciela o zaproszenie Twojego adresu e-mail. Twoja prywatna praca zostaje zachowana.',
  checkAccess: 'Sprawdź dostęp',
};

const pt: WelcomeCopy = {
  logOut: 'Terminar sessão',
  progressLabel: 'Primeiros passos',
  stepOf: 'Passo {current} de {total}',
  stepFocus: 'O seu foco',
  stepTools: 'As suas ferramentas',
  stepFirst: 'O seu primeiro passo',
  focusTitle: 'Diga-nos como trabalha.',
  focusTitleNamed: 'Bem-vindo, {name}. Como trabalha?',
  focusIntro:
    'Escolha a sua função e aquilo em que quer ajuda. Ambas são opcionais e ajudam-nos a sugerir ferramentas úteis.',
  roleHeading: 'A sua função',
  goalsHeading: 'Em que gostaria de ter ajuda?',
  roleLeadership: 'Direção',
  roleProduct: 'Produto',
  roleEngineering: 'Engenharia e dados',
  roleMarketing: 'Marketing',
  roleSales: 'Vendas',
  roleOperations: 'Operações',
  roleFinance: 'Finanças',
  goalEverydayTitle: 'Estar um passo à frente',
  goalEverydayBody: 'Preparar reuniões e pôr as conversas em dia.',
  goalCommerceTitle: 'Compreender o meu negócio',
  goalCommerceBody: 'Explorar produtos, encomendas e oportunidades.',
  goalFinanceTitle: 'Ver os números com clareza',
  goalFinanceBody: 'Perceber faturas e informação financeira.',
  goalProjectsTitle: 'Manter o trabalho a avançar',
  goalProjectsBody: 'Ligar discussões, pedidos e atualizações de projetos.',
  toolsTitle: 'Ligue as suas ferramentas do dia a dia.',
  toolsIntro: 'Um pouco de contexto faz uma grande diferença. Comece com uma ou duas.',
  toolsUnavailable:
    'As ligações não estão disponíveis de momento. Pode começar com uma pergunta ou um ficheiro.',
  toolsLabel: 'Ligue as suas ferramentas',
  suggestedTools: 'Sugestões para si',
  allTools: 'Todas as ferramentas',
  browseAllTools: 'Ver todas as ferramentas',
  backToSuggestions: 'Voltar às sugestões',
  searchTools: 'Pesquisar ferramentas',
  findTool: 'Encontrar uma ferramenta…',
  popupBlocked:
    'O navegador bloqueou a janela pop-up. Esta página continua a verificar enquanto concede a autorização.',
  openAuthorization: 'Abrir a página de autorização',
  authorizing: 'Conclua a autorização na janela do fornecedor.',
  stopWaiting: 'Deixar de esperar',
  loadingConnections: 'A carregar as suas ligações…',
  connectionsFailed: 'Não foi possível carregar as suas ligações.',
  useMyAccount: 'Usar a minha conta',
  useCompanyAccount: 'Usar uma conta para {company}',
  chooseAudience: 'Escolha quem pode usar esta ligação',
  noToolsMatch: 'Nenhuma ferramenta corresponde à pesquisa. Experimente outro nome.',
  noToolsYet:
    'Ainda não há ferramentas para sugerir. Pode começar com uma pergunta ou um ficheiro.',
  reminderTitle: 'Melhor com as suas ferramentas.',
  reminderIntro:
    'Uma ligação basta para começar. Experimente uma ferramenta que já utiliza ou volte a isto mais tarde.',
  reminderDoneTitle: 'Um bom começo.',
  reminderDoneIntro: 'Pode adicionar mais sempre que precisar.',
  firstTitle: 'Por onde começamos?',
  firstIntro:
    'Escolha um exemplo para iniciar uma conversa ou vá diretamente para o seu espaço de trabalho.',
  connectedOne: '1 conta ligada',
  connectedMany: '{count} contas ligadas',
  taskResearchTitle: 'Explorar uma oportunidade de negócio',
  taskResearchPrompt:
    'Ajuda-me a investigar uma oportunidade para o nosso negócio com fontes públicas. Pergunta o que quero explorar e depois ajuda-me a preparar um resumo útil e com fontes.',
  taskFileTitle: 'Analisar um documento ou folha de cálculo',
  taskFilePrompt:
    'Ajuda-me a analisar um documento ou uma folha de cálculo. Pede-me para anexar um ficheiro e pergunta o que quero saber ou fazer; depois guia-me.',
  taskChooseTitle: 'Ajude-me a escolher uma primeira tarefa',
  taskChoosePrompt:
    'Ajuda-me a começar com o Poneva. Usa o contexto da nossa empresa e as capacidades disponíveis para sugerir algumas coisas úteis que possamos fazer juntos. Faz-me uma pergunta para me ajudar a escolher e depois guia-me na tarefa.',
  toolGoogleSummary: 'E-mail, calendário e documentos',
  toolSlackSummary: 'Conversas e conhecimento da equipa',
  toolShopifySummary: 'Produtos, encomendas e inventário',
  toolWooSummary: 'Produtos, encomendas e relatórios',
  toolMerchantSummary: 'Feeds de produtos e estados',
  toolInvoicesSummary: 'Faturas e informação financeira',
  toolGithubSummary: 'Projetos, issues e pull requests',
  taskMeetingTitle: 'Preparar a minha próxima reunião',
  taskMeetingPrompt:
    'Ajuda-me a preparar a minha próxima reunião. Consulta o meu calendário, e-mail e documentos disponíveis, pergunta qual é a reunião se for preciso e faz um resumo conciso com fontes.',
  taskSlackTitle: 'Pôr-me a par de uma conversa',
  taskSlackPrompt:
    'Ajuda-me a pôr-me a par de uma conversa no Slack. Pergunta em que canal ou tema nos devemos focar e depois resume decisões, questões em aberto e próximos passos com ligações para as mensagens.',
  taskShopifyTitle: 'Olhar de novo para os meus produtos',
  taskShopifyPrompt:
    'Analisa uma pequena amostra dos meus produtos Shopify e identifica melhorias úteis na informação dos produtos. Mostra as evidências e não alteres nada.',
  taskWooTitle: 'Rever a informação dos meus produtos',
  taskWooPrompt:
    'Analisa uma pequena amostra dos meus produtos WooCommerce e identifica lacunas na informação dos produtos. Indica as fontes e não alteres nada.',
  taskFeedTitle: 'Rever o meu feed de produtos',
  taskFeedPrompt:
    'Ajuda-me a rever o meu feed de produtos do Google Merchant Center. Começa pela conta acessível e por uma pequena amostra, e cita as fontes do que encontrares.',
  taskFortnoxTitle: 'Perceber as faturas recentes',
  taskFortnoxPrompt:
    'Ajuda-me a perceber as faturas recentes no Fortnox. Pergunta que período devemos rever e resume a informação disponível com fontes. Não alteres nada.',
  taskTripletexTitle: 'Ter uma visão geral das faturas recentes',
  taskTripletexPrompt:
    'Ajuda-me a perceber as faturas recentes no Tripletex. Pergunta que período devemos rever e resume a informação disponível com fontes. Não alteres nada.',
  taskGithubTitle: 'Pôr-me a par de um projeto',
  taskGithubPrompt:
    'Ajuda-me a pôr-me a par de um projeto no GitHub. Pergunta que repositório devemos rever e resume as issues e pull requests recentes com ligações.',
  taskTryTitle: 'Experimentar {name}',
  taskTryPrompt:
    'Ajuda-me a começar com a minha ligação {name}. Descobre as ferramentas disponíveis, sugere uma primeira tarefa útil e pergunta o que quero fazer. Propõe-me quaisquer alterações para revisão antes de as executares.',
  back: 'Voltar',
  continue: 'Continuar',
  continueWithout: 'Continuar sem ligar',
  goToWorkspace: 'Ir para o espaço de trabalho',
  opening: 'A abrir o seu espaço de trabalho…',
  startFailed: 'Não foi possível abrir o seu espaço de trabalho. Tente novamente.',
  loadingTitle: 'A abrir a sua empresa…',
  loadingStatus: 'A carregar o seu espaço de trabalho',
  errorTitle: 'Vamos tentar novamente.',
  tryAgain: 'Tentar novamente',
  noCompanyTitle: 'É necessário um convite.',
  noCompanyBody:
    'A sua conta não está associada a nenhuma empresa. Peça a um proprietário que convide o seu endereço de e-mail. O seu trabalho privado é mantido.',
  checkAccess: 'Verificar acesso',
};

const ro: WelcomeCopy = {
  logOut: 'Deconectare',
  progressLabel: 'Primii pași',
  stepOf: 'Pasul {current} din {total}',
  stepFocus: 'Direcția dvs.',
  stepTools: 'Instrumentele dvs.',
  stepFirst: 'Primul pas',
  focusTitle: 'Spuneți-ne cum lucrați.',
  focusTitleNamed: 'Bun venit, {name}. Cum lucrați?',
  focusIntro:
    'Alegeți-vă rolul și domeniile în care doriți ajutor. Ambele sunt opționale și ne ajută să vă sugerăm instrumente utile.',
  roleHeading: 'Rolul dvs.',
  goalsHeading: 'La ce ați dori ajutor?',
  roleLeadership: 'Conducere',
  roleProduct: 'Produs',
  roleEngineering: 'Inginerie și date',
  roleMarketing: 'Marketing',
  roleSales: 'Vânzări',
  roleOperations: 'Operațiuni',
  roleFinance: 'Finanțe',
  goalEverydayTitle: 'Cu un pas înainte',
  goalEverydayBody: 'Pregătiți-vă pentru ședințe și recuperați conversațiile.',
  goalCommerceTitle: 'Să-mi înțeleg afacerea',
  goalCommerceBody: 'Explorați produse, comenzi și oportunități.',
  goalFinanceTitle: 'Cifrele, clar',
  goalFinanceBody: 'Înțelegeți facturile și informațiile financiare.',
  goalProjectsTitle: 'Munca să meargă înainte',
  goalProjectsBody: 'Legați discuțiile, problemele și noutățile proiectelor.',
  toolsTitle: 'Conectați-vă instrumentele de zi cu zi.',
  toolsIntro: 'Puțin context face o mare diferență. Începeți cu unul sau două.',
  toolsUnavailable:
    'Conexiunile nu sunt disponibile momentan. Puteți începe cu o întrebare sau un fișier.',
  toolsLabel: 'Conectați-vă instrumentele',
  suggestedTools: 'Sugestii pentru dvs.',
  allTools: 'Toate instrumentele',
  browseAllTools: 'Răsfoiți toate instrumentele',
  backToSuggestions: 'Înapoi la sugestii',
  searchTools: 'Căutați instrumente',
  findTool: 'Găsiți un instrument…',
  popupBlocked:
    'Browserul a blocat fereastra pop-up. Această pagină continuă să verifice cât timp acordați autorizarea.',
  openAuthorization: 'Deschideți pagina de autorizare',
  authorizing: 'Finalizați autorizarea în fereastra furnizorului.',
  stopWaiting: 'Nu mai așteptați',
  loadingConnections: 'Se încarcă conexiunile…',
  connectionsFailed: 'Nu am putut încărca conexiunile dvs.',
  useMyAccount: 'Folosiți contul meu',
  useCompanyAccount: 'Folosiți un cont pentru {company}',
  chooseAudience: 'Alegeți cine poate folosi această conexiune',
  noToolsMatch: 'Niciun instrument nu corespunde căutării. Încercați alt nume.',
  noToolsYet: 'Încă nu există instrumente de sugerat. Puteți începe cu o întrebare sau un fișier.',
  reminderTitle: 'Mai bine cu instrumentele dvs.',
  reminderIntro:
    'O singură conexiune ajunge pentru început. Încercați un instrument pe care îl folosiți deja sau reveniți mai târziu.',
  reminderDoneTitle: 'Un început bun.',
  reminderDoneIntro: 'Puteți adăuga mai multe oricând aveți nevoie.',
  firstTitle: 'De unde începem?',
  firstIntro:
    'Alegeți un exemplu pentru a începe o conversație sau mergeți direct în spațiul de lucru.',
  connectedOne: '1 cont conectat',
  connectedMany: 'Conturi conectate: {count}',
  taskResearchTitle: 'Explorați o oportunitate de afaceri',
  taskResearchPrompt:
    'Ajută-mă să cercetez o oportunitate pentru afacerea noastră folosind surse publice. Întreabă-mă ce vreau să explorez, apoi ajută-mă să fac un rezumat util, cu surse citate.',
  taskFileTitle: 'Lucrați pe un document sau o foaie de calcul',
  taskFilePrompt:
    'Ajută-mă să lucrez pe un document sau o foaie de calcul. Cere-mi să atașez un fișier și întreabă-mă ce vreau să aflu sau să fac, apoi ghidează-mă.',
  taskChooseTitle: 'Ajută-mă să aleg o primă sarcină',
  taskChoosePrompt:
    'Ajută-mă să încep cu Poneva. Folosește contextul companiei noastre și capabilitățile disponibile pentru a sugera câteva lucruri utile pe care le putem face împreună. Pune-mi o întrebare care să mă ajute să aleg, apoi ghidează-mă prin sarcină.',
  toolGoogleSummary: 'E-mail, calendar și documente',
  toolSlackSummary: 'Conversații și cunoștințele echipei',
  toolShopifySummary: 'Produse, comenzi și stocuri',
  toolWooSummary: 'Produse, comenzi și rapoarte',
  toolMerchantSummary: 'Fluxuri de produse și stări',
  toolInvoicesSummary: 'Facturi și informații financiare',
  toolGithubSummary: 'Proiecte, probleme și pull requests',
  taskMeetingTitle: 'Pregătește-mă pentru următoarea întâlnire',
  taskMeetingPrompt:
    'Ajută-mă să mă pregătesc pentru următoarea întâlnire. Verifică-mi calendarul, e-mailul și documentele disponibile, întreabă despre ce întâlnire e vorba dacă e nevoie și fă un rezumat concis cu surse.',
  taskSlackTitle: 'Pune-mă la curent cu o conversație',
  taskSlackPrompt:
    'Pune-mă la curent cu o conversație din Slack. Întreabă pe ce canal sau subiect să ne concentrăm, apoi rezumă deciziile, întrebările deschise și pașii următori, cu linkuri către mesaje.',
  taskShopifyTitle: 'Privește din nou produsele mele',
  taskShopifyPrompt:
    'Analizează un mic eșantion din produsele mele Shopify și identifică îmbunătățiri utile ale informațiilor despre produse. Arată dovezile și nu modifica nimic.',
  taskWooTitle: 'Verifică informațiile despre produsele mele',
  taskWooPrompt:
    'Analizează un mic eșantion din produsele mele WooCommerce și identifică lipsurile din informațiile despre produse. Indică sursele și nu modifica nimic.',
  taskFeedTitle: 'Verifică fluxul meu de produse',
  taskFeedPrompt:
    'Ajută-mă să verific fluxul meu de produse din Google Merchant Center. Începe cu contul accesibil și un mic eșantion și citează sursele pentru ce găsești.',
  taskFortnoxTitle: 'Explică-mi facturile recente',
  taskFortnoxPrompt:
    'Ajută-mă să înțeleg facturile recente din Fortnox. Întreabă ce perioadă să analizăm și rezumă informațiile disponibile cu surse. Nu modifica nimic.',
  taskTripletexTitle: 'Oferă-mi o imagine de ansamblu a facturilor recente',
  taskTripletexPrompt:
    'Ajută-mă să înțeleg facturile recente din Tripletex. Întreabă ce perioadă să analizăm și rezumă informațiile disponibile cu surse. Nu modifica nimic.',
  taskGithubTitle: 'Pune-mă la curent cu un proiect',
  taskGithubPrompt:
    'Pune-mă la curent cu un proiect GitHub. Întreabă ce repository să analizăm și rezumă problemele și pull requests recente, cu linkuri.',
  taskTryTitle: 'Încearcă {name}',
  taskTryPrompt:
    'Ajută-mă să încep cu conexiunea mea {name}. Descoperă instrumentele disponibile, sugerează o primă sarcină utilă și întreabă-mă ce vreau să fac. Propune-mi orice modificare spre revizuire înainte s-o execuți.',
  back: 'Înapoi',
  continue: 'Continuați',
  continueWithout: 'Continuați fără conectare',
  goToWorkspace: 'Mergeți la spațiul de lucru',
  opening: 'Se deschide spațiul de lucru…',
  startFailed: 'Nu am putut deschide spațiul de lucru. Încercați din nou.',
  loadingTitle: 'Se deschide compania…',
  loadingStatus: 'Se încarcă spațiul de lucru',
  errorTitle: 'Să încercăm din nou.',
  tryAgain: 'Încercați din nou',
  noCompanyTitle: 'Este nevoie de o invitație.',
  noCompanyBody:
    'Contul dvs. nu este asociat cu nicio companie. Cereți unui proprietar să vă invite adresa de e-mail. Munca dvs. privată se păstrează.',
  checkAccess: 'Verificați starea accesului',
};

const sk: WelcomeCopy = {
  logOut: 'Odhlásiť sa',
  progressLabel: 'Začíname',
  stepOf: 'Krok {current} z {total}',
  stepFocus: 'Vaše zameranie',
  stepTools: 'Vaše nástroje',
  stepFirst: 'Váš prvý krok',
  focusTitle: 'Povedzte nám, ako pracujete.',
  focusTitleNamed: 'Vitajte, {name}. Ako pracujete?',
  focusIntro:
    'Vyberte svoju rolu a s čím chcete pomôcť. Oboje je nepovinné a pomáha nám navrhnúť užitočné nástroje.',
  roleHeading: 'Vaša rola',
  goalsHeading: 'S čím by ste chceli pomôcť?',
  roleLeadership: 'Vedenie',
  roleProduct: 'Produkt',
  roleEngineering: 'Vývoj a dáta',
  roleMarketing: 'Marketing',
  roleSales: 'Predaj',
  roleOperations: 'Prevádzka',
  roleFinance: 'Financie',
  goalEverydayTitle: 'Byť o krok vpred',
  goalEverydayBody: 'Pripravte sa na stretnutia a dožente konverzácie.',
  goalCommerceTitle: 'Pochopiť svoj biznis',
  goalCommerceBody: 'Preskúmajte produkty, objednávky a príležitosti.',
  goalFinanceTitle: 'Vidieť čísla jasne',
  goalFinanceBody: 'Vyznajte sa vo faktúrach a finančných údajoch.',
  goalProjectsTitle: 'Posúvať prácu ďalej',
  goalProjectsBody: 'Prepojte diskusie, úlohy a novinky z projektov.',
  toolsTitle: 'Pripojte svoje každodenné nástroje.',
  toolsIntro: 'Trocha kontextu urobí veľký rozdiel. Začnite jedným alebo dvoma.',
  toolsUnavailable: 'Pripojenia teraz nie sú k dispozícii. Môžete začať otázkou alebo súborom.',
  toolsLabel: 'Pripojte svoje nástroje',
  suggestedTools: 'Odporúčané pre vás',
  allTools: 'Všetky nástroje',
  browseAllTools: 'Prehliadať všetky nástroje',
  backToSuggestions: 'Späť na odporúčania',
  searchTools: 'Hľadať nástroje',
  findTool: 'Nájsť nástroj…',
  popupBlocked:
    'Prehliadač zablokoval vyskakovacie okno. Táto stránka medzitým ďalej kontroluje, či ste prístup povolili.',
  openAuthorization: 'Otvoriť stránku na povolenie prístupu',
  authorizing: 'Dokončite povolenie prístupu v okne poskytovateľa.',
  stopWaiting: 'Prestať čakať',
  loadingConnections: 'Načítavajú sa vaše pripojenia…',
  connectionsFailed: 'Nepodarilo sa načítať vaše pripojenia.',
  useMyAccount: 'Použiť môj účet',
  useCompanyAccount: 'Použiť účet pre {company}',
  chooseAudience: 'Vyberte, kto môže toto pripojenie používať',
  noToolsMatch: 'Hľadaniu nezodpovedá žiadny nástroj. Skúste iný názov.',
  noToolsYet: 'Zatiaľ nie je čo odporučiť. Môžete začať otázkou alebo súborom.',
  reminderTitle: 'Lepšie s vašimi nástrojmi.',
  reminderIntro:
    'Na začiatok stačí jedno pripojenie. Vyskúšajte nástroj, ktorý už používate, alebo sa k tomu vráťte neskôr.',
  reminderDoneTitle: 'Dobrý začiatok.',
  reminderDoneIntro: 'Ďalšie môžete pridať, kedykoľvek ich budete potrebovať.',
  firstTitle: 'Čím začneme?',
  firstIntro:
    'Vyberte príklad a začnite konverzáciu, alebo prejdite rovno do pracovného priestoru.',
  connectedOne: 'Pripojený 1 účet',
  connectedMany: 'Pripojené účty: {count}',
  taskResearchTitle: 'Preskúmať obchodnú príležitosť',
  taskResearchPrompt:
    'Pomôž mi preskúmať príležitosť pre našu firmu z verejných zdrojov. Opýtaj sa, čo chcem skúmať, a potom mi pomôž pripraviť užitočné zhrnutie so zdrojmi.',
  taskFileTitle: 'Prejsť dokument alebo tabuľku',
  taskFilePrompt:
    'Pomôž mi prejsť dokument alebo tabuľku. Požiadaj ma o priloženie súboru, opýtaj sa, čo chcem zistiť alebo urobiť, a potom ma tým preveď.',
  taskChooseTitle: 'Pomôžte mi vybrať prvú úlohu',
  taskChoosePrompt:
    'Pomôž mi začať s Poneva. Využi kontext našej firmy a dostupné možnosti a navrhni pár užitočných vecí, ktoré môžeme urobiť spolu. Polož jednu otázku, ktorá mi pomôže vybrať, a potom ma preveď úlohou.',
  toolGoogleSummary: 'E-mail, kalendár a dokumenty',
  toolSlackSummary: 'Konverzácie a znalosti tímu',
  toolShopifySummary: 'Produkty, objednávky a zásoby',
  toolWooSummary: 'Produkty, objednávky a prehľady',
  toolMerchantSummary: 'Produktové feedy a stavy',
  toolInvoicesSummary: 'Faktúry a finančné informácie',
  toolGithubSummary: 'Projekty, issues a pull requesty',
  taskMeetingTitle: 'Pripraviť sa na ďalšie stretnutie',
  taskMeetingPrompt:
    'Pomôž mi pripraviť sa na moje ďalšie stretnutie. Prejdi môj dostupný kalendár, e-maily a dokumenty, v prípade potreby sa opýtaj, o ktoré stretnutie ide, a priprav stručný podklad so zdrojmi.',
  taskSlackTitle: 'Dobehnúť konverzáciu',
  taskSlackPrompt:
    'Pomôž mi dobehnúť konverzáciu v Slacku. Opýtaj sa, na ktorý kanál alebo tému sa zamerať, a potom zhrň rozhodnutia, otvorené otázky a ďalšie kroky s odkazmi na správy.',
  taskShopifyTitle: 'Pozrieť sa na moje produkty nanovo',
  taskShopifyPrompt:
    'Prejdi malú vzorku mojich produktov v Shopify a nájdi užitočné vylepšenia produktových informácií. Ukáž podklady a nič nemeň.',
  taskWooTitle: 'Skontrolovať informácie o produktoch',
  taskWooPrompt:
    'Prejdi malú vzorku mojich produktov vo WooCommerce a nájdi medzery v produktových informáciách. Uveď zdroje a nič nemeň.',
  taskFeedTitle: 'Skontrolovať produktový feed',
  taskFeedPrompt:
    'Pomôž mi skontrolovať môj produktový feed v Google Merchant Center. Začni s dostupným účtom a malou vzorkou a pri zisteniach uveď zdroje.',
  taskFortnoxTitle: 'Vyznať sa v nedávnych faktúrach',
  taskFortnoxPrompt:
    'Pomôž mi pochopiť nedávne faktúry vo Fortnoxe. Opýtaj sa, ktoré obdobie prejsť, a zhrň dostupné informácie so zdrojmi. Nič nemeň.',
  taskTripletexTitle: 'Získať prehľad o nedávnych faktúrach',
  taskTripletexPrompt:
    'Pomôž mi pochopiť nedávne faktúry v Tripletexe. Opýtaj sa, ktoré obdobie prejsť, a zhrň dostupné informácie so zdrojmi. Nič nemeň.',
  taskGithubTitle: 'Dobehnúť dianie v projekte',
  taskGithubPrompt:
    'Pomôž mi dobehnúť dianie v projekte na GitHube. Opýtaj sa, ktorý repozitár prejsť, a zhrň nedávne issues a pull requesty s odkazmi.',
  taskTryTitle: 'Vyskúšať {name}',
  taskTryPrompt:
    'Pomôž mi začať s mojím pripojením {name}. Zisti, aké nástroje ponúka, navrhni užitočnú prvú úlohu a opýtaj sa, čo chcem urobiť. Všetky zmeny mi pred vykonaním predlož na kontrolu.',
  back: 'Späť',
  continue: 'Pokračovať',
  continueWithout: 'Pokračovať bez pripojenia',
  goToWorkspace: 'Prejsť do pracovného priestoru',
  opening: 'Otvára sa pracovný priestor…',
  startFailed: 'Pracovný priestor sa nepodarilo otvoriť. Skúste to znova.',
  loadingTitle: 'Otvára sa vaša firma…',
  loadingStatus: 'Načítava sa pracovný priestor',
  errorTitle: 'Skúsme to znova.',
  tryAgain: 'Skúsiť znova',
  noCompanyTitle: 'Potrebujete pozvánku.',
  noCompanyBody:
    'Váš účet nie je prepojený so žiadnou firmou. Požiadajte vlastníka, aby pozval vašu e-mailovú adresu. Vaša súkromná práca zostane zachovaná.',
  checkAccess: 'Skontrolovať prístup',
};

const sl: WelcomeCopy = {
  logOut: 'Odjava',
  progressLabel: 'Prvi koraki',
  stepOf: 'Korak {current} od {total}',
  stepFocus: 'Vaš poudarek',
  stepTools: 'Vaša orodja',
  stepFirst: 'Vaš prvi korak',
  focusTitle: 'Povejte nam, kako delate.',
  focusTitleNamed: 'Dobrodošli, {name}. Kako delate?',
  focusIntro:
    'Izberite svojo vlogo in pri čem želite pomoč. Oboje je neobvezno in nam pomaga predlagati uporabna orodja.',
  roleHeading: 'Vaša vloga',
  goalsHeading: 'Pri čem bi želeli pomoč?',
  roleLeadership: 'Vodstvo',
  roleProduct: 'Izdelek',
  roleEngineering: 'Razvoj in podatki',
  roleMarketing: 'Trženje',
  roleSales: 'Prodaja',
  roleOperations: 'Poslovanje',
  roleFinance: 'Finance',
  goalEverydayTitle: 'Bodite korak pred drugimi',
  goalEverydayBody: 'Pripravite se na sestanke in ujemite pogovore.',
  goalCommerceTitle: 'Razumeti svoje poslovanje',
  goalCommerceBody: 'Raziščite izdelke, naročila in priložnosti.',
  goalFinanceTitle: 'Jasno videti številke',
  goalFinanceBody: 'Razumite račune in finančne podatke.',
  goalProjectsTitle: 'Ohranjati delo v teku',
  goalProjectsBody: 'Povežite razprave, zahtevke in novosti projektov.',
  toolsTitle: 'Povežite svoja vsakodnevna orodja.',
  toolsIntro: 'Malo konteksta naredi veliko razliko. Začnite z enim ali dvema.',
  toolsUnavailable: 'Povezave trenutno niso na voljo. Začnete lahko z vprašanjem ali datoteko.',
  toolsLabel: 'Povežite svoja orodja',
  suggestedTools: 'Predlagano za vas',
  allTools: 'Vsa orodja',
  browseAllTools: 'Prebrskajte vsa orodja',
  backToSuggestions: 'Nazaj na predloge',
  searchTools: 'Iskanje orodij',
  findTool: 'Poiščite orodje…',
  popupBlocked:
    'Brskalnik je blokiral pojavno okno. Ta stran še naprej preverja, medtem ko odobrite dostop.',
  openAuthorization: 'Odprite stran za odobritev',
  authorizing: 'Dokončajte odobritev v oknu ponudnika.',
  stopWaiting: 'Prekini čakanje',
  loadingConnections: 'Nalaganje vaših povezav…',
  connectionsFailed: 'Vaših povezav ni bilo mogoče naložiti.',
  useMyAccount: 'Uporabi moj račun',
  useCompanyAccount: 'Uporabi račun za {company}',
  chooseAudience: 'Izberite, kdo lahko uporablja to povezavo',
  noToolsMatch: 'Iskanju ne ustreza nobeno orodje. Poskusite z drugim imenom.',
  noToolsYet: 'Orodij za predlog še ni. Začnete lahko z vprašanjem ali datoteko.',
  reminderTitle: 'Bolje z vašimi orodji.',
  reminderIntro:
    'Za začetek zadostuje ena povezava. Preizkusite orodje, ki ga že uporabljate, ali se k temu vrnite pozneje.',
  reminderDoneTitle: 'Dober začetek.',
  reminderDoneIntro: 'Več jih lahko dodate, ko jih boste potrebovali.',
  firstTitle: 'Kje začnemo?',
  firstIntro: 'Izberite primer za začetek pogovora ali pojdite naravnost v svoj delovni prostor.',
  connectedOne: 'Povezan 1 račun',
  connectedMany: 'Povezani računi: {count}',
  taskResearchTitle: 'Raziščite poslovno priložnost',
  taskResearchPrompt:
    'Pomagaj mi raziskati priložnost za naše podjetje na podlagi javnih virov. Vprašaj, kaj želim raziskati, nato pa mi pomagaj pripraviti uporaben povzetek z viri.',
  taskFileTitle: 'Preglejte dokument ali preglednico',
  taskFilePrompt:
    'Pomagaj mi pregledati dokument ali preglednico. Prosi me, naj priložim datoteko, vprašaj, kaj želim izvedeti ali narediti, nato pa me vodi skozi.',
  taskChooseTitle: 'Pomagajte mi izbrati prvo nalogo',
  taskChoosePrompt:
    'Pomagaj mi začeti s Poneva. Uporabi kontekst našega podjetja in razpoložljive zmožnosti ter predlagaj nekaj uporabnih stvari, ki jih lahko naredimo skupaj. Postavi eno vprašanje, ki mi bo pomagalo izbrati, nato pa me vodi skozi nalogo.',
  toolGoogleSummary: 'E-pošta, koledar in dokumenti',
  toolSlackSummary: 'Pogovori in znanje ekipe',
  toolShopifySummary: 'Izdelki, naročila in zaloga',
  toolWooSummary: 'Izdelki, naročila in poročila',
  toolMerchantSummary: 'Viri izdelkov in stanja',
  toolInvoicesSummary: 'Računi in finančne informacije',
  toolGithubSummary: 'Projekti, zadeve in zahteve za združitev',
  taskMeetingTitle: 'Priprava na naslednji sestanek',
  taskMeetingPrompt:
    'Pomagaj mi pripraviti se na naslednji sestanek. Preglej moj razpoložljivi koledar, e-pošto in dokumente, po potrebi vprašaj, za kateri sestanek gre, in pripravi jedrnat povzetek z viri.',
  taskSlackTitle: 'Dohitevanje pogovora',
  taskSlackPrompt:
    'Pomagaj mi dohiteti pogovor v Slacku. Vprašaj, na kateri kanal ali temo naj se osredotočiva, nato povzemi odločitve, odprta vprašanja in naslednje korake s povezavami do sporočil.',
  taskShopifyTitle: 'Svež pogled na moje izdelke',
  taskShopifyPrompt:
    'Preglej majhen vzorec mojih izdelkov v Shopifyju in poišči koristne izboljšave podatkov o izdelkih. Pokaži dokaze in ničesar ne spreminjaj.',
  taskWooTitle: 'Pregled podatkov o mojih izdelkih',
  taskWooPrompt:
    'Preglej majhen vzorec mojih izdelkov v WooCommerceu in poišči vrzeli v podatkih o izdelkih. Navedi vire in ničesar ne spreminjaj.',
  taskFeedTitle: 'Pregled mojega vira izdelkov',
  taskFeedPrompt:
    'Pomagaj mi pregledati vir izdelkov v Google Merchant Centru. Začni z dostopnim računom in majhnim vzorcem ter navedi vire za svoje ugotovitve.',
  taskFortnoxTitle: 'Razumevanje nedavnih računov',
  taskFortnoxPrompt:
    'Pomagaj mi razumeti nedavne račune v Fortnoxu. Vprašaj, katero obdobje naj pregledava, in povzemi razpoložljive informacije z viri. Ničesar ne spreminjaj.',
  taskTripletexTitle: 'Pregled nedavnih računov',
  taskTripletexPrompt:
    'Pomagaj mi razumeti nedavne račune v Tripletexu. Vprašaj, katero obdobje naj pregledava, in povzemi razpoložljive informacije z viri. Ničesar ne spreminjaj.',
  taskGithubTitle: 'Dohitevanje projekta',
  taskGithubPrompt:
    'Pomagaj mi dohiteti dogajanje v projektu na GitHubu. Vprašaj, kateri repozitorij naj pregledava, in povzemi nedavne zadeve in zahteve za združitev s povezavami.',
  taskTryTitle: 'Preizkus povezave {name}',
  taskTryPrompt:
    'Pomagaj mi začeti s povezavo {name}. Odkrij njena razpoložljiva orodja, predlagaj koristno prvo nalogo in vprašaj, kaj želim narediti. Vse spremembe mi predloži v pregled, preden jih izvedeš.',
  back: 'Nazaj',
  continue: 'Nadaljuj',
  continueWithout: 'Nadaljuj brez povezave',
  goToWorkspace: 'Pojdi v delovni prostor',
  opening: 'Odpiranje delovnega prostora…',
  startFailed: 'Delovnega prostora ni bilo mogoče odpreti. Poskusite znova.',
  loadingTitle: 'Odpiranje vašega podjetja…',
  loadingStatus: 'Nalaganje delovnega prostora',
  errorTitle: 'Poskusimo znova.',
  tryAgain: 'Poskusi znova',
  noCompanyTitle: 'Potrebujete povabilo.',
  noCompanyBody:
    'Vaš račun ni povezan z nobenim podjetjem. Prosite lastnika, naj povabi vaš e-poštni naslov. Vaše zasebno delo ostane shranjeno.',
  checkAccess: 'Preveri dostop',
};

const es: WelcomeCopy = {
  logOut: 'Cerrar sesión',
  progressLabel: 'Primeros pasos',
  stepOf: 'Paso {current} de {total}',
  stepFocus: 'Tu enfoque',
  stepTools: 'Tus herramientas',
  stepFirst: 'Tu primer paso',
  focusTitle: 'Cuéntanos cómo trabajas.',
  focusTitleNamed: 'Te damos la bienvenida, {name}. ¿Cómo trabajas?',
  focusIntro:
    'Elige tu función y en qué quieres ayuda. Ambas son opcionales y nos ayudan a sugerir herramientas útiles.',
  roleHeading: 'Tu función',
  goalsHeading: '¿En qué te gustaría recibir ayuda?',
  roleLeadership: 'Dirección',
  roleProduct: 'Producto',
  roleEngineering: 'Ingeniería y datos',
  roleMarketing: 'Marketing',
  roleSales: 'Ventas',
  roleOperations: 'Operaciones',
  roleFinance: 'Finanzas',
  goalEverydayTitle: 'Ir un paso por delante',
  goalEverydayBody: 'Prepara reuniones y ponte al día con las conversaciones.',
  goalCommerceTitle: 'Entender mi negocio',
  goalCommerceBody: 'Explora productos, pedidos y oportunidades.',
  goalFinanceTitle: 'Ver las cifras con claridad',
  goalFinanceBody: 'Entiende las facturas y la información financiera.',
  goalProjectsTitle: 'Mantener el trabajo en marcha',
  goalProjectsBody: 'Conecta conversaciones, incidencias y novedades de proyectos.',
  toolsTitle: 'Conecta tus herramientas de cada día.',
  toolsIntro: 'Un poco de contexto marca una gran diferencia. Empieza con una o dos.',
  toolsUnavailable:
    'Las conexiones no están disponibles ahora mismo. Puedes empezar con una pregunta o un archivo.',
  toolsLabel: 'Conecta tus herramientas',
  suggestedTools: 'Sugerencias para ti',
  allTools: 'Todas las herramientas',
  browseAllTools: 'Ver todas las herramientas',
  backToSuggestions: 'Volver a las sugerencias',
  searchTools: 'Buscar herramientas',
  findTool: 'Busca una herramienta…',
  popupBlocked:
    'El navegador bloqueó la ventana emergente. Esta página sigue comprobando mientras das la autorización.',
  openAuthorization: 'Abrir la página de autorización',
  authorizing: 'Termina la autorización en la ventana del proveedor.',
  stopWaiting: 'Dejar de esperar',
  loadingConnections: 'Cargando tus conexiones…',
  connectionsFailed: 'No hemos podido cargar tus conexiones.',
  useMyAccount: 'Usar mi cuenta',
  useCompanyAccount: 'Usar una cuenta para {company}',
  chooseAudience: 'Elige quién puede usar esta conexión',
  noToolsMatch: 'Ninguna herramienta coincide con tu búsqueda. Prueba con otro nombre.',
  noToolsYet: 'Aún no hay herramientas que sugerir. Puedes empezar con una pregunta o un archivo.',
  reminderTitle: 'Mejor con tus herramientas.',
  reminderIntro:
    'Con una conexión basta para empezar. Prueba una herramienta que ya uses o vuelve a esto más tarde.',
  reminderDoneTitle: 'Un buen comienzo.',
  reminderDoneIntro: 'Puedes añadir más cuando las necesites.',
  firstTitle: '¿Por dónde empezamos?',
  firstIntro:
    'Elige un ejemplo para iniciar una conversación o ve directamente a tu espacio de trabajo.',
  connectedOne: '1 cuenta conectada',
  connectedMany: '{count} cuentas conectadas',
  taskResearchTitle: 'Explorar una oportunidad de negocio',
  taskResearchPrompt:
    'Ayúdame a investigar una oportunidad para nuestro negocio con fuentes públicas. Pregúntame qué quiero explorar y luego ayúdame a preparar un resumen útil y con fuentes.',
  taskFileTitle: 'Revisar un documento o una hoja de cálculo',
  taskFilePrompt:
    'Ayúdame a revisar un documento o una hoja de cálculo. Pídeme que adjunte un archivo y pregúntame qué quiero saber o conseguir; después guíame.',
  taskChooseTitle: 'Ayúdame a elegir una primera tarea',
  taskChoosePrompt:
    'Ayúdame a empezar con Poneva. Usa el contexto de nuestra empresa y las capacidades disponibles para sugerir algunas cosas útiles que podamos hacer juntos. Hazme una pregunta para ayudarme a elegir y luego guíame en la tarea.',
  toolGoogleSummary: 'Correo, calendario y documentos',
  toolSlackSummary: 'Conversaciones y conocimiento del equipo',
  toolShopifySummary: 'Productos, pedidos e inventario',
  toolWooSummary: 'Productos, pedidos e informes',
  toolMerchantSummary: 'Feeds de productos y estados',
  toolInvoicesSummary: 'Facturas e información financiera',
  toolGithubSummary: 'Proyectos, incidencias y pull requests',
  taskMeetingTitle: 'Preparar mi próxima reunión',
  taskMeetingPrompt:
    'Ayúdame a preparar mi próxima reunión. Revisa mi calendario, correo y documentos disponibles, pregúntame de qué reunión se trata si hace falta y prepara un resumen breve con fuentes.',
  taskSlackTitle: 'Ponerme al día con una conversación',
  taskSlackPrompt:
    'Ayúdame a ponerme al día con una conversación de Slack. Pregúntame en qué canal o tema centrarnos y luego resume las decisiones, las preguntas abiertas y los próximos pasos con enlaces a los mensajes.',
  taskShopifyTitle: 'Mirar mis productos con otros ojos',
  taskShopifyPrompt:
    'Revisa una pequeña muestra de mis productos de Shopify e identifica mejoras útiles en la información de producto. Muestra la evidencia y no cambies nada.',
  taskWooTitle: 'Revisar la información de mis productos',
  taskWooPrompt:
    'Revisa una pequeña muestra de mis productos de WooCommerce e identifica carencias en la información de producto. Muestra las fuentes y no cambies nada.',
  taskFeedTitle: 'Revisar mi feed de productos',
  taskFeedPrompt:
    'Ayúdame a revisar mi feed de productos de Google Merchant Center. Empieza por la cuenta accesible y una pequeña muestra, y cita las fuentes de lo que encuentres.',
  taskFortnoxTitle: 'Entender las facturas recientes',
  taskFortnoxPrompt:
    'Ayúdame a entender las facturas recientes de Fortnox. Pregúntame qué periodo revisar y resume la información disponible con fuentes. No cambies nada.',
  taskTripletexTitle: 'Ver un resumen de las facturas recientes',
  taskTripletexPrompt:
    'Ayúdame a entender las facturas recientes de Tripletex. Pregúntame qué periodo revisar y resume la información disponible con fuentes. No cambies nada.',
  taskGithubTitle: 'Ponerme al día con un proyecto',
  taskGithubPrompt:
    'Ayúdame a ponerme al día con un proyecto de GitHub. Pregúntame qué repositorio revisar y resume las incidencias y pull requests recientes con enlaces.',
  taskTryTitle: 'Probar {name}',
  taskTryPrompt:
    'Ayúdame a empezar con mi conexión de {name}. Descubre sus herramientas disponibles, sugiere una primera tarea útil y pregúntame qué quiero hacer. Proponme cualquier cambio para revisarlo antes de ejecutarlo.',
  back: 'Atrás',
  continue: 'Continuar',
  continueWithout: 'Continuar sin conectar',
  goToWorkspace: 'Ir al espacio de trabajo',
  opening: 'Abriendo tu espacio de trabajo…',
  startFailed: 'No hemos podido abrir tu espacio de trabajo. Inténtalo de nuevo.',
  loadingTitle: 'Abriendo tu empresa…',
  loadingStatus: 'Cargando tu espacio de trabajo',
  errorTitle: 'Volvamos a intentarlo.',
  tryAgain: 'Reintentar',
  noCompanyTitle: 'Necesitas una invitación.',
  noCompanyBody:
    'Tu cuenta no está vinculada a ninguna empresa. Pide a un propietario que invite tu correo. Tu trabajo privado se conserva.',
  checkAccess: 'Comprobar acceso',
};

const sv: WelcomeCopy = {
  logOut: 'Logga ut',
  progressLabel: 'Kom igång',
  stepOf: 'Steg {current} av {total}',
  stepFocus: 'Ditt fokus',
  stepTools: 'Dina verktyg',
  stepFirst: 'Ditt första steg',
  focusTitle: 'Berätta hur du arbetar.',
  focusTitleNamed: 'Välkommen, {name}. Hur arbetar du?',
  focusIntro:
    'Välj din roll och vad du vill ha hjälp med. Båda är valfria och hjälper oss att föreslå användbara verktyg.',
  roleHeading: 'Din roll',
  goalsHeading: 'Vad vill du ha hjälp med?',
  roleLeadership: 'Ledning',
  roleProduct: 'Produkt',
  roleEngineering: 'Teknik och data',
  roleMarketing: 'Marknadsföring',
  roleSales: 'Försäljning',
  roleOperations: 'Drift',
  roleFinance: 'Ekonomi',
  goalEverydayTitle: 'Ligg steget före',
  goalEverydayBody: 'Förbered möten och kom ikapp med samtal.',
  goalCommerceTitle: 'Förstå min verksamhet',
  goalCommerceBody: 'Utforska produkter, ordrar och möjligheter.',
  goalFinanceTitle: 'Se siffrorna tydligt',
  goalFinanceBody: 'Förstå fakturor och ekonomisk information.',
  goalProjectsTitle: 'Håll arbetet i rörelse',
  goalProjectsBody: 'Koppla ihop diskussioner, ärenden och projektuppdateringar.',
  toolsTitle: 'Anslut dina vardagsverktyg.',
  toolsIntro: 'Lite sammanhang gör stor skillnad. Börja med ett eller två.',
  toolsUnavailable:
    'Anslutningar är inte tillgängliga just nu. Du kan börja med en fråga eller en fil.',
  toolsLabel: 'Anslut dina verktyg',
  suggestedTools: 'Förslag för dig',
  allTools: 'Alla verktyg',
  browseAllTools: 'Visa alla verktyg',
  backToSuggestions: 'Tillbaka till förslagen',
  searchTools: 'Sök verktyg',
  findTool: 'Hitta ett verktyg…',
  popupBlocked:
    'Webbläsaren blockerade popup-fönstret. Sidan fortsätter att kontrollera medan du godkänner åtkomsten.',
  openAuthorization: 'Öppna godkännandesidan',
  authorizing: 'Slutför godkännandet i leverantörens fönster.',
  stopWaiting: 'Sluta vänta',
  loadingConnections: 'Läser in dina anslutningar…',
  connectionsFailed: 'Vi kunde inte läsa in dina anslutningar.',
  useMyAccount: 'Använd mitt konto',
  useCompanyAccount: 'Använd ett konto för {company}',
  chooseAudience: 'Välj vilka som kan använda anslutningen',
  noToolsMatch: 'Inga verktyg matchar sökningen. Prova ett annat namn.',
  noToolsYet: 'Det finns inga verktyg att föreslå än. Du kan börja med en fråga eller en fil.',
  reminderTitle: 'Bättre med dina verktyg.',
  reminderIntro:
    'En anslutning räcker för att börja. Testa ett verktyg du redan använder, eller återkom senare.',
  reminderDoneTitle: 'En bra början.',
  reminderDoneIntro: 'Du kan lägga till fler när du behöver dem.',
  firstTitle: 'Var ska vi börja?',
  firstIntro:
    'Välj ett exempel för att starta en konversation, eller gå direkt till din arbetsyta.',
  connectedOne: '1 konto anslutet',
  connectedMany: '{count} konton anslutna',
  taskResearchTitle: 'Utforska en affärsmöjlighet',
  taskResearchPrompt:
    'Hjälp mig att undersöka en möjlighet för vår verksamhet med hjälp av offentliga källor. Fråga vad jag vill utforska och hjälp mig sedan att ta fram en användbar sammanfattning med källor.',
  taskFileTitle: 'Gå igenom ett dokument eller kalkylark',
  taskFilePrompt:
    'Hjälp mig att gå igenom ett dokument eller kalkylark. Be mig bifoga en fil och fråga vad jag vill veta eller få gjort, och guida mig sedan genom det.',
  taskChooseTitle: 'Hjälp mig välja en första uppgift',
  taskChoosePrompt:
    'Hjälp mig att komma igång med Poneva. Använd vårt företags sammanhang och tillgängliga funktioner för att föreslå några användbara saker vi kan göra tillsammans. Ställ en fråga som hjälper mig att välja och guida mig sedan genom uppgiften.',
  toolGoogleSummary: 'E-post, kalender och dokument',
  toolSlackSummary: 'Konversationer och teamets kunskap',
  toolShopifySummary: 'Produkter, ordrar och lager',
  toolWooSummary: 'Produkter, ordrar och rapporter',
  toolMerchantSummary: 'Produktflöden och status',
  toolInvoicesSummary: 'Fakturor och ekonomisk information',
  toolGithubSummary: 'Projekt, ärenden och pull requests',
  taskMeetingTitle: 'Förbered mitt nästa möte',
  taskMeetingPrompt:
    'Hjälp mig att förbereda mitt nästa möte. Gå igenom min tillgängliga kalender, e-post och mina dokument, fråga vilket möte det gäller om det behövs och gör en kort sammanfattning med källor.',
  taskSlackTitle: 'Kom ikapp med en konversation',
  taskSlackPrompt:
    'Hjälp mig att komma ikapp med en Slack-konversation. Fråga vilken kanal eller vilket ämne vi ska fokusera på och sammanfatta sedan beslut, öppna frågor och nästa steg med länkar till meddelandena.',
  taskShopifyTitle: 'Se på mina produkter med nya ögon',
  taskShopifyPrompt:
    'Gå igenom ett litet urval av mina Shopify-produkter och hitta användbara förbättringar av produktinformationen. Visa underlaget och ändra ingenting.',
  taskWooTitle: 'Granska min produktinformation',
  taskWooPrompt:
    'Gå igenom ett litet urval av mina WooCommerce-produkter och hitta luckor i produktinformationen. Visa källorna och ändra ingenting.',
  taskFeedTitle: 'Granska mitt produktflöde',
  taskFeedPrompt:
    'Hjälp mig att granska mitt produktflöde i Google Merchant Center. Börja med det tillgängliga kontot och ett litet urval, och ange källor för det du hittar.',
  taskFortnoxTitle: 'Få grepp om de senaste fakturorna',
  taskFortnoxPrompt:
    'Hjälp mig att förstå de senaste fakturorna i Fortnox. Fråga vilken period vi ska gå igenom och sammanfatta den tillgängliga informationen med källor. Ändra ingenting.',
  taskTripletexTitle: 'Få en överblick över de senaste fakturorna',
  taskTripletexPrompt:
    'Hjälp mig att förstå de senaste fakturorna i Tripletex. Fråga vilken period vi ska gå igenom och sammanfatta den tillgängliga informationen med källor. Ändra ingenting.',
  taskGithubTitle: 'Kom ikapp med ett projekt',
  taskGithubPrompt:
    'Hjälp mig att komma ikapp med ett GitHub-projekt. Fråga vilket repository vi ska gå igenom och sammanfatta de senaste ärendena och pull requests med länkar.',
  taskTryTitle: 'Prova {name}',
  taskTryPrompt:
    'Hjälp mig att komma igång med min {name}-anslutning. Ta reda på vilka verktyg den har, föreslå en användbar första uppgift och fråga vad jag vill göra. Lägg fram alla ändringar för mig att granska innan du genomför dem.',
  back: 'Tillbaka',
  continue: 'Fortsätt',
  continueWithout: 'Fortsätt utan att ansluta',
  goToWorkspace: 'Gå till arbetsytan',
  opening: 'Öppnar din arbetsyta…',
  startFailed: 'Vi kunde inte öppna din arbetsyta. Försök igen.',
  loadingTitle: 'Öppnar ditt företag…',
  loadingStatus: 'Laddar din arbetsyta',
  errorTitle: 'Något gick fel.',
  tryAgain: 'Försök igen',
  noCompanyTitle: 'Du behöver en inbjudan.',
  noCompanyBody:
    'Ditt konto är inte kopplat till något företag. Be en ägare att bjuda in din e-postadress. Ditt privata arbete finns kvar.',
  checkAccess: 'Kontrollera åtkomst',
};

const welcomeCopy: NamespaceCatalog<WelcomeCopyKey> = {
  bg,
  hr,
  cs,
  da,
  nl,
  en,
  et,
  fi,
  fr,
  de,
  el,
  hu,
  ga,
  it,
  lv,
  lt,
  mt,
  pl,
  pt,
  ro,
  sk,
  sl,
  es,
  sv,
};
export default welcomeCopy;
